// Calling — the cold-calling team's page (PhoneBurner). Private to the
// calling team (admins, the marketing role, anyone granted "Calling page"):
// build a call list, send it to PhoneBurner, watch the calls come back.
//
// The Calls panel at the bottom of this file (window.DialerCallsPanel) is
// the one thing everyone sees: a contact's / company's call history, on
// their record pages. See backend app/dialer.py for who-sees-what.

const CALL_OUTCOME_HUE = {
  call_scheduled: 'green', ds_call: 'green', follow_up: 'blue', more_info: 'blue', live_answer: 'teal', not_qualified: 'amber',
  not_interested: 'rose', do_not_call: 'rose', wrong_number: 'slate', bad_number: 'slate',
  voicemail: 'violet', no_answer: 'slate', other: 'slate', skipped: 'slate',
};
const CALL_OUTCOME_LABEL = {
  call_scheduled: 'Call scheduled', ds_call: 'Director/Seller call', follow_up: 'Follow up',
  more_info: 'Asked for more info', live_answer: 'Live answer',
  not_qualified: 'Not qualified', not_interested: 'Not interested', do_not_call: 'Do not call',
  wrong_number: 'Wrong number', bad_number: 'Bad number', voicemail: 'Voicemail',
  no_answer: 'No answer', other: 'Other', skipped: 'Skipped',
};
const CALL_STATUS_OPTIONS = [
  ['none', 'Not in marketing'], ['new_prospect', 'New prospect'], ['email_sent', 'Email sent'],
  ['follow_up_sent', 'Follow-up sent'], ['responded', 'Responded'], ['call_scheduled', 'Call scheduled'],
];

const callWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }),
    hour: 'numeric', minute: '2-digit' });
};
const callDur = (s) => {
  if (s == null) return null;
  const m = Math.floor(s / 60), r = s % 60;
  return m ? `${m}m ${String(r).padStart(2, '0')}s` : `${r}s`;
};

// A contact/company name linking to its page — plain text for a role that
// can't open those pages (a caller sees Calling only; app.jsx AtlasCanSee).
const RecLink = ({ kind, id, children, style }) => {
  const page = kind === 'company' ? 'companies' : 'people';
  const can = !window.AtlasCanSee || window.AtlasCanSee(page);
  if (!id || !can) return <span style={style}>{children}</span>;
  const nav = window.AtlasNav || {};
  const go = kind === 'company' ? nav.goToCompany : nav.goToContact;
  return (
    <a href={`#/${page}/${id}`} onClick={(e) => { e.preventDefault(); go && go(id); }}
      style={{ textDecoration: 'none', ...style, ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>{window.PhoneHit && <window.PhoneHit />}{children}</a>
  );
};

// A call's recording: a real button that opens an in-page player.
const RecordingButton = ({ open, onToggle }) => (
  <Btn size="xs" variant={open ? 'accent' : 'outline'} icon={<Icon.Play size={10} />} onClick={onToggle}
    title={open ? 'Hide the player' : 'Play the recording here'}>
    {open ? 'Hide recording' : 'Play recording'}
  </Btn>
);
const RecordingPlayer = ({ url }) => (
  <div style={{ marginTop: 6, display: 'flex', gap: 10, alignItems: 'center' }}>
    <audio controls autoPlay preload="none" src={url} style={{ height: 32, maxWidth: 420, width: '100%' }} />
    <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: 'var(--text-3)' }}
      title="Open the recording in a new tab (to download or share)">Open</a>
  </div>
);

// AI call score (backend app/call_scoring.py): Zach's 100-point rubric.
const SCORE_HUE = { strong: 'green', solid: 'teal', developing: 'amber', weak: 'rose' };
const OUTCOME_LEVEL_HUE = { advanced: 'green', kept_open: 'blue', closed: 'slate' };

const ScoreBar = ({ value, max }) => {
  const f = max ? Math.max(0, Math.min(1, value / max)) : 0;
  const color = f >= 0.8 ? 'var(--green-fg)' : f >= 0.6 ? 'var(--accent)' : f >= 0.4 ? 'var(--amber-fg)' : 'var(--rose-fg)';
  return (
    <div style={{ height: 6, borderRadius: 3, background: 'var(--surface-2)', overflow: 'hidden', flex: 1, minWidth: 60 }}>
      <div style={{ width: `${Math.round(f * 100)}%`, height: '100%', background: color, borderRadius: 3 }} />
    </div>
  );
};

const CallScorecard = ({ s }) => (
  <div style={{ marginTop: 8, padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface-2)', fontSize: 12 }}>
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
      <b style={{ fontSize: 13 }}>{s.total}/100</b>
      {s.headline && <span style={{ color: 'var(--text-2)' }}>{s.headline}</span>}
      <span style={{ flex: 1 }} />
      {s.outcome && s.outcome.level && (
        <Pill hue={OUTCOME_LEVEL_HUE[s.outcome.level] || 'slate'} sz="sm" title={s.outcome.reason || undefined}>
          Outcome: {(s.outcome.label || s.outcome.level).split(' — ')[0]}
        </Pill>
      )}
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(150px, 230px) 1fr 44px', gap: '5px 10px', alignItems: 'center' }}>
      {(s.components || []).map((comp) => (
        <React.Fragment key={comp.key}>
          <span style={{ color: 'var(--text-2)' }} title={comp.evidence || undefined}>{comp.label}</span>
          <ScoreBar value={comp.score} max={comp.max} />
          <span style={{ textAlign: 'right', color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>{comp.score}/{comp.max}</span>
        </React.Fragment>
      ))}
    </div>
    {(s.strengths || []).length > 0 && (
      <div style={{ marginTop: 10 }}>
        <div style={{ fontWeight: 600, color: 'var(--text-2)', marginBottom: 3 }}>What worked</div>
        {s.strengths.map((x, i) => <div key={i} style={{ color: 'var(--text-2)', lineHeight: 1.5 }}>✓ {x}</div>)}
      </div>
    )}
    {(s.tips || []).length > 0 && (
      <div style={{ marginTop: 8 }}>
        <div style={{ fontWeight: 600, color: 'var(--text-2)', marginBottom: 3 }}>Try next time</div>
        {s.tips.map((t, i) => (
          <div key={i} style={{ color: 'var(--text-2)', lineHeight: 1.5 }}>
            → {t.tip}{t.label ? <span style={{ color: 'var(--text-4)' }}> · {t.label}</span> : null}
          </div>
        ))}
      </div>
    )}
    {s.outcome && s.outcome.reason && <div style={{ marginTop: 8, color: 'var(--text-3)' }}>Outcome: {s.outcome.reason}</div>}
  </div>
);

// The call panel (Calling page only — record panels keep the inline card):
// the coaching for one call, its key moments, suggestions and transcript.
const CallPanelContext = React.createContext(null);
const MOMENT_STYLE = {
  strength: { mark: '✓', color: 'var(--green-fg)', label: 'Worked' },
  miss: { mark: '→', color: 'var(--amber-fg)', label: 'Missed' },
};

const CallMoment = ({ m, onJump }) => {
  const st = MOMENT_STYLE[m.kind] || MOMENT_STYLE.miss;
  return (
    <div style={{ display: 'flex', gap: 10, padding: '8px 0', borderTop: '1px dashed var(--border)' }}>
      <span style={{ color: st.color, fontWeight: 700, width: 14, flexShrink: 0, paddingTop: 1 }} title={st.label}>{st.mark}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ borderLeft: `3px solid ${st.color}`, padding: '2px 0 2px 10px', color: 'var(--text)', fontStyle: 'italic', lineHeight: 1.5 }}>
          {m.speaker ? <span style={{ fontStyle: 'normal', fontWeight: 600, color: 'var(--text-2)' }}>{m.speaker}: </span> : null}“{m.quote}”
        </div>
        {m.note && <div style={{ color: 'var(--text-2)', marginTop: 4, lineHeight: 1.5 }}>{m.note}</div>}
        {m.try_instead && (
          <div style={{ marginTop: 5, padding: '6px 10px', borderRadius: 6, background: 'var(--surface-2)', lineHeight: 1.5 }}>
            <span style={{ fontWeight: 600, color: 'var(--text-2)' }}>Try: </span>“{m.try_instead}”
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
          {m.label && <span style={{ fontSize: 11, color: 'var(--text-4)' }}>{m.label}</span>}
          {m.line != null && onJump && (
            <button onClick={() => onJump(m.line)} style={{ fontSize: 11, color: 'var(--accent)', background: 'none', border: 0, padding: 0, cursor: 'pointer' }}>
              Find in transcript
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const CallCoaching = ({ c, onJump, onRescore, rescoring }) => {
  const sc = c.score && c.score.scorable ? c.score : null;
  const head = { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-4)', fontWeight: 600, margin: '14px 0 4px' };
  if (!sc) {
    const why = c.score && !c.score.scorable ? `Not scored: ${c.score.reason || 'no real conversation with the owner'}.`
      : c.transcript_status === 'completed' ? ''
      : (c.transcript_reason || 'No transcript, so nothing to score.');
    if (!why && !onRescore) return null;
    return (
      <div style={{ padding: '10px 12px', borderRadius: 8, background: 'var(--surface-2)', color: 'var(--text-3)', fontSize: 12.5, display: 'flex', gap: 10, alignItems: 'center' }}>
        <span style={{ flex: 1 }}>{why}</span>
        {onRescore && <Btn size="xs" variant="outline" disabled={rescoring} onClick={onRescore}>{rescoring ? 'Scoring…' : 'Score now'}</Btn>}
      </div>
    );
  }
  return (
    <div style={{ fontSize: 12.5 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 26, fontWeight: 650, color: 'var(--text)', lineHeight: 1 }}>{sc.total}<span style={{ fontSize: 13, color: 'var(--text-4)', fontWeight: 400 }}>/100</span></span>
        <Pill hue={SCORE_HUE[sc.band] || 'slate'} sz="sm">{sc.band}</Pill>
        {sc.outcome && sc.outcome.level && (
          <Pill hue={OUTCOME_LEVEL_HUE[sc.outcome.level] || 'slate'} sz="sm" title={sc.outcome.reason || undefined}>
            Outcome: {(sc.outcome.label || sc.outcome.level).split(' — ')[0]}
          </Pill>
        )}
        <span style={{ flex: 1 }} />
        {onRescore && <Btn size="xs" variant="ghost" disabled={rescoring} onClick={onRescore}>{rescoring ? 'Scoring…' : 'Re-score'}</Btn>}
      </div>
      {sc.headline && <div style={{ marginTop: 8, color: 'var(--text)', lineHeight: 1.5 }}>{sc.headline}</div>}
      {sc.outcome && sc.outcome.reason && <div style={{ marginTop: 4, color: 'var(--text-3)', lineHeight: 1.5 }}>Outcome: {sc.outcome.reason}</div>}

      {(sc.moments || []).length > 0 && (
        <>
          <div style={head}>Key moments</div>
          {sc.moments.map((m, i) => <CallMoment key={i} m={m} onJump={onJump} />)}
        </>
      )}

      {(sc.tips || []).length > 0 && (
        <>
          <div style={head}>Try next time</div>
          {sc.tips.map((t, i) => (
            <div key={i} style={{ color: 'var(--text-2)', lineHeight: 1.5, marginBottom: 3 }}>
              → {t.tip}{t.label ? <span style={{ color: 'var(--text-4)' }}> · {t.label}</span> : null}
            </div>
          ))}
        </>
      )}
      {(sc.strengths || []).length > 0 && (
        <>
          <div style={head}>What worked</div>
          {sc.strengths.map((x, i) => <div key={i} style={{ color: 'var(--text-2)', lineHeight: 1.5, marginBottom: 3 }}>✓ {x}</div>)}
        </>
      )}

      <div style={head}>The rubric</div>
      {(sc.components || []).map((comp) => (
        <div key={comp.key} style={{ padding: '5px 0', borderTop: '1px dashed var(--border)' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{ width: 230, color: 'var(--text-2)' }}>{comp.label}</span>
            <ScoreBar value={comp.score} max={comp.max} />
            <span style={{ width: 44, textAlign: 'right', color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>{comp.score}/{comp.max}</span>
          </div>
          {comp.evidence && <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2, lineHeight: 1.45 }}>{comp.evidence}</div>}
        </div>
      ))}
    </div>
  );
};

const CallTranscript = ({ t, callerName, marked, active, lineRefs }) => {
  if (!t || !t.available) return <div style={{ fontSize: 12.5, color: 'var(--text-4)' }}>No transcript stored for this call.</div>;
  const callerFirst = ((callerName || '').split(' ')[0] || '').toLowerCase();
  return (
    <div style={{ fontSize: 12.5, lineHeight: 1.55 }}>
      {t.sentences.map((s, i) => {
        const isCaller = callerFirst && String(s.speaker || '').toLowerCase().startsWith(callerFirst);
        const on = active === i;
        return (
          <div key={i} ref={(el) => { lineRefs.current[i] = el; }}
            style={{ display: 'flex', gap: 10, padding: '3px 6px', borderRadius: 4,
              background: on ? 'var(--amber-bg)' : marked.has(i) ? 'var(--surface-2)' : 'transparent', transition: 'background 300ms' }}>
            <span style={{ width: 70, flexShrink: 0, fontWeight: 600, color: isCaller ? 'var(--accent)' : 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {s.speaker}
            </span>
            <span style={{ color: 'var(--text)' }}>{s.text}</span>
          </div>
        );
      })}
    </div>
  );
};

const CallDetailDrawer = ({ call, onClose, focus }) => {
  const [d, setD] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [active, setActive] = React.useState(null);
  const [rescoring, setRescoring] = React.useState(false);
  const lineRefs = React.useRef({});
  const transcriptRef = React.useRef(null);
  const load = React.useCallback(() => {
    window.AtlasApi.dialerCall(call.id).then((r) => { setD(r); setErr(null); }).catch((e) => setErr(e.message));
  }, [call.id]);
  React.useEffect(() => { setD(null); load(); }, [load]);
  React.useEffect(() => {   // "Transcript" opens the panel at the transcript
    if (focus === 'transcript' && d && transcriptRef.current) transcriptRef.current.scrollIntoView({ block: 'start' });
  }, [focus, d]);
  const c = d || call;
  const jump = (i) => {
    setActive(i);
    const el = lineRefs.current[i];
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };
  const rescore = async () => {
    setRescoring(true);
    try { await window.AtlasApi.dialerScoreCall(c.id); load(); }
    catch (e) { pushToast({ type: 'error', title: 'Scoring failed', body: e.message }); }
    finally { setRescoring(false); }
  };
  const marked = new Set(((c.score && c.score.moments) || []).map((m) => m.line).filter((x) => x != null));
  const sect = { fontSize: 12, fontWeight: 600, color: 'var(--text-2)', margin: '18px 0 6px', paddingTop: 12, borderTop: '1px solid var(--border)' };
  const sub = [c.outcome_label, callWhen(c.started_at), callDur(c.duration_seconds), c.caller_name].filter(Boolean).join(' · ');
  return (
    <Drawer open onClose={onClose} width={720} widthKey="callDetail" title={`${c.contact_name || 'Call'}${c.company_name ? ` · ${c.company_name}` : ''}`} subtitle={sub}>
      <div style={{ padding: '4px 2px 20px' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5 }}>
          <Pill hue={CALL_OUTCOME_HUE[c.outcome] || 'slate'} sz="sm" title={c.disposition || undefined}>{c.outcome_label || 'Call'}</Pill>
          {c.corrected && <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>was {c.corrected.from_label} · moved to {c.corrected.folder}</span>}
          {c.contact_id && <RecLink kind="contact" id={c.contact_id} style={{ color: 'var(--accent)' }}>Open contact</RecLink>}
        </div>
        {c.recording_url && (
          <audio controls preload="none" src={c.recording_url} style={{ height: 32, width: '100%', marginTop: 10 }} />
        )}
        {err && <div style={{ color: 'var(--rose-fg)', fontSize: 12.5, marginTop: 10 }}>{err}</div>}

        <div style={sect}>Coaching</div>
        {!d && !err ? <div style={{ fontSize: 12.5, color: 'var(--text-4)' }}>Loading…</div>
          : <CallCoaching c={c} onJump={c.transcript && c.transcript.available ? jump : null}
              onRescore={d && d.can_score ? rescore : null} rescoring={rescoring} />}

        {(c.summary || c.caller_note) && (
          <>
            <div style={sect}>Summary</div>
            {c.summary && <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.55 }}>{c.summary}</div>}
            {c.caller_note && <div style={{ fontSize: 12.5, color: 'var(--text-3)', lineHeight: 1.55, marginTop: 6 }}>Caller notes: {c.caller_note}</div>}
          </>
        )}

        {d && (d.actions || []).length > 0 && (
          <>
            <div style={sect}>Suggested from this call</div>
            <div style={{ margin: '0 -14px' }}>
              <CallSuggestionGroup items={d.actions}
                onDone={(id) => setD((x) => ({ ...x, actions: (x.actions || []).filter((a) => a.id !== id) }))} />
            </div>
          </>
        )}

        <div style={sect} ref={transcriptRef}>Transcript{d && d.transcript && d.transcript.available ? ` · ${d.transcript.sentences.length} lines` : ''}</div>
        {d ? <CallTranscript t={d.transcript} callerName={c.caller_name} marked={marked} active={active} lineRefs={lineRefs} />
          : <div style={{ fontSize: 12.5, color: 'var(--text-4)' }}>Loading…</div>}
      </div>
    </Drawer>
  );
};

// One call — shared by the Calling feed and the record panels.
const DialerCallRow = ({ c, showContact = false, showCompany = false }) => {
  const [open, setOpen] = React.useState(false);
  const [noteOpen, setNoteOpen] = React.useState(false);
  const [scoreOpen, setScoreOpen] = React.useState(false);
  const panel = React.useContext(CallPanelContext);
  const sc = c.score && c.score.scorable ? c.score : null;
  const detail = c.summary || c.caller_note;
  const NoteModalC = window.NoteModal;
  // On the Calling page a click anywhere on the row (not on its links or
  // buttons) opens the call panel; elsewhere the row stays as it was.
  const rowClick = panel ? (e) => {
    if (e.target.closest('a, button, input, textarea, select, audio, label')) return;
    panel.open(c);
  } : undefined;
  return (
    <div onClick={rowClick} style={{ padding: '9px 14px', borderBottom: '1px solid var(--border)', cursor: panel ? 'pointer' : 'default' }}
      title={panel ? 'Open the call: coaching, key moments, transcript' : undefined}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5 }}>
        <Pill hue={CALL_OUTCOME_HUE[c.outcome] || 'slate'} sz="sm" title={c.disposition || undefined}>
          {c.outcome_label || CALL_OUTCOME_LABEL[c.outcome] || c.disposition || 'Call'}
        </Pill>
        {c.corrected && (
          <span style={{ fontSize: 11, color: 'var(--text-3)' }}
            title={`Tagged “${c.disposition || c.corrected.from_label}” on the call; moved to ${c.corrected.folder} in PhoneBurner afterwards`}>
            was {c.corrected.from_label} · moved to {c.corrected.folder}
          </span>
        )}
        {showContact && (c.contact_id ? (
          <RecLink kind="contact" id={c.contact_id} style={{ fontWeight: 600, color: 'var(--text)' }}>
            {c.contact_name || 'Contact'}
          </RecLink>
        ) : <span style={{ color: 'var(--amber-fg)' }} title="No Atlas contact matched this call">Unmatched · {c.phone || 'no phone'}</span>)}
        {showCompany && c.company_id && (
          <RecLink kind="company" id={c.company_id} style={{ color: 'var(--text-2)' }}>
            {c.company_name}
          </RecLink>
        )}
        <span style={{ color: 'var(--text-3)' }}>{callWhen(c.started_at)}</span>
        {callDur(c.duration_seconds) && <span style={{ color: 'var(--text-4)' }}>· {callDur(c.duration_seconds)}</span>}
        {c.caller_name && <span style={{ color: 'var(--text-4)' }}>· {c.caller_name}</span>}
        <span style={{ flex: 1 }} />
        {sc && (
          <Btn size="xs" variant={scoreOpen ? 'accent' : 'outline'} onClick={() => (panel ? panel.open(c) : setScoreOpen((o) => !o))}
            title={sc.headline || 'Call quality score'}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <Pill hue={SCORE_HUE[sc.band] || 'slate'} sz="sm">{sc.total}</Pill>{panel ? 'Feedback' : scoreOpen ? 'Hide score' : 'Score'}
            </span>
          </Btn>
        )}
        {c.score && !c.score.scorable && (
          <span style={{ fontSize: 11, color: 'var(--text-4)' }} title={c.score.reason || undefined}>not scored ⓘ</span>
        )}
        {c.has_transcript && c.note_id && (panel || NoteModalC) && (
          <Btn size="xs" variant="outline" icon={<Icon.Doc size={10} />}
            onClick={() => { if (panel) panel.open(c, 'transcript'); else if (!window.openRecordNote(c.note_id, { via: 'button' })) setNoteOpen(true); }}
            title="Read the transcript">Transcript</Btn>
        )}
        {c.recording_url && <RecordingButton open={open} onToggle={() => setOpen((o) => !o)} />}
        {c.conversation && !c.has_transcript && c.transcript_reason && (
          <span style={{ fontSize: 11, color: 'var(--text-4)' }} title={c.transcript_reason}>
            {c.transcript_status === 'gave_up' ? 'no transcript' : 'transcript pending'} ⓘ
          </span>
        )}
      </div>
      {open && c.recording_url && <RecordingPlayer url={c.recording_url} />}
      {scoreOpen && sc && <CallScorecard s={sc} />}
      {noteOpen && NoteModalC && <NoteModalC noteId={c.note_id} onClose={() => setNoteOpen(false)} />}
      {detail && (
        <div onClick={panel ? undefined : () => setOpen((o) => !o)}
          style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 4, lineHeight: 1.5, cursor: 'pointer',
            ...(open ? {} : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }) }}>
          {c.summary}
          {c.summary && c.caller_note ? ' ' : ''}
          {c.caller_note && <span style={{ color: 'var(--text-3)' }}>Caller notes: {c.caller_note}</span>}
        </div>
      )}
    </div>
  );
};

// The Calls panel's two actions, shared with the Meetings & calls drawer body
// (record-page redesign step 10). `load` re-reads the panel after a write.
const useDialerActions = (contactId, load) => {
  const [busy, setBusy] = React.useState(false);
  const toggleQueue = async (queued) => {
    setBusy(true);
    try {
      await window.AtlasApi.dialerQueue({ contact_ids: [contactId], queued: !queued });
      pushToast({ type: 'success', title: queued ? 'Removed from the call queue' : 'Marked to call',
                  body: queued ? undefined : 'It shows under “Marked to call” on the Calling page.' });
      load();
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not update the call queue', body: e.message });
    } finally { setBusy(false); }
  };
  const send = async () => {
    setBusy(true);
    try {
      const b = await window.AtlasApi.dialerSend({ contact_ids: [contactId] });
      let cur = b;
      // Up to ~4 minutes: a first send prepares the caller note (seller
      // brief, comps) before the contact goes out.
      for (let i = 0; i < 120 && (cur.status === 'queued' || cur.status === 'running'); i += 1) {
        await new Promise((r) => setTimeout(r, i < 10 ? 700 : 2000));
        cur = await window.AtlasApi.dialerBatch(b.id);
      }
      if (cur.sent) pushToast({ type: 'success', title: 'Sent to PhoneBurner', body: 'It shows in the caller’s contact list.' });
      else if (cur.skipped) {
        const why = (cur.skips && cur.skips[0] && cur.skips[0].reason) || '';
        pushToast({ type: 'error', title: 'Not sent', body: (window.DIALER_SKIP_LABELS || {})[why] || why || 'Skipped' });
      } else pushToast({ type: 'error', title: 'Send failed', body: ((cur.errors || [])[0] || {}).error || cur.status });
      load();
    } catch (e) {
      pushToast({ type: 'error', title: 'Send failed', body: e.message });
    } finally { setBusy(false); }
  };
  return { busy, toggleQueue, send };
};

// ── Record panels (everyone) ───────────────────────────────────────────────
// Renders nothing on the vast majority of records (never called, and the
// viewer isn't on the calling team) — the contact page stays uncluttered.
const DialerCallsPanel = ({ contactId, companyId }) => {
  const [data, setData] = React.useState(null);
  const [showAll, setShowAll] = React.useState(false);
  const load = React.useCallback(() => {
    const p = contactId ? window.AtlasApi.contactCalls(contactId) : window.AtlasApi.companyCalls(companyId);
    p.then(setData).catch(() => setData(null));
  }, [contactId, companyId]);
  React.useEffect(() => { setData(null); load(); }, [load]);
  const { busy, toggleQueue, send } = useDialerActions(contactId, load);

  if (!data) return null;
  const calls = data.calls || [];
  const st = data.state || {};
  const flagged = st.do_not_call || st.bad_phone || st.sent_at || st.queued;
  if (!calls.length && !data.can_send && !data.can_queue && !flagged) return null;

  const shown = showAll ? calls : calls.slice(0, 5);
  // The gap below is the section's own margin, not a sibling spacer.
  return (
    <>
      <Section
        style={{ marginBottom: 14 }}
        title={`Calls${calls.length ? ` · ${calls.length}` : ''}`}
        action={contactId && (data.can_queue || data.can_send) ? (
          <div style={{ display: 'flex', gap: 6 }}>
            {data.can_queue && (
              <Btn size="xs" variant={st.queued ? 'ghost' : 'outline'} disabled={busy || st.do_not_call} onClick={() => toggleQueue(st.queued)}
                title={st.queued ? 'Take it off the call queue' : 'Put it on the call queue for the calling team'}>
                {st.queued ? 'Queued ✓ · Remove' : 'Mark to call'}
              </Btn>
            )}
            {data.can_send && (
              <Btn size="xs" variant="outline" icon={<Icon.Phone size={10} />} disabled={busy || st.do_not_call} onClick={send}
                title={st.do_not_call ? 'This contact asked not to be called' : 'Add this contact to PhoneBurner'}>
                {busy ? 'Sending…' : (st.sent_at ? 'Send again' : 'Send to PhoneBurner')}
              </Btn>
            )}
          </div>
        ) : null}
      >
        {flagged && (
          <div style={{ padding: '10px 14px 4px', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {st.do_not_call && <Pill hue="rose" sz="sm">Do not call</Pill>}
            {st.queued && <Pill hue="blue" sz="sm">Marked to call</Pill>}
            {st.bad_phone && <Pill hue="amber" sz="sm">Phone marked bad</Pill>}
            {st.sent_at && (
              <span style={{ fontSize: 11, color: 'var(--text-4)' }}>
                In PhoneBurner since {new Date(st.sent_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                {st.sent_by ? ` · sent by ${st.sent_by}` : ''}{st.folder_name ? ` · ${st.folder_name}` : ''}
              </span>
            )}
          </div>
        )}
        {!calls.length && (
          <div style={{ padding: '10px 14px 12px', fontSize: 12, color: 'var(--text-3)' }}>No calls yet.</div>
        )}
        {shown.map((c) => <DialerCallRow key={c.id} c={c} showContact={!!companyId} />)}
        {calls.length > 5 && (
          <div style={{ padding: '6px 14px' }}>
            <Btn size="xs" variant="ghost" onClick={() => setShowAll((s) => !s)}>
              {showAll ? 'Show fewer' : `Show all ${calls.length}`}
            </Btn>
          </div>
        )}
      </Section>
    </>
  );
};

// ── Calling page ───────────────────────────────────────────────────────────

const callTabBtn = (active) => ({
  fontSize: 12.5, padding: '5px 10px', borderRadius: 6, border: '1px solid',
  borderColor: active ? 'var(--border-strong)' : 'transparent',
  background: active ? 'var(--surface)' : 'transparent', color: active ? 'var(--text)' : 'var(--text-3)', cursor: 'pointer',
});
const callSel = { fontSize: 12, padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 6,
  background: 'var(--surface)', color: 'var(--text)' };
const callBox = { marginTop: 12, border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface)', overflow: 'hidden' };
const CALL_LIMIT_OPTIONS = [50, 100, 200, 500, 1000].map((n) => ({ value: n, label: `Up to ${n}` }));

// Where a send goes in PhoneBurner (backend dialer.run_push): '' = the
// REC / NO REC – Attempt 1 routing; 'pair:<rec>|<no rec>' = another REC /
// NO REC pair, the recording rule choosing between them (Jake's EVENT – … –
// Attempt 1 folders, 10-19); 'folder:<id>' = one folder for everyone.
const callDestOptions = (found) => [
  ...(((found && found.pairs) || []).map((p) => ({ value: `pair:${p.rec_folder_id}|${p.no_rec_folder_id}`, label: p.label, group: 'REC / NO REC' }))),
  ...(((found && found.available) || []).map((f) => ({ value: `folder:${f.folder_id}`, label: f.path || f.folder_name, group: 'One folder' }))),
];
const parseCallDest = (value, found) => {
  const v = String(value || '');
  if (v.startsWith('pair:')) {
    const [rec, noRec] = v.slice(5).split('|');
    const p = ((found && found.pairs) || []).find((x) => String(x.rec_folder_id) === rec && String(x.no_rec_folder_id) === noRec);
    return { mode: 'pair', rec_folder_id: rec, no_rec_folder_id: noRec, label: p ? p.label : 'REC / NO REC pair' };
  }
  if (v.startsWith('folder:')) {
    const id = v.slice(7);
    const f = ((found && found.available) || []).find((x) => String(x.folder_id) === id);
    return { mode: 'folder', folder_id: id, label: f ? (f.path || f.folder_name) : 'Folder' };
  }
  return { mode: 'routing', label: 'REC / NO REC – Attempt 1' };
};
const callDestValue = (d) => (!d || d.mode === 'routing' ? ''
  : d.mode === 'pair' ? `pair:${d.rec_folder_id}|${d.no_rec_folder_id}` : `folder:${d.folder_id}`);
const callDestBody = (d) => (d.mode === 'pair' ? { rec_folder_id: d.rec_folder_id, no_rec_folder_id: d.no_rec_folder_id }
  : d.mode === 'folder' ? { folder_id: d.folder_id } : {});

// State and city pickers for the Calling lists (backend dialer.place_options):
// US states by company count; the picked states' cities, several at a time.
const CallingPlacePickers = ({ states, setStates, cities, setCities }) => {
  const [opts, setOpts] = React.useState({ states: [], cities: [] });
  const key = states.join('|');
  React.useEffect(() => {
    let stale = false;
    window.AtlasApi.dialerPlaces(states).then((r) => { if (!stale) setOpts(r || { states: [], cities: [] }); }).catch(() => {});
    return () => { stale = true; };
  }, [key]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Cities follow the states: drop picks that no longer belong to one.
  React.useEffect(() => {
    if (!cities.length) return;
    if (!states.length) { setCities([]); return; }
    const have = new Set((opts.cities || []).map((c) => c.value.toLowerCase()));
    if (have.size && cities.some((c) => !have.has(c.toLowerCase()))) setCities(cities.filter((c) => have.has(c.toLowerCase())));
  }, [opts]);   // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <window.Picker multiple value={states} onChange={setStates} placeholder="All states" width={170}
        options={(opts.states || []).map((x) => ({ value: x.value, label: x.value, count: x.count }))} />
      <window.Picker multiple value={cities} onChange={setCities} width={200} disabled={!states.length}
        placeholder={states.length ? 'All cities' : 'Pick a state for cities'} menuMinWidth={260}
        options={(opts.cities || []).map((x) => ({ value: x.value, label: x.value, count: x.count }))} />
    </>
  );
};

const CallingView = ({ allowed = false }) => {
  if (!allowed) {
    return (
      <div style={{ padding: '18px 22px', maxWidth: 720 }}>
        <h1 style={{ fontSize: 18, fontWeight: 650, margin: 0, color: 'var(--text)' }}>Calling</h1>
        <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 8, lineHeight: 1.6 }}>
          An admin can turn it on for you in Settings → Members (“Calling page (PhoneBurner)”).
          Call history still shows on every contact.
        </div>
      </div>
    );
  }
  return <CallingViewBody />;
};

const CallingViewBody = () => {
  const [status, setStatus] = React.useState(null);
  const [drafts, setDrafts] = React.useState(0);
  const [panelCall, setPanelCall] = React.useState(null);   // {call, focus}
  const panel = React.useMemo(() => ({ open: (call, focus) => setPanelCall({ call, focus }) }), []);
  // The Calls tab renders (and loads its own data) right away — it used to
  // wait for /status first. An admin with nothing connected lands on Setup.
  const [tab, setTab] = React.useState('calls');
  const picked = React.useRef(false);
  const pickTab = (k) => { picked.current = true; setTab(k); };
  const loadStatus = React.useCallback(() => {
    window.AtlasApi.dialerStatus()
      .then((s) => {
        setStatus(s);
        setDrafts(s.email_drafts || 0);
        window.DIALER_SKIP_LABELS = s.skip_reasons || {};
        if (!picked.current && !s.connected && s.is_admin) setTab('setup');
      })
      .catch((e) => { setStatus({ error: e.message }); });
  }, []);
  React.useEffect(() => { loadStatus(); }, [loadStatus]);

  return (
    <CallPanelContext.Provider value={panel}>
    {/* The app shell clips its main area; each page scrolls itself. */}
    <div style={{ height: '100%', overflowY: 'auto' }}>
      <div style={{ padding: '18px 22px', maxWidth: 1150 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 18, fontWeight: 650, margin: 0, color: 'var(--text)' }}>Calling</h1>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2, padding: 2, background: 'var(--surface-2)', borderRadius: 8, marginTop: 12, width: 'fit-content', maxWidth: '100%' }}>
          {[['calls', 'Calls'], ['sent', 'Sent'], ['emails', 'Emails'], ['send', 'Send contacts'], ['campaigns', 'Email campaigns'], ['setup', 'Setup']]
            .filter(([k]) => k !== 'setup' || !status || status.is_admin)   // setup is admin work
            .map(([k, l]) => (
            <button key={k} style={callTabBtn(tab === k)} onClick={() => pickTab(k)}>
              {l}{k === 'setup' && status && !status.connected ? ' ·' : ''}
              {k === 'emails' && drafts ? <span style={{ marginLeft: 5, fontSize: 11, padding: '0 6px', borderRadius: 9, background: 'var(--accent)', color: '#fff' }}>{drafts}</span> : null}
            </button>
          ))}
        </div>
        {status && status.error && (
          <div style={{ marginTop: 12, fontSize: 12.5, color: 'var(--rose-fg)' }}>{status.error}</div>
        )}
        {tab === 'calls' && <CallingCallsTab status={status} />}
        {tab === 'sent' && <CallingSentTab isAdmin={!!(status && status.is_admin)} />}
        {tab === 'emails' && <CallingEmailsTab onCount={setDrafts} />}
        {tab === 'send' && <CallingSendTab status={status} onSeeSent={() => pickTab('sent')} />}
        {tab === 'campaigns' && <CallingEmailCampaignsTab status={status} />}
        {tab === 'setup' && <CallingSetupTab status={status} onChanged={loadStatus} />}
      </div>
    </div>
    {panelCall && <CallDetailDrawer call={panelCall.call} focus={panelCall.focus} onClose={() => setPanelCall(null)} />}
    </CallPanelContext.Provider>
  );
};

// ── Calls: the dashboard + the feed ────────────────────────────────────────
const CALL_GROUPS = [
  ['', 'All calls'], ['booked', 'Booked'], ['conversations', 'Conversations'], ['no_answer', 'No answer'],
  ['declined', 'Not interested / qualified'], ['bad_number', 'Bad number'], ['scored', 'Scored'], ['skipped', 'Skipped'],
];
const pct = (x) => (x == null ? '—' : `${Math.round(x * 100)}%`);
const talk = (s) => {
  if (!s) return '0m';
  const h = Math.floor(s / 3600); const m = Math.round((s % 3600) / 60);
  return h ? `${h}h ${m}m` : `${m}m`;
};
const shortDay = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'numeric', day: 'numeric' });
};
const hourLabel = (h) => `${((h + 11) % 12) + 1}${h < 12 ? 'a' : 'p'}`;

const CallStat = ({ label, value, sub, onClick, active, accent }) => (
  <button onClick={onClick} disabled={!onClick}
    style={{ textAlign: 'left', padding: '10px 12px', borderRadius: 10, cursor: onClick ? 'pointer' : 'default',
      border: `1px solid ${active ? 'var(--border-strong)' : 'var(--border)'}`, background: active ? 'var(--surface-2)' : 'var(--surface)',
      minWidth: 118, flex: '1 1 118px' }}>
    <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 650, color: accent ? 'var(--green-fg)' : 'var(--text)', lineHeight: 1.25 }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: 'var(--text-4)' }}>{sub}</div>}
  </button>
);

// One-series bars (the value is the height); hover shows the full row.
const CallBars = ({ title, rows, value, label, tip, format = (v) => v, empty }) => {
  const max = Math.max(1, ...rows.map(value));
  return (
    <div style={{ ...callBox, marginTop: 0, padding: '10px 12px', flex: '1 1 320px' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 8 }}>{title}</div>
      {!rows.length ? <div style={{ fontSize: 12, color: 'var(--text-4)', padding: '18px 0' }}>{empty}</div> : (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 120 }}>
          {rows.map((r, i) => {
            const v = value(r);
            return (
              <div key={i} title={tip(r)} style={{ flex: 1, minWidth: 14, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, height: '100%', justifyContent: 'flex-end' }}>
                <span style={{ fontSize: 10.5, color: 'var(--text-3)' }}>{v ? format(v) : ''}</span>
                <div style={{ width: '100%', maxWidth: 28, height: `${Math.max(2, (v / max) * 88)}px`,
                  background: 'var(--accent)', borderRadius: '4px 4px 0 0', opacity: v ? 1 : 0.25 }} />
                <span style={{ fontSize: 10, color: 'var(--text-4)', whiteSpace: 'nowrap' }}>{label(r)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const CallTable = ({ title, rows, keyName, keyLabel }) => (
  <div style={{ ...callBox, marginTop: 0, flex: '1 1 320px' }}>
    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', padding: '10px 12px 4px' }}>{title}</div>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
      <thead>
        <tr style={{ color: 'var(--text-3)', textAlign: 'right' }}>
          <th style={{ textAlign: 'left', padding: '4px 12px', fontWeight: 500 }}>{keyLabel}</th>
          <th style={{ padding: '4px 8px', fontWeight: 500 }}>Dials</th>
          <th style={{ padding: '4px 8px', fontWeight: 500 }}>Connects</th>
          <th style={{ padding: '4px 8px', fontWeight: 500 }}>Rate</th>
          <th style={{ padding: '4px 12px', fontWeight: 500 }}>Booked</th>
        </tr>
      </thead>
      <tbody>
        {rows.slice(0, 10).map((r) => (
          <tr key={r[keyName]} style={{ borderTop: '1px solid var(--border)', textAlign: 'right' }}>
            <td style={{ textAlign: 'left', padding: '5px 12px', color: 'var(--text)' }}>{r[keyName]}</td>
            <td style={{ padding: '5px 8px' }}>{r.dials}</td>
            <td style={{ padding: '5px 8px' }}>{r.connects}</td>
            <td style={{ padding: '5px 8px', color: 'var(--text-3)' }}>{pct(r.dials ? r.connects / r.dials : null)}</td>
            <td style={{ padding: '5px 12px', fontWeight: r.booked ? 650 : 400, color: r.booked ? 'var(--green-fg)' : 'var(--text-3)' }}>{r.booked}</td>
          </tr>
        ))}
        {!rows.length && <tr><td colSpan={5} style={{ padding: '10px 12px', color: 'var(--text-4)' }}>No calls yet.</td></tr>}
      </tbody>
    </table>
  </div>
);

// A Calls-tab section that folds away; open/closed is remembered per
// browser (a convenience only — it renders fine without storage).
const CallSection = ({ id, title, count, summary, accent, defaultOpen = true, children }) => {
  const key = `atlas.calling.section.${id}`;
  const [open, setOpen] = React.useState(() => {
    try { const v = window.localStorage.getItem(key); return v == null ? defaultOpen : v === '1'; } catch (_) { return defaultOpen; }
  });
  const toggle = () => setOpen((o) => {
    try { window.localStorage.setItem(key, o ? '0' : '1'); } catch (_) {}
    return !o;
  });
  return (
    <div style={{ ...callBox, ...(accent ? { borderColor: accent } : {}) }}>
      <button onClick={toggle} aria-expanded={open}
        style={{ display: 'flex', gap: 8, alignItems: 'baseline', width: '100%', padding: '10px 14px', background: 'none',
          border: 0, cursor: 'pointer', textAlign: 'left', fontSize: 12, color: 'var(--text-2)' }}>
        <span style={{ display: 'inline-block', width: 10, color: 'var(--text-4)', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 120ms' }}>▸</span>
        <span style={{ fontWeight: 600 }}>{title}</span>
        {count != null && <span style={{ color: 'var(--text-3)' }}>· {count}</span>}
        {summary && <span style={{ color: 'var(--text-4)', fontWeight: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{summary}</span>}
      </button>
      {open && children}
    </div>
  );
};

// Suggested CRM updates from call transcripts (backend app/call_actions.py):
// one card per call — its new contact, notes and corrections together.
const ACTION_HUE = { new_contact: 'green', note: 'blue', update_contact: 'amber' };
const ACTION_FIELD_LABEL = { title: 'Title', phone: 'Phone', email: 'Email' };
const ACTION_VERB = { new_contact: 'Add contact', note: 'Add note', update_contact: 'Update' };

const CallActionRow = ({ a, p, onEdit, busy, err, onApply, onDismiss }) => {
  const set = (k, v) => onEdit({ ...p, [k]: v });
  const inp = (k, ph, w) => (
    <input style={{ ...callSel, width: w || 150 }} placeholder={ph} value={p[k] || ''} onChange={(e) => set(k, e.target.value)} />
  );
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 0', borderTop: '1px dashed var(--border)' }}>
      <div style={{ width: 92, flexShrink: 0, paddingTop: 4 }}>
        <Pill hue={ACTION_HUE[a.kind] || 'slate'} sz="sm" title={a.evidence || undefined}>{a.kind_label}</Pill>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {a.kind === 'new_contact' && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {inp('first_name', 'First name', 110)}{inp('last_name', 'Last name', 120)}{inp('title', 'Title', 130)}
            {inp('phone', 'Phone', 120)}{inp('email', 'Email', 170)}
            <label style={{ display: 'flex', gap: 5, alignItems: 'center', color: 'var(--text-2)' }}>
              <input type="checkbox" checked={!!p.call_next} onChange={(e) => set('call_next', e.target.checked)} /> Mark to call
            </label>
          </div>
        )}
        {a.kind === 'note' && (
          <textarea style={{ ...callSel, width: '100%', boxSizing: 'border-box', minHeight: 40, fontFamily: 'inherit', lineHeight: 1.45, resize: 'vertical' }}
            value={p.text || ''} onChange={(e) => set('text', e.target.value)} />
        )}
        {a.kind === 'update_contact' && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--text-2)' }}>{ACTION_FIELD_LABEL[p.field] || p.field}:</span>
            <span style={{ color: 'var(--text-4)', textDecoration: p.current ? 'line-through' : 'none' }}>{p.current || 'empty'}</span>
            <span style={{ color: 'var(--text-4)' }}>→</span>
            {inp('value', '', 220)}
          </div>
        )}
        <div style={{ fontSize: 11.5, color: 'var(--text-4)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          title={a.evidence || undefined}>
          {a.kind === 'new_contact' && p.relationship ? `${p.relationship} · ` : ''}{a.evidence ? `“${a.evidence}”` : ''}
        </div>
        {err && <div style={{ color: 'var(--rose-fg)', marginTop: 4 }}>{err}</div>}
      </div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0, paddingTop: 2 }}>
        <Btn size="xs" variant="ghost" disabled={!!busy} onClick={onDismiss}>Dismiss</Btn>
        <Btn size="xs" variant="outline" disabled={!!busy} onClick={onApply}>{busy === 'apply' ? 'Applying…' : ACTION_VERB[a.kind]}</Btn>
      </div>
    </div>
  );
};

const CallSuggestionGroup = ({ items, onDone }) => {
  const first = items[0];
  const [edits, setEdits] = React.useState(() => Object.fromEntries(items.map((a) => [a.id, a.payload || {}])));
  const [busy, setBusy] = React.useState({});
  const [errs, setErrs] = React.useState({});
  const run = async (a, kind) => {
    setBusy((b) => ({ ...b, [a.id]: kind })); setErrs((e) => ({ ...e, [a.id]: null }));
    try {
      if (kind === 'apply') {
        const r = await window.AtlasApi.dialerApplyAction(a.id, edits[a.id]);
        const p = edits[a.id];
        pushToast({ type: 'success', title: a.kind === 'new_contact' ? `${p.first_name} ${p.last_name} added` : a.kind === 'note' ? 'Note added' : 'Contact updated',
          body: r.result && r.result.queued ? 'Also marked to call.' : undefined });
      } else {
        await window.AtlasApi.dialerDismissAction(a.id);
      }
      onDone(a.id);
      return true;
    } catch (e) { setErrs((x) => ({ ...x, [a.id]: e.message })); return false; }
    finally { setBusy((b) => ({ ...b, [a.id]: null })); }
  };
  const all = async (kind) => { for (const a of items) { if (!(await run(a, kind))) break; } };
  const anyBusy = Object.values(busy).some(Boolean);
  return (
    <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border)', fontSize: 12.5 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 2 }}>
        {first.contact_id ? <RecLink kind="contact" id={first.contact_id} style={{ fontWeight: 600, color: 'var(--text)' }}>{first.contact_name || 'Contact'}</RecLink> : null}
        {first.company && <span style={{ color: 'var(--text-3)' }}>{first.company}</span>}
        <span style={{ color: 'var(--text-4)' }}>· {first.outcome_label} · {callWhen(first.called_at)}</span>
        <span style={{ flex: 1 }} />
        {items.length > 1 && (
          <>
            <Btn size="xs" variant="ghost" disabled={anyBusy} onClick={() => all('dismiss')}>Dismiss all</Btn>
            <Btn size="xs" variant="accent" disabled={anyBusy} onClick={() => all('apply')}>Apply all {items.length}</Btn>
          </>
        )}
      </div>
      {items.map((a) => (
        <CallActionRow key={a.id} a={a} p={edits[a.id]} busy={busy[a.id]} err={errs[a.id]}
          onEdit={(np) => setEdits((e) => ({ ...e, [a.id]: np }))}
          onApply={() => run(a, 'apply')} onDismiss={() => run(a, 'dismiss')} />
      ))}
    </div>
  );
};

const CallSuggestions = () => {
  const [items, setItems] = React.useState(null);
  const [show, setShow] = React.useState(3);
  React.useEffect(() => {
    window.AtlasApi.dialerActions({ status: 'suggested' }).then((r) => setItems(r.items || [])).catch(() => setItems([]));
  }, []);
  if (!items || !items.length) return null;
  const groups = [];
  const byCall = {};
  for (const a of items) {
    if (!byCall[a.call_id]) { byCall[a.call_id] = []; groups.push(byCall[a.call_id]); }
    byCall[a.call_id].push(a);
  }
  const n = (k) => items.filter((a) => a.kind === k).length;
  const summary = [n('new_contact') && `${n('new_contact')} contact${n('new_contact') === 1 ? '' : 's'} to add`,
    n('note') && `${n('note')} note${n('note') === 1 ? '' : 's'}`, n('update_contact') && `${n('update_contact')} correction${n('update_contact') === 1 ? '' : 's'}`]
    .filter(Boolean).join(' · ');
  return (
    <CallSection id="suggested" title="Suggested from calls" count={`${groups.length} call${groups.length === 1 ? '' : 's'}`}
      summary={summary} accent="var(--accent)">
      {groups.slice(0, show).map((g) => (
        <CallSuggestionGroup key={g[0].call_id} items={g} onDone={(id) => setItems((xs) => xs.filter((x) => x.id !== id))} />
      ))}
      {groups.length > show && (
        <div style={{ padding: '6px 14px 10px', borderTop: '1px solid var(--border)' }}>
          <Btn size="xs" variant="ghost" onClick={() => setShow((k) => k + 10)}>Show {Math.min(10, groups.length - show)} more calls</Btn>
        </div>
      )}
    </CallSection>
  );
};

// The Calls tab's quality block: average, outcome split, where to improve.
const callBand = (avg) => (avg >= 80 ? 'strong' : avg >= 65 ? 'solid' : avg >= 50 ? 'developing' : 'weak');

const CallQuality = ({ q, onScored }) => {
  const [all, setAll] = React.useState(false);
  const band = callBand(q.avg);
  const o = q.outcomes || {};
  return (
    <CallSection id="quality" title="Call quality" count={`${q.scored} scored`} defaultOpen={false}
      summary={`average ${Math.round(q.avg)} (${band})${q.weakest && q.weakest[0] ? ` · weakest: ${q.weakest[0].label}` : ''}`}>
    <div style={{ padding: '0 14px 12px' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 8 }}>
        <button onClick={onScored} style={{ fontSize: 11.5, color: 'var(--text-3)', background: 'none', border: 0, padding: 0, cursor: 'pointer', textDecoration: 'underline' }}>
          See the {q.scored} scored call{q.scored === 1 ? '' : 's'}
        </button>
        <span style={{ flex: 1 }} />
      </div>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ minWidth: 120 }}>
          <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>Average score</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{ fontSize: 24, fontWeight: 650, color: 'var(--text)' }}>{Math.round(q.avg)}</span>
            <Pill hue={SCORE_HUE[band]} sz="sm">{band}</Pill>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 2 }}>
            Outcome: {o.advanced || 0} advanced · {o.kept_open || 0} kept open · {o.closed || 0} closed
          </div>
        </div>
        <div style={{ flex: '1 1 360px' }}>
          <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginBottom: 4 }}>Where to improve</div>
          {(q.weakest || []).map((w) => (
            <div key={w.key} style={{ marginBottom: 6 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12 }}>
                <span style={{ width: 230, color: 'var(--text-2)' }}>{w.label}</span>
                <ScoreBar value={w.avg} max={w.max} />
                <span style={{ width: 52, textAlign: 'right', color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>{w.avg}/{w.max}</span>
              </div>
              {w.tip && <div style={{ fontSize: 11.5, color: 'var(--text-3)', lineHeight: 1.45, marginTop: 2 }}>→ {w.tip}</div>}
            </div>
          ))}
          <button onClick={() => setAll((a) => !a)} style={{ fontSize: 11.5, color: 'var(--text-3)', background: 'none', border: 0, padding: 0, cursor: 'pointer' }}>
            {all ? 'Hide the full rubric' : 'All ten parts'}
          </button>
          {all && (
            <div style={{ marginTop: 6 }}>
              {(q.components || []).map((comp) => (
                <div key={comp.key} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, marginBottom: 4 }}>
                  <span style={{ width: 230, color: 'var(--text-2)' }}>{comp.label}</span>
                  <ScoreBar value={comp.avg} max={comp.max} />
                  <span style={{ width: 52, textAlign: 'right', color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>{comp.avg}/{comp.max}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
    </CallSection>
  );
};

const CallingCallsTab = ({ status }) => {
  const panel = React.useContext(CallPanelContext);
  const [period, setPeriod] = React.useState('7');
  const [caller, setCaller] = React.useState('');
  const [group, setGroup] = React.useState('');
  const [q, setQ] = React.useState('');
  const [withRec, setWithRec] = React.useState(false);
  const [dash, setDash] = React.useState(null);
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [syncing, setSyncing] = React.useState(false);
  const [playing, setPlaying] = React.useState({});   // booked-call players open
  const range = period === 'today' ? { today: 'true', days: 1 } : { days: Number(period) };
  const rangeKey = JSON.stringify([period, caller]);
  const loadDash = React.useCallback(() => {
    const p = { ...range };
    if (caller) p.caller_member_id = caller;
    window.AtlasApi.dialerDashboard(p).then(setDash).catch(() => setDash(null));
  }, [rangeKey]);   // eslint-disable-line react-hooks/exhaustive-deps
  const reqRef = React.useRef(0);
  const loadCalls = React.useCallback(() => {
    const mine = ++reqRef.current;
    const p = { ...range, limit: 300 };
    if (caller) p.caller_member_id = caller;
    if (group) p.group = group;
    if (q.trim()) p.q = q.trim();
    if (withRec) p.has_recording = 'true';
    window.AtlasApi.dialerCalls(p)
      .then((r) => { if (mine === reqRef.current) { setData(r); setError(null); } })
      .catch((e) => { if (mine === reqRef.current) { setError(e.message); setData({ items: [] }); } });
  }, [rangeKey, group, q, withRec]);   // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => { loadDash(); }, [loadDash]);
  React.useEffect(() => { const t = setTimeout(loadCalls, 250); return () => clearTimeout(t); }, [loadCalls]);
  // Calls land by webhook all day: refresh while the tab is open.
  React.useEffect(() => {
    const t = setInterval(() => { loadDash(); loadCalls(); }, 60000);
    return () => clearInterval(t);
  }, [loadDash, loadCalls]);

  const sync = async () => {
    setSyncing(true);
    try {
      await window.AtlasApi.dialerSync(2);
      pushToast({ type: 'info', title: 'Pulling calls from PhoneBurner' });
      setTimeout(() => { loadDash(); loadCalls(); }, 8000);
    } catch (e) { pushToast({ type: 'error', title: 'Sync failed', body: e.message }); }
    finally { setSyncing(false); }
  };

  const t = (dash && dash.totals) || {};
  const seats = ((status && status.members) || []).filter((m) => m.atlas_member_id);
  const pick = (g) => setGroup((cur) => (cur === g ? '' : g));
  return (
    <>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 2, padding: 2, background: 'var(--surface-2)', borderRadius: 8 }}>
          {[['today', 'Today'], ['7', '7 days'], ['30', '30 days'], ['90', '90 days']].map(([k, l]) => (
            <button key={k} style={callTabBtn(period === k)} onClick={() => setPeriod(k)}>{l}</button>
          ))}
        </div>
        {seats.length > 1 && (
          <window.Picker value={caller} onChange={setCaller} emptyLabel="Every caller" minWidth={150}
            options={seats.map((m) => ({ value: m.atlas_member_id, label: m.atlas_member_name || m.name }))} />
        )}
        <span style={{ flex: 1 }} />
        {status && status.unmatched_calls ? (
          <span style={{ fontSize: 12, color: 'var(--amber-fg)' }}>{status.unmatched_calls} not matched to a contact</span>
        ) : null}
        <Btn size="sm" variant="outline" icon={<Icon.Refresh size={12} />} disabled={syncing || !(status && status.connected)} onClick={sync}
          title="Read the last two days of dial sessions from PhoneBurner">
          {syncing ? 'Syncing…' : 'Sync now'}
        </Btn>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <CallStat label="Dials" value={(t.dials || 0).toLocaleString()} sub={`${(t.contacts_dialed || 0).toLocaleString()} contacts`}
          onClick={() => pick('')} active={group === ''} />
        <CallStat label="Connects" value={(t.connects || 0).toLocaleString()} sub={`${pct(t.connect_rate)} of dials`}
          onClick={() => pick('conversations')} active={group === 'conversations'} />
        <CallStat label="Booked" value={(t.booked || 0).toLocaleString()} sub={`${pct(t.booking_rate)} of connects`}
          onClick={() => pick('booked')} active={group === 'booked'} accent />
        <CallStat label="Not interested / qualified" value={(t.declined || 0).toLocaleString()}
          onClick={() => pick('declined')} active={group === 'declined'} />
        <CallStat label="No answer" value={(t.no_answer || 0).toLocaleString()}
          onClick={() => pick('no_answer')} active={group === 'no_answer'} />
        <CallStat label="Bad numbers" value={(t.bad_numbers || 0).toLocaleString()}
          onClick={() => pick('bad_number')} active={group === 'bad_number'} />
        <CallStat label="Talk time" value={talk(t.talk_seconds)}
          sub={t.connects ? `${Math.round((t.talk_seconds || 0) / t.connects)}s a connect` : undefined} />
      </div>
      {dash && (dash.queue || t.skipped) && (
        <div style={{ fontSize: 11.5, color: 'var(--text-4)', marginTop: 6 }}>
          {dash.queue ? `${dash.queue.marked_to_call.toLocaleString()} marked to call · ${dash.queue.sent_not_called.toLocaleString()} in PhoneBurner not dialed yet` : ''}
          {t.skipped ? ` · ${t.skipped} skipped (outside calling hours) — not counted as dials` : ''}
        </div>
      )}

      {dash && dash.booked && dash.booked.length > 0 && (
        <CallSection id="booked" title="Booked calls" count={dash.booked.length} accent="var(--green-fg)"
          summary={dash.booked.slice(0, 4).map((c) => c.contact_name).filter(Boolean).join(', ') + (dash.booked.length > 4 ? '…' : '')}>
          {dash.booked.map((c) => (
            <div key={c.id} style={{ padding: '7px 14px', borderTop: '1px solid var(--border)', cursor: panel ? 'pointer' : 'default' }}
              onClick={panel ? (e) => { if (!e.target.closest('a, button, audio')) panel.open(c); } : undefined}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5 }}>
              <RecLink kind="contact" id={c.contact_id} style={{ fontWeight: 600, color: 'var(--text)' }}>{c.contact_name || 'Contact'}</RecLink>
              {c.company_id && <RecLink kind="company" id={c.company_id} style={{ color: 'var(--text-2)' }}>{c.company_name}</RecLink>}
              <span style={{ color: 'var(--text-3)' }}>{callWhen(c.started_at)}</span>
              {c.caller_name && <span style={{ color: 'var(--text-4)' }}>· {c.caller_name}</span>}
              <span style={{ flex: 1 }} />
              {c.recording_url && (
                <RecordingButton open={!!playing[c.id]} onToggle={() => setPlaying((p) => ({ ...p, [c.id]: !p[c.id] }))} />
              )}
            </div>
            {playing[c.id] && c.recording_url && <RecordingPlayer url={c.recording_url} />}
            </div>
          ))}
        </CallSection>
      )}

      <CallSuggestions />
      {dash && dash.quality && dash.quality.scored > 0 && <CallQuality q={dash.quality} onScored={() => setGroup('scored')} />}

      {dash && (
        <CallSection id="breakdown" title="Activity breakdown" defaultOpen={false}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', padding: '0 12px' }}>
          <CallBars title="Dials by day" rows={dash.by_day || []} value={(r) => r.dials} label={(r) => shortDay(r.day)}
            tip={(r) => `${shortDay(r.day)}: ${r.dials} dials · ${r.connects} connects · ${r.booked} booked · ${talk(r.talk_seconds)} talk`}
            empty="No dials in this period." />
          <CallBars title="Connect rate by hour (Eastern)" rows={dash.by_hour || []}
            value={(r) => (r.dials ? r.connects / r.dials : 0)} format={(v) => pct(v)} label={(r) => hourLabel(r.hour)}
            tip={(r) => `${hourLabel(r.hour)}: ${r.connects} of ${r.dials} dials connected · ${r.booked} booked`}
            empty="No dials in this period." />
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap', padding: '0 12px 12px' }}>
          <CallTable title="By industry" rows={dash.by_industry || []} keyName="industry" keyLabel="Industry" />
          <CallTable title="By caller" rows={dash.by_caller || []} keyName="caller" keyLabel="Caller" />
        </div>
        </CallSection>
      )}

      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 16, flexWrap: 'wrap' }}>
        {CALL_GROUPS.map(([k, l]) => (
          <button key={k || 'all'} onClick={() => setGroup(k)}
            style={{ ...callTabBtn(group === k), fontSize: 12, padding: '4px 9px',
              border: `1px solid ${group === k ? 'var(--border-strong)' : 'var(--border)'}` }}>{l}</button>
        ))}
        <span style={{ flex: 1 }} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, company or phone…" style={{ ...callSel, width: 200 }} />
        <label style={{ fontSize: 12, color: 'var(--text-2)', display: 'flex', gap: 5, alignItems: 'center' }}>
          <input type="checkbox" checked={withRec} onChange={(e) => setWithRec(e.target.checked)} /> Has recording
        </label>
      </div>
      <div style={callBox}>
        {error && <div style={{ padding: 14, fontSize: 12.5, color: 'var(--rose-fg)' }}>{error}</div>}
        {!data && !error && <div style={{ padding: 14, fontSize: 12.5, color: 'var(--text-4)' }}>Loading…</div>}
        {data && !data.items.length && !error && (
          <div style={{ padding: 18, fontSize: 12.5, color: 'var(--text-3)', lineHeight: 1.6 }}>
            No calls match.{status && !status.connected ? (status.is_admin ? ' Connect PhoneBurner in Setup first.' : ' PhoneBurner isn’t connected yet — ask an admin.') : ''}
          </div>
        )}
        {(data ? data.items : []).map((c) => <DialerCallRow key={c.id} c={c} showContact showCompany />)}
      </div>
    </>
  );
};

// ── Sent: who went to PhoneBurner and what happened since ──────────────────
const SENT_STATUS_HUE = {
  not_called: 'slate', no_answer: 'amber', talked: 'blue', call_scheduled: 'green', ds_call: 'green', do_not_call: 'rose', bad_number: 'slate',
};
const SENT_STATUS_ORDER = ['not_called', 'no_answer', 'talked', 'call_scheduled', 'ds_call', 'do_not_call', 'bad_number'];

// ── Emails: the caller's drafts after a booked / more-info call ───────────
// Atlas drafts, the caller reviews and clicks Send: it goes out from their
// own Outlook (backend app/dialer_emails.py). Nothing sends on its own.

const EMAIL_KIND_HUE = { booked: 'green', more_info: 'blue' };
const callInput = { ...callSel, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' };

// datetime-local wants local "YYYY-MM-DDTHH:mm".
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const EmailDraftCard = ({ em, onChanged, ask }) => {
  const [to, setTo] = React.useState(em.to_email || '');
  const [subject, setSubject] = React.useState(em.subject || '');
  const [body, setBody] = React.useState(em.body || '');
  const [meeting, setMeeting] = React.useState(toLocalInput(em.meeting_at));
  const [busy, setBusy] = React.useState(null);
  const [err, setErr] = React.useState(em.error || null);
  React.useEffect(() => {
    setTo(em.to_email || ''); setSubject(em.subject || ''); setBody(em.body || ''); setMeeting(toLocalInput(em.meeting_at));
  }, [em.id, em.body, em.subject, em.to_email, em.meeting_at]);   // eslint-disable-line react-hooks/exhaustive-deps
  const edits = () => ({ to_email: to, subject, body, ...(meeting ? { meeting_at: new Date(meeting).toISOString() } : { clear_meeting: true }) });
  const dirty = to !== (em.to_email || '') || subject !== (em.subject || '') || body !== (em.body || '')
    || meeting !== toLocalInput(em.meeting_at);
  const save = async () => {
    if (!dirty) return;
    try { onChanged(await window.AtlasApi.dialerEmailEdit(em.id, edits())); setErr(null); }
    catch (e) { setErr(e.message); }
  };
  const act = async (kind) => {
    if (kind === 'send' && !(await ask({
      title: `Send to ${to || 'the owner'}?`,
      body: `It goes out from ${em.sender_email || 'your mailbox'} and a copy lands in your Sent folder.`,
      confirmLabel: 'Send' }))) return;
    setBusy(kind);
    try {
      const r = kind === 'send' ? await window.AtlasApi.dialerEmailSend(em.id, edits())
        : kind === 'write' ? await window.AtlasApi.dialerEmailWrite(em.id)
        : await window.AtlasApi.dialerEmailDismiss(em.id);
      setErr(null);
      onChanged(r);
      if (kind === 'send') pushToast({ type: 'success', title: 'Email sent', body: `To ${r.to_email}` });
    } catch (e) { setErr(e.message); }
    finally { setBusy(null); }
  };
  const needsDay = body.includes('{meeting_day}') || subject.includes('{meeting_day}');
  const needsAsk = body.includes('{what_they_asked}');
  const label = { fontSize: 11, color: 'var(--text-4)', marginBottom: 3 };
  return (
    <div style={{ padding: '12px 14px', borderTop: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5, marginBottom: 8 }}>
        <Pill hue={EMAIL_KIND_HUE[em.kind] || 'slate'} sz="sm">{em.kind_label}</Pill>
        <RecLink kind="contact" id={em.contact_id} style={{ fontWeight: 600, color: 'var(--text)' }}>{em.contact_name || 'Contact'}</RecLink>
        {em.company && <span style={{ color: 'var(--text-3)' }}>{em.company}</span>}
        <span style={{ color: 'var(--text-4)' }}>· drafted {callWhen(em.created_at)}</span>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>from {em.sender_email || 'no mailbox'}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: em.kind === 'booked' ? 'minmax(0,1fr) 220px' : 'minmax(0,1fr)', gap: 10 }}>
        <div>
          <div style={label}>To</div>
          <input style={callInput} value={to} placeholder="owner@company.com" onChange={(e) => setTo(e.target.value)} onBlur={save} />
        </div>
        {em.kind === 'booked' && (
          <div>
            <div style={label}>Call day{em.meeting_source === 'calendar' ? ' (from your Outlook)' : ''}</div>
            <input type="datetime-local" style={callInput} value={meeting} onChange={(e) => setMeeting(e.target.value)} onBlur={save} />
          </div>
        )}
      </div>
      <div style={{ marginTop: 8 }}>
        <div style={label}>Subject</div>
        <input style={callInput} value={subject} onChange={(e) => setSubject(e.target.value)} onBlur={save} />
      </div>
      <div style={{ marginTop: 8 }}>
        <div style={label}>Email</div>
        <textarea style={{ ...callInput, minHeight: 260, lineHeight: 1.5, resize: 'vertical' }} value={body}
          onChange={(e) => setBody(e.target.value)} onBlur={save} />
      </div>
      {needsDay && (
        <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 4, lineHeight: 1.5 }}>
          {meeting ? <>{'{meeting_day}'} becomes <b>{new Date(meeting).toLocaleDateString(undefined, { weekday: 'long' })}</b> when it sends. </>
            : <>Pick the call day above to fill {'{meeting_day}'}. </>}
        </div>
      )}
      {err && <div style={{ fontSize: 12, color: 'var(--rose-fg)', marginTop: 6 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
        {needsAsk && <Btn size="xs" variant="outline" disabled={!!busy} onClick={() => act('write')}>{busy === 'write' ? 'Writing…' : 'Write it with Claude'}</Btn>}
        <span style={{ flex: 1 }} />
        <Btn size="xs" variant="ghost" disabled={!!busy} onClick={() => act('dismiss')}>Dismiss</Btn>
        <Btn size="sm" variant="accent" icon={<Icon.Mail size={12} />} disabled={!!busy} onClick={() => act('send')}>
          {busy === 'send' ? 'Sending…' : 'Send from Outlook'}
        </Btn>
      </div>
    </div>
  );
};

const emailOutcome = (em) => {
  if (em.replied_at) return { hue: 'green', text: `Replied ${callWhen(em.replied_at)}` };
  if (em.recalled_at) return em.recall_folder
    ? { hue: 'amber', text: `No reply — back to ${em.recall_folder} ${callWhen(em.recalled_at)}` }
    : { hue: 'slate', text: 'Called again since' };
  if (em.kind === 'more_info' && em.follow_up_due) return { hue: 'slate', text: `Back to calls ${callWhen(em.follow_up_due)} if no reply` };
  return null;
};

const CallingEmailsTab = ({ onCount }) => {
  const { ask, dialog } = useCallingConfirm();
  const [status, setStatus] = React.useState('draft');
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const load = React.useCallback(() => {
    window.AtlasApi.dialerEmails(status).then((r) => { setData(r); setError(null); if (onCount) onCount(r.counts.draft || 0); })
      .catch((e) => { setError(e.message); setData({ items: [], counts: {} }); });
  }, [status]);   // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => { load(); }, [load]);
  const changed = (em) => {
    setData((d) => {
      const items = (d.items || []).map((x) => (x.id === em.id ? em : x)).filter((x) => !status || x.status === status);
      const counts = { ...d.counts };
      if (em.status !== 'draft') { counts.draft = Math.max(0, (counts.draft || 0) - 1); counts[em.status] = (counts[em.status] || 0) + 1; }
      if (onCount) onCount(counts.draft || 0);
      return { ...d, items, counts };
    });
  };
  const counts = (data && data.counts) || {};
  const items = (data && data.items) || [];
  return (
    <>
      {dialog}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 14, flexWrap: 'wrap' }}>
        {[['draft', 'To send'], ['sent', 'Sent'], ['dismissed', 'Dismissed']].map(([k, l]) => (
          <button key={k} style={callTabBtn(status === k)} onClick={() => setStatus(k)}>{l} · {counts[k] || 0}</button>
        ))}
        <span style={{ flex: 1 }} />
      </div>
      {error && <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--rose-fg)' }}>{error}</div>}
      <div style={callBox}>
        {!data && <div style={{ padding: 14, fontSize: 12.5, color: 'var(--text-4)' }}>Loading…</div>}
        {data && !items.length && (
          <div style={{ padding: 14, fontSize: 12.5, color: 'var(--text-3)' }}>
            {status === 'draft' ? 'Nothing to send.' : 'None yet.'}
          </div>
        )}
        {status === 'draft' && items.map((em) => <EmailDraftCard key={em.id} em={em} onChanged={changed} ask={ask} />)}
        {status !== 'draft' && items.map((em) => {
          const out = emailOutcome(em);
          return (
            <div key={em.id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '9px 14px', borderTop: '1px solid var(--border)', fontSize: 12.5 }}>
              <Pill hue={EMAIL_KIND_HUE[em.kind] || 'slate'} sz="sm">{em.kind_label}</Pill>
              <RecLink kind="contact" id={em.contact_id} style={{ fontWeight: 600, color: 'var(--text)' }}>{em.contact_name || 'Contact'}</RecLink>
              {em.company && <span style={{ color: 'var(--text-3)' }}>{em.company}</span>}
              <span style={{ color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320 }}>{em.subject}</span>
              <span style={{ flex: 1 }} />
              {em.status === 'sent' && <span style={{ color: 'var(--text-4)' }}>sent {callWhen(em.sent_at)}</span>}
              {em.status === 'dismissed' && em.error && <span style={{ color: 'var(--text-4)' }}>{em.error}</span>}
              {out && <Pill hue={out.hue} sz="sm">{out.text}</Pill>}
            </div>
          );
        })}
      </div>
    </>
  );
};

// Setup: the email templates new drafts start from.
const CallingEmailTemplatesSection = () => {
  const [data, setData] = React.useState(null);
  const [draft, setDraft] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const take = (r) => { setData(r); setDraft(JSON.parse(JSON.stringify(r.templates))); };
  React.useEffect(() => { window.AtlasApi.dialerEmailTemplates().then(take).catch((e) => setData({ error: e.message })); }, []);
  const box = { padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: 12.5 };
  if (!data) return <div style={{ ...box, color: 'var(--text-4)' }}>Loading email templates…</div>;
  if (data.error) return <div style={{ ...box, color: 'var(--rose-fg)' }}>{data.error}</div>;
  const dirty = JSON.stringify(draft) !== JSON.stringify(data.templates);
  const set = (kind, key, v) => setDraft((d) => ({ ...d, [kind]: { ...d[kind], [key]: v } }));
  const save = async () => {
    setBusy(true);
    try { take(await window.AtlasApi.dialerSaveEmailTemplates(draft)); pushToast({ type: 'success', title: 'Templates saved', body: 'New drafts use them.' }); }
    catch (e) { pushToast({ type: 'error', title: 'Could not save', body: e.message }); }
    finally { setBusy(false); }
  };
  return (
    <div style={box}>
      <div style={{ fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>Email templates</div>
      <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 8, lineHeight: 1.5 }}>
        {Object.entries(data.placeholders || {}).map(([k, v]) => (
          <span key={k} title={v} style={{ marginRight: 8, whiteSpace: 'nowrap' }}><code style={{ fontSize: 11 }}>{`{${k}}`}</code></span>
        ))}
      </div>
      {Object.entries(data.kinds || {}).map(([kind, name]) => (
        <div key={kind} style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
            <Pill hue={EMAIL_KIND_HUE[kind] || 'slate'} sz="sm">{name}</Pill>
            <span style={{ flex: 1 }} />
            <Btn size="xs" variant="ghost" onClick={() => setDraft((d) => ({ ...d, [kind]: { ...data.defaults[kind] } }))}>Reset to default</Btn>
          </div>
          <input style={{ ...callInput, marginBottom: 6 }} value={(draft[kind] || {}).subject || ''} onChange={(e) => set(kind, 'subject', e.target.value)} />
          <textarea style={{ ...callInput, minHeight: 200, lineHeight: 1.5, resize: 'vertical' }} value={(draft[kind] || {}).body || ''}
            onChange={(e) => set(kind, 'body', e.target.value)} />
        </div>
      ))}
      <Btn size="xs" variant="accent" disabled={!dirty || busy} onClick={save}>{busy ? 'Saving…' : 'Save templates'}</Btn>
    </div>
  );
};

const CallingSentTab = ({ isAdmin = false }) => {
  const [restarting, setRestarting] = React.useState(null);
  // Rows open to the contact's calls (recordings, transcripts, notes).
  const [open, setOpen] = React.useState({});
  const [calls, setCalls] = React.useState({});
  const toggle = (id) => {
    setOpen((o) => ({ ...o, [id]: !o[id] }));
    if (calls[id] === undefined) {
      window.AtlasApi.contactCalls(id)
        .then((r) => setCalls((c) => ({ ...c, [id]: r.calls || [] })))
        .catch(() => setCalls((c) => ({ ...c, [id]: null })));
    }
  };
  const [status, setStatus] = React.useState('');
  const [folder, setFolder] = React.useState('');
  const [q, setQ] = React.useState('');
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const load = React.useCallback(() => {
    const p = { limit: 1000 };
    if (status) p.status = status;
    if (folder) p.folder = folder;
    if (q.trim()) p.q = q.trim();
    window.AtlasApi.dialerSent(p).then((r) => { setData(r); setError(null); })
      .catch((e) => { setError(e.message); setData({ items: [], counts: {}, folders: [], total: 0 }); });
  }, [status, folder, q]);
  React.useEffect(() => { const t = setTimeout(load, q ? 250 : 0); return () => clearTimeout(t); }, [load, q]);

  const counts = (data && data.counts) || {};
  const labels = (data && data.labels) || {};
  const total = data ? data.total : 0;
  const called = total - (counts.not_called || 0);
  return (
    <>
      {data && (
        <div style={{ display: 'flex', gap: 18, marginTop: 14, fontSize: 12.5, color: 'var(--text-3)', flexWrap: 'wrap', alignItems: 'baseline' }}>
          <span><b style={{ color: 'var(--text)', fontSize: 15 }}>{total}</b> sent</span>
          <span><b style={{ color: 'var(--text)', fontSize: 15 }}>{called}</b> called</span>
          <span><b style={{ color: 'var(--text)', fontSize: 15 }}>{counts.not_called || 0}</b> not called yet</span>
          <span><b style={{ color: 'var(--text)', fontSize: 15 }}>{counts.talked || 0}</b> talked</span>
          <span><b style={{ color: 'var(--green-fg)', fontSize: 15 }}>{counts.call_scheduled || 0}</b> call scheduled</span>
          {counts.ds_call ? <span><b style={{ color: 'var(--green-fg)', fontSize: 15 }}>{counts.ds_call}</b> D/S calls</span> : null}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 2, padding: 2, background: 'var(--surface-2)', borderRadius: 8, flexWrap: 'wrap' }}>
          <button style={callTabBtn(status === '')} onClick={() => setStatus('')}>All {total || ''}</button>
          {SENT_STATUS_ORDER.map((k) => (
            <button key={k} style={callTabBtn(status === k)} onClick={() => setStatus(k)}>
              {labels[k] || k}{counts[k] ? ` ${counts[k]}` : ''}
            </button>
          ))}
        </div>
        <window.Picker value={folder} onChange={setFolder} emptyLabel="All folders" minWidth={200} menuMinWidth={300}
          options={((data && data.folders) || []).map((f) => ({ value: f.name, label: f.name, count: f.count }))} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or company…" style={{ ...callSel, minWidth: 180 }} />
      </div>
      <div style={{ ...callBox, maxHeight: 620, overflow: 'auto' }}>
        {error && <div style={{ padding: 14, fontSize: 12.5, color: 'var(--rose-fg)' }}>{error}</div>}
        {!data && !error && <div style={{ padding: 14, fontSize: 12.5, color: 'var(--text-4)' }}>Loading…</div>}
        {data && !data.items.length && !error && (
          <div style={{ padding: 18, fontSize: 12.5, color: 'var(--text-3)' }}>
            {total ? 'Nobody in this group.' : 'Nothing sent to PhoneBurner yet — build a list in Send contacts.'}
          </div>
        )}
        {data && data.items.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-3)', position: 'sticky', top: 0, background: 'var(--surface)' }}>
                {['Contact', 'Company', 'Phone', 'Status', 'Calls', 'Last call', 'Sent'].map((h) => (
                  <th key={h} style={{ padding: '7px 10px', fontWeight: 600, borderBottom: '1px solid var(--border)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.items.map((i) => (
                <React.Fragment key={i.contact_id}>
                <tr style={{ borderBottom: open[i.contact_id] ? 'none' : '1px solid var(--border)' }}>
                  <td style={{ padding: '7px 10px' }}>
                    <RecLink kind="contact" id={i.contact_id} style={{ color: 'var(--text)', fontWeight: 550 }}>{i.name || '—'}</RecLink>
                    {i.title && <div style={{ fontSize: 11, color: 'var(--text-4)' }}>{i.title}</div>}
                  </td>
                  <td style={{ padding: '7px 10px', color: 'var(--text-2)' }}>
                    {i.company_id ? (
                      <RecLink kind="company" id={i.company_id} style={{ color: 'var(--text-2)' }}>{i.company}</RecLink>
                    ) : '—'}
                  </td>
                  <td style={{ padding: '7px 10px', color: 'var(--text-2)' }} className="num">{i.phone || '—'}</td>
                  <td style={{ padding: '7px 10px' }}><Pill hue={SENT_STATUS_HUE[i.status] || 'slate'} sz="sm">{i.status_label}</Pill></td>
                  <td style={{ padding: '7px 10px', color: 'var(--text-2)' }} className="num">
                    {i.call_count ? (
                      <button onClick={() => toggle(i.contact_id)} title="Show the calls — recordings and transcripts"
                        style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 5, cursor: 'pointer',
                          padding: '1px 7px', fontSize: 12, color: 'var(--accent-text)' }}>
                        {i.call_count} {open[i.contact_id] ? '▴' : '▾'}
                      </button>
                    ) : '—'}
                  </td>
                  <td style={{ padding: '7px 10px', color: 'var(--text-3)' }}>
                    {i.last_called_at ? `${callWhen(i.last_called_at)} · ${i.last_outcome_label || CALL_OUTCOME_LABEL[i.last_outcome] || ''}` : '—'}
                    {i.callback_at && (
                      <div style={{ fontSize: 11, color: 'var(--accent-text)' }}
                        title={i.callback_in_calendar ? 'On the caller’s Outlook calendar' : 'Not on the Outlook calendar yet'}>
                        Call back {callWhen(i.callback_at)}{i.callback_time_set ? '' : ' (no time set)'}{i.callback_in_calendar ? ' · in Outlook' : ''}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '7px 10px', color: 'var(--text-3)' }}>
                    {callWhen(i.sent_at)}
                    <div style={{ fontSize: 11, color: 'var(--text-4)' }}>
                      {[i.sent_by, i.folder_name, i.times_sent > 1 ? `sent ${i.times_sent}×` : null].filter(Boolean).join(' · ')}
                    </div>
                  </td>
                </tr>
                {open[i.contact_id] && (
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <td colSpan={7} style={{ padding: '0 10px 8px 24px', background: 'var(--surface-2)' }}>
                      {calls[i.contact_id] === undefined
                        ? <div style={{ padding: 10, fontSize: 12, color: 'var(--text-4)' }}>Loading calls…</div>
                        : calls[i.contact_id] === null
                          ? <div style={{ padding: 10, fontSize: 12, color: 'var(--rose-fg)' }}>Could not load the calls.</div>
                          : calls[i.contact_id].map((c) => <DialerCallRow key={c.id} c={c} />)}
                      {isAdmin && i.folder_name && !/attempt\s*1/i.test(i.folder_name) && (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 4px 2px', fontSize: 12, color: 'var(--text-3)' }}>
                          <span>In PhoneBurner’s “{i.folder_name}” folder.</span>
                          <Btn size="xs" variant="outline" disabled={restarting === i.contact_id}
                            title="Move it back to REC or NO REC – Attempt 1 (by its state's recording rule), so it's dialed again"
                            onClick={async () => {
                              setRestarting(i.contact_id);
                              try {
                                const r = await window.AtlasApi.dialerRestart([i.contact_id]);
                                const where = Object.keys(r.by_folder || {})[0];
                                pushToast({ type: r.moved ? 'success' : 'info', title: r.moved ? `Moved to ${where}` : 'Not moved',
                                  body: r.moved ? 'It will be dialed again.' : ((r.skipped || [])[0] || {}).reason || (r.already ? 'Already in Attempt 1.' : undefined) });
                                load();
                              } catch (e) { pushToast({ type: 'error', title: 'Could not move it', body: e.message }); }
                              finally { setRestarting(null); }
                            }}>
                            {restarting === i.contact_id ? 'Moving…' : 'Move back to Attempt 1'}
                          </Btn>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

// The caller's notes (seller brief + comps) are written on a contact's FIRST
// send, so the queue should be prepared before it goes out.
const CallingBriefsBar = ({ initial, queueCount }) => {
  const [b, setB] = React.useState(initial || null);
  React.useEffect(() => { if (initial) setB(initial); }, [initial]);
  React.useEffect(() => {
    if (!b || !b.active) return undefined;
    const t = setTimeout(() => {
      window.AtlasApi.dialerBriefsStatus().then(setB).catch(() => {});
    }, 8000);
    return () => clearTimeout(t);
  }, [b]);
  if (!b || !queueCount || !b.companies) return null;
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 12, color: 'var(--text-2)',
      padding: '7px 10px', borderRadius: 8, background: 'var(--surface-2)' }}>
      <span style={{ fontWeight: 600, color: 'var(--text)' }}>Caller notes</span>
      <span>
        {b.ready.toLocaleString()} of {b.companies.toLocaleString()} queued companies ready now
      </span>
      {b.facts_ready != null && (
        <span style={{ color: 'var(--text-3)' }}
          title="Years in operation, locations, est. revenue and employees">
          · company facts {Math.min(b.facts_ready, b.companies).toLocaleString()} of {b.companies.toLocaleString()}
        </span>
      )}
      {b.apollo_pending > 0 && !b.apollo_key && (
        <span style={{ color: 'var(--amber-fg)' }} title="Add APOLLO_KEY to the API service's environment in Render">· Apollo isn’t set up on the server ({b.apollo_pending} waiting)</span>
      )}
      {b.failed > 0 && <span style={{ color: 'var(--text-3)' }}>· {b.failed} couldn’t be built</span>}
      <span style={{ flex: 1 }} />
      {b.active && <Pill hue="blue" sz="sm">Preparing the queue…</Pill>}
    </div>
  );
};

// In-app confirmation (instead of the browser's "site says" box):
//   const { ask, dialog } = useCallingConfirm();
//   if (!(await ask({ title, body, confirmLabel, danger }))) return;
//   …and render {dialog} once in the component.
const useCallingConfirm = () => {
  const [req, setReq] = React.useState(null);
  const ask = React.useCallback((opts) => new Promise((resolve) => setReq({ ...opts, resolve })), []);
  const close = (ok) => { if (req) req.resolve(ok); setReq(null); };
  React.useEffect(() => {
    if (!req) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });   // eslint-disable-line react-hooks/exhaustive-deps
  const dialog = req ? (
    <div role="dialog" aria-modal="true" onMouseDown={(e) => { if (e.target === e.currentTarget) close(false); }}
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ width: 420, maxWidth: '100%', background: 'var(--surface)', color: 'var(--text)',
        border: '1px solid var(--border)', borderRadius: 10, boxShadow: 'var(--shadow-lg)', padding: '16px 18px' }}>
        <div style={{ fontSize: 14, fontWeight: 650, marginBottom: 6 }}>{req.title}</div>
        {req.body && <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.55, whiteSpace: 'pre-line' }}>{req.body}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <Btn size="sm" variant="outline" onClick={() => close(false)}>{req.cancelLabel || 'Cancel'}</Btn>
          <Btn size="sm" variant="accent" autoFocus onClick={() => close(true)}
            style={req.danger ? { background: 'var(--rose-fg)', borderColor: 'var(--rose-fg)', color: '#fff' } : undefined}>
            {req.confirmLabel || 'OK'}
          </Btn>
        </div>
      </div>
    </div>
  ) : null;
  return { ask, dialog };
};

// A batch that stopped before sending anything carries one general error.
const batchError = (b) => {
  const e = ((b && b.errors) || []).find((x) => !x.contact_id);
  return e ? e.error : null;
};

// Contacts a send didn't deliver. Dropdown misses are grouped by value so the
// missing options can be added in PhoneBurner; they stay on the queue.
const CallingBatchErrors = ({ batch }) => {
  const [open, setOpen] = React.useState(false);
  const rows = ((batch && batch.errors) || []).filter((e) => e.contact_id);
  if (!rows.length) return null;
  const misses = rows.filter((e) => e.rule === 'dropdown');
  const other = rows.filter((e) => e.rule !== 'dropdown');
  const groups = {};
  misses.forEach((e) => {
    const k = `${e.field}::${e.value == null ? '' : e.value}`;
    groups[k] = groups[k] || { field: e.field, value: e.value, n: 0 };
    groups[k].n += 1;
  });
  const byField = {};
  Object.values(groups).forEach((g) => { (byField[g.field] = byField[g.field] || []).push(g); });
  const copy = (vals) => {
    try {
      navigator.clipboard.writeText(vals.join('\n'));
      pushToast({ type: 'success', title: `Copied ${vals.length}`, body: 'Add them as options in PhoneBurner, then send these again.' });
    } catch (_) {}
  };
  return (
    <div style={{ marginTop: 6, padding: '8px 10px', borderRadius: 8, background: 'var(--surface-2)', fontSize: 12 }}>
      {Object.entries(byField).map(([field, gs]) => {
        const named = gs.filter((g) => g.value).sort((a, b) => b.n - a.n);
        const blank = gs.find((g) => !g.value);
        return (
          <div key={field} style={{ marginBottom: 4 }}>
            <span style={{ color: 'var(--rose-fg)', fontWeight: 600 }}>
              {gs.reduce((a, g) => a + g.n, 0)} not sent — not in PhoneBurner’s “{field}” dropdown:
            </span>{' '}
            {named.map((g) => `${g.value}${g.n > 1 ? ` ×${g.n}` : ''}`).join(', ')}
            {blank && <span style={{ color: 'var(--text-3)' }}>{named.length ? ' · ' : ''}{blank.n} with none in Atlas</span>}
            {named.length > 0 && (
              <Btn size="xs" variant="ghost" style={{ marginLeft: 6 }} onClick={() => copy(named.map((g) => g.value))}>Copy values</Btn>
            )}
          </div>
        );
      })}
      {other.length > 0 && (
        <div style={{ color: 'var(--rose-fg)', marginBottom: 4 }}>{other.length} failed in PhoneBurner</div>
      )}
      <div style={{ color: 'var(--text-3)' }}>
        They’re still marked to call — send them again once it’s fixed.
        <Btn size="xs" variant="ghost" style={{ marginLeft: 6 }} onClick={() => setOpen((o) => !o)}>{open ? 'Hide' : 'Show'} contacts</Btn>
      </div>
      {open && (
        <div style={{ marginTop: 4, maxHeight: 220, overflow: 'auto' }}>
          {rows.map((e) => (
            <div key={e.contact_id} style={{ display: 'flex', gap: 8, padding: '2px 0' }}>
              <a href={`#/people/${e.contact_id}`} style={{ color: 'var(--accent-text)', minWidth: 160 }}>{e.name || 'Contact'}</a>
              <span style={{ color: 'var(--text-3)' }}>{e.error}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ Caller note builder ============
// The note PhoneBurner shows the caller: blocks switched on/off and ordered,
// previewed live on a contact from the list. Saved for the whole team; it
// applies to contacts sent for the first time (app/dialer_note.py).
const CallingNoteBuilder = ({ open, onClose, rows, pickedIds }) => {
  const [cat, setCat] = React.useState(null);
  const [tpl, setTpl] = React.useState(null);
  const [contactId, setContactId] = React.useState('');
  const [pv, setPv] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => {
    if (!open) return;
    window.AtlasApi.dialerNoteTemplate()
      .then((r) => { setCat(r); setTpl(r.template); })
      .catch((e) => pushToast({ type: 'error', title: 'Could not load the caller note', body: e.message }));
  }, [open]);
  const sample = (rows || []).slice(0, 80);
  React.useEffect(() => {
    if (!open) return;
    if (contactId && sample.some((r) => r.contact_id === contactId)) return;
    const first = (pickedIds && pickedIds[0]) || (sample[0] && sample[0].contact_id) || '';
    setContactId(first);
  }, [open, sample.length]);   // eslint-disable-line react-hooks/exhaustive-deps
  const tplKey = JSON.stringify(tpl);
  React.useEffect(() => {
    if (!open || !tpl || !contactId) return undefined;
    const t = setTimeout(() => {
      window.AtlasApi.dialerNotePreview({ contact_id: contactId, template: tpl })
        .then(setPv).catch((e) => setPv({ error: e.message }));
    }, 400);
    return () => clearTimeout(t);
  }, [open, contactId, tplKey]);   // eslint-disable-line react-hooks/exhaustive-deps

  const info = {};
  ((cat && cat.blocks) || []).forEach((b) => { info[b.key] = b; });
  const setBlock = (i, patch) => setTpl((t) => ({ ...t, blocks: t.blocks.map((b, j) => (j === i ? { ...b, ...patch } : b)) }));
  const move = (i, d) => setTpl((t) => {
    const bl = [...t.blocks]; const j = i + d;
    if (j < 0 || j >= bl.length) return t;
    [bl[i], bl[j]] = [bl[j], bl[i]];
    return { ...t, blocks: bl };
  });
  const removeBlock = (i) => setTpl((t) => ({ ...t, blocks: t.blocks.filter((_, j) => j !== i) }));
  const addText = () => setTpl((t) => ({ ...t, blocks: [...t.blocks, { key: 'text', on: true, text: '' }] }));
  // Company facts: which bullets the note carries. The short ones can move
  // into PhoneBurner custom fields instead (Jake, 09-29).
  const factParts = (cat && cat.fact_parts) || [];
  const partsOf = (b) => (Array.isArray(b.parts) ? b.parts : factParts.map((p) => p.key));
  const togglePart = (i, key) => setTpl((t) => ({ ...t, blocks: t.blocks.map((b, j) => {
    if (j !== i) return b;
    const cur = partsOf(b);
    const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
    return { ...b, parts: factParts.map((p) => p.key).filter((k) => next.includes(k)) };
  }) }));
  const TO_FIELD = {
    years: { name: 'Years in operation', type_id: 7, source: 'years' },
    revenue: { name: 'Est. revenue', type_id: 1, source: 'est_revenue' },
    employees: { name: 'Est. employees', type_id: 1, source: 'employees' },
  };
  const [moving, setMoving] = React.useState('');
  const moveToField = async (i, key) => {
    const spec = TO_FIELD[key];
    if (!spec) return;
    setMoving(key);
    try {
      await window.AtlasApi.dialerNewCustomField(spec);
      pushToast({ type: 'success', title: `“${spec.name}” will be a PhoneBurner field`,
        body: 'PhoneBurner creates it with the next send, filled from Atlas. Save the note to drop the bullet.' });
      if (partsOf(tpl.blocks[i]).includes(key)) togglePart(i, key);
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not create the field', body: /already has/.test(e.message)
        ? `${e.message}. Map it in Setup → Custom fields, then untick the bullet here.` : e.message });
    } finally { setMoving(''); }
  };
  const dirty = !!(cat && tpl && JSON.stringify(tpl) !== JSON.stringify(cat.template));
  const save = async () => {
    setSaving(true);
    try {
      const r = await window.AtlasApi.dialerSaveNoteTemplate(tpl);
      setCat(r); setTpl(r.template);
      pushToast({ type: 'success', title: 'Caller note saved', body: 'Contacts sent from now on get this note.' });
    } catch (e) { pushToast({ type: 'error', title: 'Could not save', body: e.message }); }
    finally { setSaving(false); }
  };
  const { ask, dialog: confirmDialog } = useCallingConfirm();
  const close = async () => {
    if (dirty && !(await ask({ title: 'Discard your changes?', body: 'Your edits to the caller note haven’t been saved.',
      confirmLabel: 'Discard', danger: true }))) return;
    onClose();
  };
  const smallBtn = { background: 'transparent', border: '1px solid var(--border)', borderRadius: 5, cursor: 'pointer',
    color: 'var(--text-3)', padding: '1px 5px', fontSize: 11, lineHeight: '16px' };
  const maxChars = (tpl && tpl.max_chars) || 1800;
  return (
    <>
    {confirmDialog}
    <Drawer open={open} onClose={close} width={760} title="Caller note"
      subtitle="Applies to contacts sent for the first time."
      footer={(
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
          <Btn size="sm" variant="ghost" disabled={!cat} onClick={() => cat && setTpl(cat.default)}>Reset to default</Btn>
          <div style={{ flex: 1 }} />
          <Btn size="sm" variant="outline" onClick={close}>Close</Btn>
          <Btn size="sm" variant="accent" disabled={!dirty || saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</Btn>
        </div>
      )}>
      {!tpl ? <div style={{ padding: 16, fontSize: 12.5, color: 'var(--text-4)' }}>Loading…</div> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 14, padding: 14, fontSize: 12.5 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontWeight: 600, color: 'var(--text-2)' }}>Fields</div>
            {tpl.blocks.map((b, i) => {
              const meta = info[b.key] || { label: b.key };
              return (
                <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '6px 8px',
                  background: b.on ? 'var(--surface)' : 'var(--surface-2)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="checkbox" checked={!!b.on} onChange={(e) => setBlock(i, { on: e.target.checked })} />
                    <span title={meta.hint} style={{ flex: 1, color: b.on ? 'var(--text)' : 'var(--text-4)' }}>{meta.label}</span>
                    {meta.limit != null && (
                      <input type="number" min={1} max={meta.max_limit || 10} value={b.limit || meta.limit}
                        disabled={!b.on} title="How many"
                        onChange={(e) => setBlock(i, { limit: Math.max(1, Math.min(meta.max_limit || 10, Number(e.target.value) || 1)) })}
                        style={{ width: 44, fontSize: 11.5, padding: '1px 4px', border: '1px solid var(--border)', borderRadius: 5 }} />
                    )}
                    <button style={smallBtn} title="Move up" disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                    <button style={smallBtn} title="Move down" disabled={i === tpl.blocks.length - 1} onClick={() => move(i, 1)}>↓</button>
                    {b.key === 'text' && <button style={smallBtn} title="Remove" onClick={() => removeBlock(i)}>✕</button>}
                  </div>
                  {b.key === 'company_facts' && b.on && factParts.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 6, paddingLeft: 20 }}>
                      {factParts.map((p) => {
                        const on = partsOf(b).includes(p.key);
                        return (
                          <div key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5 }}>
                            <input type="checkbox" checked={on} onChange={() => togglePart(i, p.key)} />
                            <span style={{ flex: 1, color: on ? 'var(--text-2)' : 'var(--text-4)' }}>{p.label}</span>
                            {TO_FIELD[p.key] && (
                              <button style={{ ...smallBtn, fontSize: 10.5 }} disabled={!!moving}
                                title={`Add “${TO_FIELD[p.key].name}” as a PhoneBurner field filled from Atlas (created with the next send), and take the bullet out of the note`}
                                onClick={() => moveToField(i, p.key)}>
                                {moving === p.key ? 'Adding…' : 'Make it a PhoneBurner field'}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {b.key === 'text' && (
                    <>
                      <textarea value={b.text || ''} rows={3} maxLength={1000}
                        placeholder="e.g. Ask {first_name} if they’ve thought about selling. Book with {lead}: {calendly}"
                        onChange={(e) => setBlock(i, { text: e.target.value })}
                        style={{ width: '100%', marginTop: 6, fontSize: 12, fontFamily: 'inherit', padding: 6,
                          border: '1px solid var(--border)', borderRadius: 6, resize: 'vertical', boxSizing: 'border-box' }} />
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                        {((cat && cat.placeholders) || []).map((ph) => (
                          <button key={ph} style={{ ...smallBtn, fontSize: 10.5 }} title={`Insert {${ph}}`}
                            onClick={() => setBlock(i, { text: `${b.text || ''}{${ph}}` })}>{`{${ph}}`}</button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 2 }}>
              <Btn size="xs" variant="ghost" icon={<Icon.Plus size={11} />} onClick={addText}>Add custom text</Btn>
              <span style={{ flex: 1 }} />
              <label style={{ fontSize: 11.5, color: 'var(--text-3)', display: 'flex', gap: 4, alignItems: 'center' }}
                title="PhoneBurner notes are cut off after this many characters">
                Max
                <input type="number" min={300} max={4000} step={100} value={maxChars}
                  onChange={(e) => setTpl((t) => ({ ...t, max_chars: Number(e.target.value) || 1800 }))}
                  style={{ width: 64, fontSize: 11.5, padding: '1px 4px', border: '1px solid var(--border)', borderRadius: 5 }} />
                chars
              </label>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0, position: 'sticky', top: 0, alignSelf: 'start' }}>
            <div style={{ fontWeight: 600, color: 'var(--text-2)' }}>Preview</div>
            <window.Picker value={contactId} onChange={setContactId} width="100%" placeholder="Load a list to preview a contact"
              disabled={!sample.length} options={sample.map((r) => ({ value: r.contact_id, label: r.name || '—', hint: r.company }))} />
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit', fontSize: 12,
              lineHeight: 1.55, color: 'var(--text)', background: 'var(--surface-2)', borderRadius: 8, padding: 10,
              minHeight: 160, maxHeight: 520, overflow: 'auto' }}>
              {pv && pv.error ? <span style={{ color: 'var(--rose-fg)' }}>{pv.error}</span>
                : pv ? (pv.text || <span style={{ color: 'var(--text-4)' }}>Nothing to show for this contact with these fields.</span>)
                  : <span style={{ color: 'var(--text-4)' }}>{contactId ? 'Loading…' : 'Pick a contact'}</span>}
            </pre>
            {pv && !pv.error && (
              <span style={{ fontSize: 11, color: pv.chars >= (pv.max_chars || maxChars) ? 'var(--amber-fg)' : 'var(--text-4)' }}>
                {pv.chars.toLocaleString()} / {(pv.max_chars || maxChars).toLocaleString()} characters
              </span>
            )}
          </div>
        </div>
      )}
    </Drawer>
    </>
  );
};

const CallingSendTab = ({ status, onSeeSent }) => {
  const { ask: sendAsk, dialog: sendDialog } = useCallingConfirm();
  const [industries, setIndustries] = React.useState([]);
  const [industry, setIndustry] = React.useState('');
  const [statuses, setStatuses] = React.useState(['none', 'new_prospect']);
  const [channel, setChannel] = React.useState('');
  const [states, setStates] = React.useState([]);
  const [cities, setCities] = React.useState([]);
  const [q, setQ] = React.useState('');
  const [neverCalled, setNeverCalled] = React.useState(true);
  // The call queue: marked contacts, whatever their marketing status.
  const [queuedOnly, setQueuedOnly] = React.useState(() => !!(status && status.queue_count > 0));
  // Marked to call, one list at a time: an upload, by hand, named on a call (10-06).
  const [queueList, setQueueList] = React.useState('');
  const [queueLists, setQueueLists] = React.useState([]);
  const [queueCount, setQueueCount] = React.useState(null);
  const [briefs, setBriefs] = React.useState(null);
  const [importing, setImporting] = React.useState(false);
  // The last import's leftovers: people to add, rows that matched nothing (10-06).
  const [importResult, setImportResult] = React.useState(null);
  const [adding, setAdding] = React.useState(false);
  const fileRef = React.useRef(null);
  const [neverSent, setNeverSent] = React.useState(false);
  const [liveDeals, setLiveDeals] = React.useState(false);
  const [limit, setLimit] = React.useState(200);
  const [preview, setPreview] = React.useState(null);
  const [picked, setPicked] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [owner, setOwner] = React.useState('');
  // The PhoneBurner tag this send puts on its contacts (10-19: Jake's
  // "Miami event", so the caller can include / exclude them in a session).
  const [tag, setTag] = React.useState('');
  const [pbTags, setPbTags] = React.useState([]);
  // Where this send goes in PhoneBurner: '' = REC / NO REC – Attempt 1 by the
  // recording rule; or any folder PhoneBurner has (10-19: Jake files event
  // lists in their own folders so Andrew's sessions stay separate).
  const [dest, setDest] = React.useState('');
  const [pbFound, setPbFound] = React.useState(null);
  const [batch, setBatch] = React.useState(null);
  const [history, setHistory] = React.useState([]);
  const [noteOpen, setNoteOpen] = React.useState(false);
  const reqRef = React.useRef(0);   // only the newest list request may land

  React.useEffect(() => {
    window.AtlasApi.listIndustries().then((r) => setIndustries(Array.isArray(r) ? r : (r.items || []))).catch(() => {});
    window.AtlasApi.dialerBatches().then((r) => setHistory(r.items || [])).catch(() => {});
    window.AtlasApi.dialerTags().then((r) => setPbTags(r.tags || [])).catch(() => {});
    window.AtlasApi.dialerRoutingFolders().then((r) => setPbFound(r || null)).catch(() => {});
  }, []);
  const seats = ((status && status.members) || []).filter((m) => m.user_id);
  React.useEffect(() => {
    if (!owner && seats.length) {
      const caller = seats.find((m) => m.has_token && !/admin/i.test(m.name || '')) || seats[0];
      setOwner(caller.user_id);
    }
  }, [seats.length]);   // eslint-disable-line react-hooks/exhaustive-deps

  const sawStatus = React.useRef(!!status);
  React.useEffect(() => {
    window.AtlasApi.dialerBriefsStatus().then(setBriefs).catch(() => {});
  }, []);
  React.useEffect(() => {
    if (status && status.queue_count != null) setQueueCount(status.queue_count);
    // First status: open on the call queue when there is one.
    if (status && !sawStatus.current) {
      sawStatus.current = true;
      if (status.queue_count > 0) setQueuedOnly(true);
    }
  }, [status]);
  const runPreview = async (opts = {}) => {
    const queueMode = opts.queuedOnly != null ? opts.queuedOnly : queuedOnly;
    const mine = ++reqRef.current;
    setLoading(true);
    try {
      const body = queueMode ? {
        // The queue is the list: only the list picker, geography and search narrow it.
        queued_only: true,
        queue_source: (opts.queueList != null ? opts.queueList : queueList) || null,
        industry_ids: industry ? [industry] : null,
        states: states.length ? states : null, cities: cities.length ? cities : null,
        include_live_deals: liveDeals, q: q.trim() || null, limit: Math.max(limit, 1000),
      } : {
        statuses: statuses.length ? statuses : null,
        channel: channel || null,
        industry_ids: industry ? [industry] : null,
        states: states.length ? states : null, cities: cities.length ? cities : null,
        never_called: neverCalled, never_sent: neverSent, include_live_deals: liveDeals,
        q: q.trim() || null, limit,
      };
      const r = await window.AtlasApi.dialerCandidates(body);
      if (mine !== reqRef.current) return;   // a newer filter change is in flight
      setPreview(r);
      const sel = {};
      (r.items || []).forEach((i) => { if (i.eligible) sel[i.contact_id] = true; });
      setPicked(sel);
    } catch (e) {
      if (mine === reqRef.current) pushToast({ type: 'error', title: 'Could not load the list', body: e.message });
    } finally { if (mine === reqRef.current) setLoading(false); }
  };

  // Live filters: every change re-runs the list (debounced for typing).
  const filterKey = JSON.stringify([queuedOnly, queueList, industry, statuses, channel, states, cities, q, neverCalled, neverSent, liveDeals, limit]);
  // The list picker follows the queue: reload it whenever the count moves.
  React.useEffect(() => {
    window.AtlasApi.dialerQueueLists().then((r) => {
      const lists = r.lists || [];
      setQueueLists(lists);
      setQueueList((cur) => (cur && !lists.some((l) => l.source === cur) ? '' : cur));
    }).catch(() => {});
  }, [queueCount]);
  React.useEffect(() => {
    const t = setTimeout(() => runPreview(), 350);
    return () => clearTimeout(t);
  }, [filterKey]);   // eslint-disable-line react-hooks/exhaustive-deps

  const chosen = Object.keys(picked).filter((k) => picked[k]);
  // In the queue, a skipped row can still be picked — to take it off the
  // queue. Only eligible picks are ever sent.
  const eligibleIds = new Set(((preview && preview.items) || []).filter((i) => i.eligible).map((i) => i.contact_id));
  const sendable = chosen.filter((id) => eligibleIds.has(id));
  const setQueue = async (queued) => {
    const ids = chosen;
    if (!ids.length) return;
    try {
      const r = await window.AtlasApi.dialerQueue({ contact_ids: ids, queued });
      setQueueCount(r.queue_count);
      pushToast({ type: 'success', title: queued ? `Marked ${ids.length} to call` : `Removed ${r.changed} from the queue` });
      runPreview();
    } catch (e) { pushToast({ type: 'error', title: 'Could not update the call queue', body: e.message }); }
  };
  const importList = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const r = await window.AtlasApi.dialerImportList(file);
      setQueueCount(r.queue_count);
      pushToast({ type: 'success', title: `Marked ${r.queued} to call from ${file.name}`,
        body: `${r.matched} of ${r.rows} rows matched${r.phones_filled ? ` · ${r.phones_filled} phones filled in` : ''}${r.phones_corrected ? ` · ${r.phones_corrected} phones corrected from the list` : ''}${r.creatable ? ` · ${r.creatable} not in Atlas yet` : ''}${r.unmatched ? ` · ${r.unmatched} not found` : ''}`,
        duration: 8000 });
      setImportResult((r.creatable || r.unmatched) ? { ...r, file: file.name } : null);
      setQueuedOnly(true);
      setQueueList(`import:${file.name}`.slice(0, 200));
      runPreview({ queuedOnly: true, queueList: `import:${file.name}`.slice(0, 200) });
      // Refresh the prep counts (notes are prepared when these are sent).
      window.AtlasApi.dialerBriefsStatus().then(setBriefs).catch(() => {});
    } catch (e) {
      pushToast({ type: 'error', title: 'Import failed', body: e.message });
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };
  const addFromList = async () => {
    const rows = (importResult && importResult.creatable_rows) || [];
    if (!rows.length) return;
    if (!(await sendAsk({ title: `Add ${rows.length} new contact${rows.length === 1 ? '' : 's'}?`,
      body: `They’re added under their companies in Atlas with the name, phone and email from ${importResult.file}, then marked to call. Anyone already at the company by that name is marked instead of added twice.`,
      confirmLabel: 'Add and mark to call' }))) return;
    setAdding(true);
    try {
      const r = await window.AtlasApi.dialerCreateListContacts({ rows, source: `import:${importResult.file}` });
      setQueueCount(r.queue_count);
      pushToast({ type: 'success', title: `Added ${r.created} contact${r.created === 1 ? '' : 's'} and marked ${r.queued} to call`,
        body: [r.existing ? `${r.existing} were already in Atlas` : null, r.skipped ? `${r.skipped} skipped` : null].filter(Boolean).join(' · ') || undefined });
      setImportResult((cur) => cur && { ...cur, creatable: 0, creatable_rows: [], added: r.created });
      runPreview({ queuedOnly: true });
    } catch (e) { pushToast({ type: 'error', title: 'Could not add the contacts', body: e.message }); }
    finally { setAdding(false); }
  };
  const downloadLeftovers = () => {
    const ir = importResult || {};
    const rows = [
      ...(ir.creatable_rows || []).map((r) => ({ ...r, reason: 'Person not in Atlas (company is)' })),
      ...(ir.unmatched_rows || []),
    ].sort((a, b) => (a.row || 0) - (b.row || 0));
    window.exportCsv(`${(ir.file || 'call list').replace(/\.[a-z]+$/i, '')} – not marked.csv`, [
      { key: 'row', label: 'Row' }, { key: 'first_name', label: 'First name' }, { key: 'last_name', label: 'Last name' },
      { key: 'company', label: 'Company' }, { key: 'phone', label: 'Phone' }, { key: 'email', label: 'Email' },
      { key: 'reason', label: 'Why' }], rows);
  };
  const routing = (status && status.routing) || {};
  const chosenZones = { rec: 0, no_rec: 0 };
  ((preview && preview.items) || []).forEach((i) => { if (picked[i.contact_id] && i.eligible && i.zone) chosenZones[i.zone] += 1; });
  const send = async () => {
    if (!sendable.length) return;
    const d = parseCallDest(dest, pbFound);
    const lines = [
      d.mode === 'folder' ? `Folder: ${d.label}`
        : `Folders: ${d.label} — REC ${chosenZones.rec} · NO REC ${chosenZones.no_rec}`,
      // One folder for everyone drops the recording split: say so when it mixes them.
      d.mode === 'folder' && chosenZones.rec > 0 && chosenZones.no_rec > 0
        ? `${chosenZones.no_rec} of them can't be recorded (all-party consent) and will share this folder with ${chosenZones.rec} that can.`
        : null,
      tag ? `Tag: ${tag}` : 'No tag',
    ].filter(Boolean);
    if (!(await sendAsk({ title: `Send ${sendable.length} contact${sendable.length === 1 ? '' : 's'} to PhoneBurner?`,
      body: lines.join('\n'), confirmLabel: 'Send' }))) return;
    try {
      // No folder picked: the API routes each contact to REC or NO REC – Attempt 1.
      const b = await window.AtlasApi.dialerSend({ contact_ids: sendable,
        owner_id: owner || null, include_live_deals: liveDeals, tag: tag || null, ...callDestBody(d) });
      setBatch(b);
    } catch (e) { pushToast({ type: 'error', title: 'Send failed', body: e.message }); }
  };
  React.useEffect(() => {
    if (!batch || !(batch.status === 'queued' || batch.status === 'running')) return undefined;
    const t = setTimeout(() => {
      window.AtlasApi.dialerBatch(batch.id).then((b) => {
        setBatch({ ...b });   // always a new object, so the next poll is armed
        if (b.status === 'done' || b.status === 'error') {
          window.AtlasApi.dialerBatches().then((r) => setHistory(r.items || [])).catch(() => {});
          const why = b.status === 'error' ? batchError(b) : null;
          pushToast({ type: b.status === 'done' && !b.failed ? 'success' : 'error',
            title: b.status === 'done' ? `Sent ${b.sent} to PhoneBurner${b.failed ? ` · ${b.failed} not sent` : ''}` : 'Send stopped',
            body: why || (b.failed ? 'See the list under the Send button.' : `${b.skipped} skipped`),
            duration: why || b.failed ? 12000 : undefined });
        }
      }).catch(() => setBatch((cur) => (cur ? { ...cur } : cur)));   // a failed poll tries again
    }, 1200);
    return () => clearTimeout(t);
  }, [batch]);

  const toggleStatus = (k) => setStatuses((cur) => (cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]));
  const skipLabels = (status && status.skip_reasons) || {};
  const connected = status && status.connected;
  const blocked = status && status.send_blocked;
  return (
    <>
      {sendDialog}
      {blocked && (
        <div style={{ marginTop: 12, padding: '8px 12px', borderRadius: 8, fontSize: 12.5,
          background: 'var(--amber-bg, var(--surface-2))', color: 'var(--amber-fg)' }}>{blocked}</div>
      )}
      <div style={{ ...callBox, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 2, padding: 2, background: 'var(--surface-2)', borderRadius: 8 }}>
            <button style={callTabBtn(queuedOnly)} onClick={() => setQueuedOnly(true)}
              title="Contacts someone marked to call, not sent to PhoneBurner since">
              Marked to call{queueCount ? ` ${queueCount.toLocaleString()}` : ''}
            </button>
            <button style={callTabBtn(!queuedOnly)} onClick={() => setQueuedOnly(false)}>Build a list</button>
          </div>
          <input ref={fileRef} type="file" accept=".xlsx,.csv" style={{ display: 'none' }}
            onChange={(e) => importList(e.target.files && e.target.files[0])} />
          <Btn size="sm" variant="outline" disabled={importing} onClick={() => fileRef.current && fileRef.current.click()}
            title="An .xlsx or .csv with a DC ID, email or phone column — every match is marked to call">
            {importing ? 'Importing…' : 'Import call list'}
          </Btn>
          <Btn size="sm" variant="outline" icon={<Icon.Edit size={12} />} onClick={() => setNoteOpen(true)}
            title="Choose what goes into the caller’s PhoneBurner notes">
            Caller note
          </Btn>
          <span style={{ flex: 1 }} />
        </div>
        {queuedOnly && <CallingBriefsBar initial={briefs} queueCount={queueCount} />}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {queuedOnly && queueLists.length > 0 && (
            <window.Picker value={queueList} onChange={setQueueList} minWidth={220} menuMinWidth={320}
              title="Where these contacts were marked to call: an uploaded list, by hand, or named on a call"
              emptyLabel={`All marked (${(queueCount || 0).toLocaleString()})`}
              options={queueLists.map((l) => ({ value: l.source, label: l.label, count: l.count,
                hint: l.marked_at ? new Date(l.marked_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : undefined }))} />
          )}
          <window.Picker value={industry} onChange={setIndustry} emptyLabel="All industries" minWidth={170} menuMinWidth={260}
            options={industries.map((i) => ({ value: i.id, label: i.name }))} />
          <CallingPlacePickers states={states} setStates={setStates} cities={cities} setCities={setCities} />
          <window.Picker value={channel} onChange={setChannel} emptyLabel="Any channel" width={160}
            options={[{ value: 'call', label: 'Cold-call channel' }, { value: 'email', label: 'Email channel' }]} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, company or email…" style={{ ...callSel, minWidth: 190 }} />
          <window.Picker value={limit} onChange={setLimit} options={CALL_LIMIT_OPTIONS} searchable={false} width={124} />
        </div>
        {!queuedOnly && <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', fontSize: 12 }}>
          <span style={{ color: 'var(--text-3)' }}>Marketing status:</span>
          {CALL_STATUS_OPTIONS.map(([k, l]) => (
            <button key={k} onClick={() => toggleStatus(k)}
              style={{ ...callTabBtn(statuses.includes(k)), fontSize: 11.5, padding: '3px 8px',
                borderColor: statuses.includes(k) ? 'var(--border-strong)' : 'var(--border)' }}>{l}</button>
          ))}
        </div>}
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', fontSize: 12, color: 'var(--text-2)' }}>
          {!queuedOnly && <label style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
            <input type="checkbox" checked={neverCalled} onChange={(e) => setNeverCalled(e.target.checked)} /> Never called
          </label>}
          {!queuedOnly && <label style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
            <input type="checkbox" checked={neverSent} onChange={(e) => setNeverSent(e.target.checked)} /> Not in PhoneBurner yet
          </label>}
          <label style={{ display: 'flex', gap: 5, alignItems: 'center' }} title="Companies with a deal at D/S call or later belong to a dealmaker; they are skipped unless you include them">
            <input type="checkbox" checked={liveDeals} onChange={(e) => setLiveDeals(e.target.checked)} /> Include companies with a live deal
          </label>
          <span style={{ flex: 1 }} />
          {loading && <span style={{ fontSize: 12, color: 'var(--text-4)' }}>Updating…</span>}
          <Btn size="sm" variant="ghost" icon={<Icon.Refresh size={12} />} disabled={loading} onClick={() => runPreview()}
            title="Load the list again">Refresh</Btn>
        </div>
      </div>

      {importResult && (
        <div style={{ marginTop: 12, padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 8,
          background: 'var(--surface-2)', fontSize: 12.5, display: 'grid', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <b style={{ color: 'var(--text)' }}>{importResult.file}</b>
            <span style={{ color: 'var(--text-3)' }}>
              {importResult.matched} of {importResult.rows} rows matched and marked to call
              {importResult.added ? ` · ${importResult.added} new contacts added` : ''}
            </span>
            <span style={{ flex: 1 }} />
            {((importResult.creatable_rows || []).length + (importResult.unmatched_rows || []).length) > 0 && (
              <Btn size="xs" variant="ghost" onClick={downloadLeftovers}>Download the rest (.csv)</Btn>
            )}
            <Btn size="xs" variant="ghost" onClick={() => setImportResult(null)}>Dismiss</Btn>
          </div>
          {importResult.creatable > 0 && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--text-2)' }}>
                <b>{importResult.creatable}</b> {importResult.creatable === 1 ? 'person isn’t' : 'people aren’t'} in Atlas yet, but {importResult.creatable === 1 ? 'their company is' : 'their companies are'}.
              </span>
              <Btn size="xs" variant="accent" disabled={adding} onClick={addFromList}>
                {adding ? 'Adding…' : `Add ${importResult.creatable} and mark to call`}
              </Btn>
            </div>
          )}
          {importResult.unmatched > 0 && (
            <div style={{ color: 'var(--text-3)' }}>
              <b>{importResult.unmatched}</b> row{importResult.unmatched === 1 ? '' : 's'} matched nothing — the company isn’t in Atlas (or the row has no name). They’re in the download.
            </div>
          )}
        </div>
      )}

      {preview && (
        <>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 14, flexWrap: 'wrap', fontSize: 12.5 }}>
            <span style={{ color: 'var(--text)' }}><b>{chosen.length}</b> selected of {preview.eligible} eligible</span>
            {chosen.length > 0 && (queuedOnly
              ? <Btn size="xs" variant="ghost" onClick={() => setQueue(false)}>Remove from queue</Btn>
              : <Btn size="xs" variant="ghost" onClick={() => setQueue(true)} title="Save these for later on the call queue">Mark to call</Btn>)}
            {Object.entries(preview.skipped || {}).map(([k, n]) => (
              <Pill key={k} hue="slate" sz="sm">{n} · {skipLabels[k] || k}</Pill>
            ))}
            {preview.limit_reached && <span style={{ color: 'var(--text-4)' }}>(hit the limit — narrow the filters or raise it)</span>}
            <span style={{ flex: 1 }} />
            {!String(dest).startsWith('folder:') && chosenZones.rec + chosenZones.no_rec > 0 && (
              <span style={{ fontSize: 12, color: 'var(--text-2)' }}
                title={`${routing.rec_folder ? routing.rec_folder.name : 'REC – Attempt 1'} / ${routing.no_rec_folder ? routing.no_rec_folder.name : 'NO REC – Attempt 1'}`}>
                → <Pill hue="green" sz="sm">REC {chosenZones.rec}</Pill> <Pill hue="amber" sz="sm">NO REC {chosenZones.no_rec}</Pill>
              </span>
            )}
            <window.Picker value={owner} onChange={setOwner} minWidth={170} title="The PhoneBurner seat that owns these contacts"
              placeholder="Caller (connect first)" disabled={!seats.length}
              options={seats.map((m) => ({ value: m.user_id, label: m.name || m.email || m.user_id }))} />
            <window.Picker value={dest} onChange={setDest} emptyLabel="REC / NO REC – Attempt 1" width={240} menuMinWidth={340}
              title="Where these contacts go in PhoneBurner" searchable options={callDestOptions(pbFound)} />
            <window.Picker value={tag} onChange={setTag} emptyLabel="No tag" width={150} menuMinWidth={240}
              title="The PhoneBurner tag these contacts get" creatable createLabel={(q) => `New tag “${q}”`}
              options={pbTags.map((t) => ({ value: t.title, label: t.title }))} />
            <Btn size="sm" variant="accent" icon={<Icon.Phone size={11} />}
              disabled={!connected || blocked || !sendable.length || (batch && (batch.status === 'queued' || batch.status === 'running'))}
              onClick={send}>
              Send {sendable.length} to PhoneBurner
            </Btn>
          </div>
          {batch && (
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-2)' }}>
              {batch.status === 'queued' || batch.status === 'running'
                ? `${batch.prep && batch.prep.phase === 'preparing' ? 'Preparing notes… ' : 'Sending… '}${batch.sent} sent · ${batch.skipped} skipped of ${batch.requested}`
                : `${batch.status === 'done' ? 'Done' : 'Stopped'}: ${batch.sent} sent · ${batch.skipped} skipped · ${batch.failed} failed`}
              {batch.prep && batch.prep.companies > 0 && (
                <span style={{ color: 'var(--text-4)' }}>
                  {' '}· notes for {batch.prep.companies} compan{batch.prep.companies === 1 ? 'y' : 'ies'}
                </span>
              )}
              {batch.status === 'error' && batchError(batch) && (
                <div style={{ marginTop: 4, color: 'var(--rose-fg)' }}>{batchError(batch)}</div>
              )}
              {batch.status !== 'queued' && batch.status !== 'running' && <CallingBatchErrors batch={batch} />}
              {batch.status === 'done' && batch.sent > 0 && onSeeSent && (
                <Btn size="xs" variant="ghost" onClick={onSeeSent} style={{ marginLeft: 8 }}>See them in Sent →</Btn>
              )}
            </div>
          )}
          <div style={{ ...callBox, maxHeight: 520, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-3)', position: 'sticky', top: 0, background: 'var(--surface)' }}>
                  <th style={{ padding: '7px 10px', width: 28 }}>
                    <input type="checkbox"
                      checked={chosen.length > 0 && chosen.length === preview.eligible}
                      onChange={(e) => {
                        const sel = {};
                        if (e.target.checked) (preview.items || []).forEach((i) => { if (i.eligible) sel[i.contact_id] = true; });
                        setPicked(sel);
                      }} />
                  </th>
                  {['Contact', 'Company', 'Industry', 'Phone', 'Recording', 'Status', 'Last call', ''].map((h) => (
                    <th key={h} style={{ padding: '7px 8px', fontWeight: 600, borderBottom: '1px solid var(--border)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(preview.items || []).map((i) => (
                  <tr key={i.contact_id} style={{ borderBottom: '1px solid var(--border)', opacity: i.eligible ? 1 : 0.55 }}>
                    <td style={{ padding: '6px 10px' }}>
                      <input type="checkbox" disabled={!i.eligible && !queuedOnly} checked={!!picked[i.contact_id]}
                        onChange={(e) => setPicked((p) => ({ ...p, [i.contact_id]: e.target.checked }))} />
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      <RecLink kind="contact" id={i.contact_id} style={{ color: 'var(--text)', fontWeight: 550 }}>{i.name || '—'}</RecLink>
                      {i.title && <div style={{ fontSize: 11, color: 'var(--text-4)' }}>{i.title}</div>}
                    </td>
                    <td style={{ padding: '6px 8px', color: 'var(--text-2)' }}>{i.company || '—'}{i.state ? <span style={{ color: 'var(--text-4)' }}> · {i.state}</span> : null}</td>
                    <td style={{ padding: '6px 8px', color: 'var(--text-3)' }}>{i.industry || '—'}</td>
                    <td style={{ padding: '6px 8px', color: 'var(--text-2)' }} className="num">{i.phone || '—'}</td>
                    <td style={{ padding: '6px 8px' }} title={i.zone_reason || ''}>
                      {i.zone === 'rec' ? <Pill hue="green" sz="sm">REC</Pill> : i.zone === 'no_rec' ? <Pill hue="amber" sz="sm">NO REC</Pill> : '—'}
                    </td>
                    <td style={{ padding: '6px 8px', color: 'var(--text-3)' }}>
                      {(i.marketing_status || '').replace(/_/g, ' ') || '—'}
                      {i.queued && !queuedOnly && <span style={{ marginLeft: 6 }}><Pill hue="blue" sz="sm">Queued</Pill></span>}
                    </td>
                    <td style={{ padding: '6px 8px', color: 'var(--text-3)' }}>
                      {i.last_called_at ? `${callWhen(i.last_called_at)} · ${CALL_OUTCOME_LABEL[i.last_outcome] || i.last_outcome || ''}` : (i.sent_at ? 'In PhoneBurner' : '—')}
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      {!i.eligible && <span style={{ fontSize: 11, color: 'var(--amber-fg)' }}>{i.skip_detail}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!(preview.items || []).length && (
              <div style={{ padding: 18, fontSize: 12.5, color: 'var(--text-3)' }}>Nobody matches these filters.</div>
            )}
          </div>
        </>
      )}

      {history.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-3)', marginBottom: 6 }}>Recent sends</div>
          <div style={{ ...callBox, marginTop: 0 }}>
            {history.slice(0, 10).map((b) => (
              <div key={b.id} style={{ padding: '7px 14px', borderBottom: '1px solid var(--border)', fontSize: 12, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--text)' }}>{b.folder_name || 'No folder'}</span>
                <span style={{ color: 'var(--text-3)' }}>{b.sent} sent · {b.skipped} skipped{b.failed ? ` · ${b.failed} failed` : ''}</span>
                <span style={{ color: 'var(--text-4)' }}>{callWhen(b.created_at)}{b.created_by ? ` · ${b.created_by}` : ''}</span>
                {b.tag && <Pill hue="blue" sz="sm">{b.tag}</Pill>}
                {b.status !== 'done' && <Pill hue={b.status === 'error' ? 'rose' : 'amber'} sz="sm">{b.status}</Pill>}
              </div>
            ))}
          </div>
        </div>
      )}
      <CallingNoteBuilder open={noteOpen} onClose={() => setNoteOpen(false)}
        rows={(preview && preview.items) || []} pickedIds={chosen} />
    </>
  );
};

// Calling → Email campaigns (backend app/dialer_campaigns.py): one list goes
// into two SuperSend campaigns by who filters the contact's mail
// (app/email_mx.py) — spam-filtered inboxes to one, everyone else to the
// other. Emailed contacts stay callable; a reply moves them out of the dialer.
const EMAIL_PROVIDER_HUE = { google: 'green', microsoft: 'green', other: 'green', personal: 'slate', gateway: 'amber', none: 'rose' };
const EMAIL_BUCKETS = [['clean', 'Clean inbox'], ['filtered', 'Spam filter'], ['none', 'No mail server'], ['unknown', 'Not checked']];
const EmailProviderPill = ({ provider, gateway, labels }) => {
  if (!provider) return <Pill hue="slate" sz="sm">Not checked</Pill>;
  const label = provider === 'gateway' && gateway ? gateway : ((labels || {})[provider] || provider);
  return <Pill hue={EMAIL_PROVIDER_HUE[provider] || 'slate'} sz="sm">{label}</Pill>;
};

// The same-trade check behind a comp or a nearby-sale opener (backend
// app/email_trade_check.py): the verdict, why, and the words it rests on.
const EMAIL_VERDICT = { same: 'Same trade', different: 'Different trade', unsure: 'Not sure', failed: 'Not checked' };
const EmailTradeCheck = ({ c }) => !c ? null : (
  <div style={{ display: 'grid', gap: 2 }}>
    <span><b style={{ color: c.verdict === 'same' ? 'var(--green-fg)' : 'var(--amber-fg)' }}>{EMAIL_VERDICT[c.verdict] || c.verdict}</b>
      {c.trade ? ` · ${c.trade}` : ''}{c.reason ? ` — ${c.reason}` : ''}</span>
    {c.company_quote && <span>This company: “{c.company_quote}”</span>}
    {c.target_quote && <span>The sold business: “{c.target_quote}”</span>}
    {c.model && <span style={{ color: 'var(--text-4)' }}>{c.model}{c.checked_at ? ` · ${callWhen(c.checked_at)}` : ''}</span>}
  </div>
);

// A pill that opens where a merge field's value came from (the
// show-your-work rule: a tooltip isn't enough).
const EmailSourcePill = ({ label, children }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <button onClick={() => setOpen((o) => !o)} aria-expanded={open}
        style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', font: 'inherit' }}>
        <Pill hue={open ? 'blue' : 'slate'} sz="sm">{label} {open ? '▴' : '▾'}</Pill>
      </button>
      {open && (
        <div style={{ display: 'grid', gap: 4, padding: '6px 8px', borderRadius: 6, background: 'var(--surface-2)',
          color: 'var(--text-3)', fontSize: 11, lineHeight: 1.4, maxWidth: 360 }}>{children}</div>
      )}
    </>
  );
};

// {{opener}} for one contact and the ONE stored fact it rests on (backend
// app/email_openers.py). No fact = empty field, the template's own line runs.
const EmailOpenerCell = ({ o }) => {
  if (!o || o.status === 'no_fact') return <span style={{ color: 'var(--text-4)' }} title="No usable fact: the field stays empty">—</span>;
  return (
    <div style={{ fontSize: 11.5, lineHeight: 1.35, display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
      {o.status === 'written' && <span style={{ color: 'var(--text-2)' }}>{o.text}</span>}
      {o.status === 'pending' && <span style={{ color: 'var(--text-4)' }}>Written on push</span>}
      {(o.status === 'rejected' || o.status === 'failed') && <span style={{ color: 'var(--amber-fg)' }}>Not used: {o.reject_reason}</span>}
      <EmailSourcePill label={o.label || o.kind}>
        {o.source && <span>{o.source}</span>}
        {o.quote && <span style={{ color: 'var(--text-2)' }}>{o.quote}</span>}
        {o.rests_on && <span>Rests on: {o.rests_on}</span>}
        {o.kind === 'nearby_sale' && (o.trade_check ? <EmailTradeCheck c={o.trade_check} /> : <span>Trade checked on push</span>)}
      </EmailSourcePill>
    </div>
  );
};

// {{multiple_range}} / {{local_comp}} for one contact and the stored rows it
// came from (backend app/email_fields.py). Blank = the template's own line runs.
const EMAIL_FIELD_LABEL = { multiple_range: 'Market range', local_comp: 'Local comp' };
const EmailFieldCell = ({ name, f }) => {
  if (!f) return null;
  const src = f.source || {};
  const passed = (f.passed || []).filter((x) => x.why);
  return (
    <div style={{ fontSize: 11.5, lineHeight: 1.35, display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
      {f.status === 'written' && <span style={{ color: 'var(--text-2)' }}>{f.value}</span>}
      {f.status === 'pending' && <span style={{ color: 'var(--text-4)' }}>Trade checked on push</span>}
      {(f.status === 'no_fact' || f.status === 'rejected') && <span style={{ color: 'var(--text-4)' }}>— {f.reason}</span>}
      {(f.source || passed.length > 0) && (
        <EmailSourcePill label={EMAIL_FIELD_LABEL[name] || name}>
          {src.source && <span>{src.source}{src.distance_mi != null ? ` · ${src.distance_mi} mi` : ''}</span>}
          {src.quote && <span style={{ color: 'var(--text-2)' }}>{src.quote}</span>}
          {src.url && <a href={src.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-text)' }}>Press</a>}
          {src.check && <EmailTradeCheck c={src.check} />}
          {passed.map((x, k) => (
            <div key={k} style={{ display: 'grid', gap: 2, paddingTop: 4, borderTop: '1px solid var(--border)' }}>
              <span>Passed over{x.sale ? ` ${x.sale}` : ''}{x.distance_mi != null ? ` · ${x.distance_mi} mi` : ''}{x.check ? '' : `: ${x.why}`}</span>
              {x.check && <EmailTradeCheck c={x.check} />}
            </div>
          ))}
          {f.passed_more > 0 && <span>+{f.passed_more} more out of reach</span>}
        </EmailSourcePill>
      )}
    </div>
  );
};

// The copy rules the pre-push check holds for (backend email_gate
// DEFAULT_COPY_RULES / app_settings[email.copy_rules]), one phrase a line.
const EMAIL_RULES = [['buyer_bait', 'Buyer-bait'], ['customer_bait', 'Customer-bait subjects'],
  ['big_ask', 'Asks for a call'], ['follow_up', 'Follow-ups, no prior touch']];
const EmailCopyRules = ({ onClose, onSaved }) => {
  const [draft, setDraft] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => {
    window.AtlasApi.dialerEmailCopyRules()
      .then((r) => setDraft(Object.fromEntries(EMAIL_RULES.map(([k]) => [k, ((r.rules || {})[k] || []).join('\n')]))))
      .catch((e) => { pushToast({ type: 'error', title: 'Could not load the copy rules', body: e.message }); onClose(); });
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    setSaving(true);
    try {
      await window.AtlasApi.dialerEmailSaveCopyRules(Object.fromEntries(EMAIL_RULES.map(([k]) =>
        [k, draft[k].split('\n').map((x) => x.trim()).filter(Boolean)])));
      pushToast({ type: 'success', title: 'Copy rules saved' });
      onSaved();
      onClose();
    } catch (e) { pushToast({ type: 'error', title: 'Could not save the copy rules', body: e.message }); }
    finally { setSaving(false); }
  };
  if (!draft) return <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-4)' }}>Loading…</div>;
  return (
    <div style={{ ...callBox, padding: 12, display: 'grid', gap: 10 }}>
      <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
        {EMAIL_RULES.map(([k, label]) => (
          <label key={k} style={{ display: 'grid', gap: 4, fontSize: 12, color: 'var(--text-3)' }}>
            {label}
            <textarea value={draft[k]} rows={6} onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
              style={{ ...callSel, width: '100%', minHeight: 110, fontFamily: 'inherit', fontSize: 12, resize: 'vertical' }} />
          </label>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Btn size="sm" variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn size="sm" variant="accent" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</Btn>
      </div>
    </div>
  );
};

// The pre-push check (backend app/email_gate.py): a reason holds the contact.
const EmailCheckCell = ({ i }) => {
  if (!i.eligible) return <span style={{ fontSize: 11, color: 'var(--amber-fg)' }}>{i.skip_detail}</span>;
  const c = i.check;
  if (!c) return null;
  return (
    <div style={{ fontSize: 11, lineHeight: 1.35 }}>
      {!c.ok && <div style={{ color: 'var(--rose-fg)' }}>Held · {c.reasons.join(' · ')}</div>}
      {(c.flags || []).length > 0 && <div style={{ color: 'var(--text-4)' }}>{c.flags.join(' · ')}</div>}
    </div>
  );
};

// A campaign set to "send to PhoneBurner" once emailed (backend
// dialer_campaigns._auto_send): how long after the first email, where, which
// caller seat, and an optional tag.
const CALL_DELAY_LABEL = { 0: 'Right away', 4: '4 hours after', 24: '1 day after', 48: '2 days after', 72: '3 days after' };
const CallingAutoSendRow = ({ c, seats, found, tags, delays, onPatch, colSpan }) => {
  const s = c.send || {};
  const d = s.dest || { mode: 'routing' };
  const pickDest = (v) => {
    const p = parseCallDest(v, found);
    onPatch({ dest_mode: p.mode, folder_id: p.folder_id || null, rec_folder_id: p.rec_folder_id || null,
      no_rec_folder_id: p.no_rec_folder_id || null, label: p.mode === 'routing' ? null : p.label });
  };
  const dests = callDestOptions(found);
  const destValue = callDestValue(d);
  const destOpts = destValue && !dests.some((o) => o.value === destValue)
    ? [{ value: destValue, label: d.label || destValue }, ...dests] : dests;
  return (
    <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface-2)' }}>
      <td colSpan={colSpan} style={{ padding: '7px 10px' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12 }}>
          <span style={{ color: 'var(--text-3)' }}>Send to PhoneBurner</span>
          <window.Picker value={s.delay_hours != null ? s.delay_hours : 24} size="sm" width={130} searchable={false}
            onChange={(v) => onPatch({ delay_hours: Number(v) })}
            options={(delays.length ? delays : [0, 4, 24, 48, 72]).map((h) => ({ value: h, label: CALL_DELAY_LABEL[h] || `${h} hours after` }))} />
          <span style={{ color: 'var(--text-3)' }}>the first email, into</span>
          <window.Picker value={destValue} onChange={pickDest} emptyLabel="REC / NO REC – Attempt 1" size="sm" width={250} menuMinWidth={340}
            searchable options={destOpts} />
          <window.Picker value={s.owner_id || ''} onChange={(v) => onPatch({ owner_id: v || null })} size="sm" width={150}
            placeholder="Caller seat" title="The PhoneBurner seat that owns these contacts"
            options={seats.map((m) => ({ value: m.user_id, label: m.name || m.email || m.user_id }))} />
          <window.Picker value={s.tag || ''} onChange={(v) => onPatch({ tag: v || null })} emptyLabel="No tag" size="sm" width={140}
            menuMinWidth={240} creatable createLabel={(q) => `New tag “${q}”`}
            options={(tags || []).map((t) => ({ value: t.title, label: t.title }))} />
          {!s.owner_id && <Pill hue="amber" sz="sm">Pick a caller seat</Pill>}
          {d.mode === 'folder' && <Pill hue="amber" sz="sm" title="Recordable and no-record contacts share one folder">No REC / NO REC split</Pill>}
          {c.auto_sent > 0 && <span style={{ color: 'var(--text-4)' }}>{c.auto_sent.toLocaleString()} sent so far</span>}
        </div>
      </td>
    </tr>
  );
};

// A campaign's Up next (backend dialer_campaigns.up_next): everyone it has
// emailed, when each goes to PhoneBurner (first email + the campaign's
// delay), or why not.
const UP_NEXT_LABEL = { due: 'Next pass (within 10 min)', sending: 'Sending now', marked: 'Marked to call',
  pending: 'Joins the list within 10 min', off: 'Not sent (When emailed: nothing)', in_pb: 'In PhoneBurner' };
const CallingUpNextPanel = ({ data, colSpan, onClose }) => {
  if (!data) return null;
  const s = data.summary || {};
  const send = data.when_emailed === 'send';
  const notEmailed = Math.max(0, (s.members || 0) - (s.emailed || 0));
  const reasons = Object.entries(s.reasons || {}).map(([k, n]) => `${n} ${k.toLowerCase()}`).join(', ');
  const bits = send
    ? [`${s.due_24h || 0} due in the next 24 h`, s.waiting ? `${s.waiting} waiting` : null, s.due ? `${s.due} going now` : null,
      s.blocked ? `${s.blocked} can't go (${reasons})` : null, s.in_pb ? `${s.in_pb} already in PhoneBurner` : null,
      notEmailed ? `${notEmailed} not emailed yet` : null]
    : [s.marked ? `${s.marked} marked to call` : null, s.pending ? `${s.pending} joining the list` : null,
      s.blocked ? `${s.blocked} can't go (${reasons})` : null, s.in_pb ? `${s.in_pb} in PhoneBurner` : null,
      notEmailed ? `${notEmailed} not emailed yet` : null];
  const goes = (i) => (i.status === 'waiting' ? callWhen(i.due_at)
    : i.status === 'pending' && send && i.due_at ? callWhen(i.due_at)
    : i.status === 'blocked' ? i.reason : UP_NEXT_LABEL[i.status] || i.status);
  return (
    <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface-2)' }}>
      <td colSpan={colSpan} style={{ padding: '8px 10px' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 12, marginBottom: 6 }}>
          <b style={{ color: 'var(--text)' }}>Up next</b>
          <span style={{ color: 'var(--text-2)' }}>{bits.filter(Boolean).join(' · ') || 'Nobody emailed yet'}</span>
          {send && !data.has_seat && <Pill hue="amber" sz="sm">Pick a caller seat — nothing goes until then</Pill>}
          {!send && data.when_emailed === 'mark' && <span style={{ color: 'var(--text-4)' }}>sent from Send contacts → Marked to call</span>}
          <span style={{ flex: 1 }} />
          <Btn size="xs" variant="ghost" onClick={onClose}>Close</Btn>
        </div>
        {(data.items || []).length > 0 && (
          <div style={{ maxHeight: 320, overflow: 'auto', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-3)', position: 'sticky', top: 0, background: 'var(--surface)' }}>
                  {['Contact', 'Company', 'Emailed', send ? 'Goes to PhoneBurner' : 'Status'].map((h) => (
                    <th key={h} style={{ padding: '6px 8px', fontWeight: 600, borderBottom: '1px solid var(--border)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.items.map((i) => (
                  <tr key={i.contact_id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '5px 8px' }}>
                      <RecLink kind="contact" id={i.contact_id} style={{ color: 'var(--text)', fontWeight: 550 }}>{i.name || '—'}</RecLink>
                    </td>
                    <td style={{ padding: '5px 8px', color: 'var(--text-2)' }}>{i.company || '—'}</td>
                    <td style={{ padding: '5px 8px', color: 'var(--text-3)' }}>{callWhen(i.emailed_at)}</td>
                    <td style={{ padding: '5px 8px', color: i.status === 'blocked' ? 'var(--amber-fg)' : i.status === 'due' ? 'var(--accent-text)' : 'var(--text-2)' }}>
                      {goes(i)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data.limit_reached && <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-4)' }}>Showing the first {data.items.length}.</div>}
      </td>
    </tr>
  );
};

const CallingEmailCampaignsTab = ({ status }) => {
  const { ask, dialog } = useCallingConfirm();
  const isAdmin = !!(status && status.is_admin);
  const [board, setBoard] = React.useState(null);
  const [clean, setClean] = React.useState('');
  const [filtered, setFiltered] = React.useState('');
  const [industries, setIndustries] = React.useState([]);
  const [industry, setIndustry] = React.useState('');
  const [statuses, setStatuses] = React.useState(['none', 'new_prospect']);
  const [states, setStates] = React.useState([]);
  const [cities, setCities] = React.useState([]);
  const [q, setQ] = React.useState('');
  const [buckets, setBuckets] = React.useState([]);
  const [queuedOnly, setQueuedOnly] = React.useState(false);
  const [queueList, setQueueList] = React.useState('');
  const [queueLists, setQueueLists] = React.useState([]);
  const [emailed, setEmailed] = React.useState('');
  // Uploaded lists (backend app/contact_lists.py): the last upload's result
  // carries the people Atlas doesn't have yet.
  const [uploads, setUploads] = React.useState(false);
  const [listId, setListId] = React.useState('');
  const [lists, setLists] = React.useState([]);
  const [upload, setUpload] = React.useState(null);
  const [importing, setImporting] = React.useState(false);
  const [addingList, setAddingList] = React.useState(null);   // {done, total}
  const listFileRef = React.useRef(null);
  const [liveDeals, setLiveDeals] = React.useState(false);
  const [limit, setLimit] = React.useState(200);
  const [preview, setPreview] = React.useState(null);
  const [picked, setPicked] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [pushing, setPushing] = React.useState(null);   // {done, total, added, already, skipped, held, failed}
  const [writing, setWriting] = React.useState(null);   // {done, total}
  const [heldOpen, setHeldOpen] = React.useState(null); // {campaign_id, items}
  const [upNext, setUpNext] = React.useState(null);     // {campaign_id, data}
  const [miles, setMiles] = React.useState('');
  const [rulesOpen, setRulesOpen] = React.useState(false);
  const [folders, setFolders] = React.useState(null);
  // Who's in a campaign already (10-19: Jake splits a list between Kellen
  // and Thomas): '' any, 'none' = not in one on the board yet, or a campaign.
  const [inCampaign, setInCampaign] = React.useState('');
  const [pbTags, setPbTags] = React.useState([]);
  const reqRef = React.useRef(0);

  const loadBoard = React.useCallback(() => {
    window.AtlasApi.dialerEmailCampaigns().then(setBoard).catch((e) => setBoard({ error: e.message, campaigns: [], tracked: [] }));
  }, []);
  React.useEffect(() => {
    loadBoard();
    window.AtlasApi.listIndustries().then((r) => setIndustries(Array.isArray(r) ? r : (r.items || []))).catch(() => {});
    window.AtlasApi.dialerQueueLists().then((r) => setQueueLists(r.lists || [])).catch(() => {});
    window.AtlasApi.dialerEmailLists().then((r) => setLists(r.lists || [])).catch(() => {});
    window.AtlasApi.dialerRoutingFolders().then(setFolders).catch(() => {});
    window.AtlasApi.dialerTags().then((r) => setPbTags(r.tags || [])).catch(() => {});
  }, [loadBoard, isAdmin]);

  // Building a list waits for something to narrow it: unfiltered, it sorts
  // every contact in Atlas before showing 200 (~30 s on prod, 10-16).
  const narrowed = queuedOnly || !!emailed || (uploads && !!listId)
    || (!uploads && (!!industry || states.length > 0 || cities.length > 0 || !!q.trim()));
  const setMode = (m) => {
    setQueuedOnly(m === 'queued');
    setUploads(m === 'uploads');
    setEmailed(m === 'emailed' ? (emailed || (tracked[0] && tracked[0].campaign_id) || '') : '');
  };
  const runPreview = async () => {
    const mine = ++reqRef.current;
    if (!narrowed) { setPreview(null); setPicked({}); setLoading(false); return; }
    setLoading(true);
    try {
      const geo = { industry_ids: industry ? [industry] : null, states: states.length ? states : null,
        cities: cities.length ? cities : null, q: q.trim() || null };
      const body = {
        ...(uploads ? { list_id: listId }
          : queuedOnly ? { queued_only: true, queue_source: queueList || null }
          : emailed ? { emailed_campaign: emailed } : { statuses: statuses.length ? statuses : null }),
        ...geo, email_buckets: buckets.length ? buckets : null, include_live_deals: liveDeals,
        in_campaign: inCampaign || null,
        clean_campaign_id: clean || null, filtered_campaign_id: filtered || null, limit,
      };
      const r = await window.AtlasApi.dialerEmailPlan(body);
      if (mine !== reqRef.current) return;
      setPreview(r);
      const sel = {};
      (r.items || []).forEach((i) => { if (i.eligible) sel[i.contact_id] = true; });
      setPicked(sel);
    } catch (e) {
      if (mine === reqRef.current) pushToast({ type: 'error', title: 'Could not load the list', body: e.message });
    } finally { if (mine === reqRef.current) setLoading(false); }
  };
  const filterKey = JSON.stringify([queuedOnly, queueList, uploads, listId, emailed, industry, statuses, states, cities, q, buckets, inCampaign, liveDeals, limit, clean, filtered]);
  React.useEffect(() => {
    const t = setTimeout(() => runPreview(), 350);
    return () => clearTimeout(t);
  }, [filterKey]);   // eslint-disable-line react-hooks/exhaustive-deps

  const items = (preview && preview.items) || [];
  const eligibleIds = new Set(items.filter((i) => i.eligible).map((i) => i.contact_id));
  const target = { clean, filtered };
  // Picked, eligible, and its side has a campaign: what a push sends.
  const chosen = items.filter((i) => picked[i.contact_id] && i.eligible && target[i.side]).map((i) => i.contact_id);
  // The opener + pre-push check are on only for a campaign whose emails use
  // {{opener}}; without one the tab is exactly #782's.
  const showOpener = !!(preview && (preview.opener_campaigns || []).length);
  const forOpeners = items.filter((i) => picked[i.contact_id] && i.eligible && i.opener).map((i) => i.contact_id);
  // …and the same switch for {{multiple_range}} / {{local_comp}} (backend app/email_fields.py).
  const fieldsInPlay = (preview && preview.fields_in_play) || [];
  const showChecked = !!(preview && (preview.checked_campaigns || []).length);
  // Only a comp needs a model call (the same-trade check); a range is read straight from the deck.
  const forComps = items.filter((i) => picked[i.contact_id] && i.eligible && i.fields && i.fields.local_comp)
    .map((i) => i.contact_id);
  const split = { clean: 0, filtered: 0 };
  items.forEach((i) => { if (picked[i.contact_id] && i.eligible && i.side) split[i.side] += 1; });
  const campaigns = (board && board.campaigns) || [];
  const nameOf = (id) => (campaigns.find((c) => c.id === id) || {}).name || id;
  // Campaigns used here first, then the rest of SuperSend.
  const usedIds = new Set(((board && board.tracked) || []).map((c) => c.campaign_id));
  const campaignOptions = [...campaigns.filter((c) => usedIds.has(c.id)), ...campaigns.filter((c) => !usedIds.has(c.id))]
    .map((c) => ({ value: c.id, label: c.name, group: usedIds.has(c.id) ? 'Used here' : 'SuperSend',
      hint: c.contact_count ? `${Number(c.contact_count).toLocaleString()} contacts` : undefined }));
  const labels = (board && board.providers) || {};

  const importList = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const r = await window.AtlasApi.dialerEmailListImport(file);
      setUpload({ ...r, file: file.name });
      setLists((ls) => [r.list, ...ls.filter((l) => l.id !== r.list.id)]);
      setMode('uploads');
      setListId(r.list.id);
    } catch (e) {
      pushToast({ type: 'error', title: 'Upload failed', body: e.message });
    } finally {
      setImporting(false);
      if (listFileRef.current) listFileRef.current.value = '';
    }
  };
  const addMissing = async () => {
    const rows = (upload && upload.addable_rows) || [];
    if (!rows.length) return;
    const nc = upload.new_companies || 0;
    if (!(await ask({ title: `Add ${rows.length} ${rows.length === 1 ? 'person' : 'people'} to Atlas?`,
      body: `${nc ? `${nc} new compan${nc === 1 ? 'y' : 'ies'} at Lead pool, no owner.\n` : ''}Lead source: Cold email · ${upload.list.name}.\nNobody is marked to call.`,
      confirmLabel: 'Add to Atlas' }))) return;
    const pick = ['first_name', 'last_name', 'company', 'company_id', 'website', 'title', 'phone', 'email', 'city', 'state', 'industry', 'linkedin', 'row'];
    const tally = { contacts_created: 0, companies_created: 0, existing: 0, skipped: 0 };
    setAddingList({ done: 0, total: rows.length });
    let last = null;
    try {
      for (let i = 0; i < rows.length; i += 50) {
        const chunk = rows.slice(i, i + 50).map((r) => Object.fromEntries(pick.map((k) => [k, r[k] == null ? null : r[k]])));
        last = await window.AtlasApi.dialerEmailListAdd(upload.list.id, { rows: chunk });
        Object.keys(tally).forEach((k) => { tally[k] += last[k] || 0; });
        setAddingList({ done: Math.min(rows.length, i + 50), total: rows.length });
      }
      pushToast({ type: 'success', title: `Added ${tally.contacts_created} to Atlas`,
        body: [tally.companies_created ? `${tally.companies_created} new compan${tally.companies_created === 1 ? 'y' : 'ies'}` : null,
          tally.existing ? `${tally.existing} were already there` : null,
          tally.skipped ? `${tally.skipped} skipped` : null].filter(Boolean).join(' · ') || undefined });
      setUpload((cur) => cur && { ...cur, addable: 0, addable_rows: [], added: tally.contacts_created + tally.existing });
    } catch (e) {
      pushToast({ type: 'error', title: 'Adding stopped', body: `${tally.contacts_created} added before: ${e.message}` });
    } finally {
      setAddingList(null);
      if (last && last.list) setLists((ls) => ls.map((l) => (l.id === last.list.id ? last.list : l)));
      runPreview();
    }
  };
  const downloadLeftovers = () => {
    const u = upload || {};
    window.exportCsv(`${(u.file || 'list').replace(/\.[a-z]+$/i, '')} – not added.csv`, [
      { key: 'row', label: 'Row' }, { key: 'first_name', label: 'First name' }, { key: 'last_name', label: 'Last name' },
      { key: 'full_name', label: 'Name' }, { key: 'company', label: 'Company' }, { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' }, { key: 'reason', label: 'Why' }], u.leftover_rows || []);
  };
  const push = async () => {
    if (!chosen.length) return;
    const lines = [split.clean ? `${split.clean} → ${nameOf(clean)}` : null,
      split.filtered ? `${split.filtered} → ${nameOf(filtered)}` : null].filter(Boolean).join('\n');
    if (!(await ask({ title: `Add ${chosen.length} to SuperSend?`, body: lines, confirmLabel: 'Add to campaigns' }))) return;
    const tally = { done: 0, total: chosen.length, added: 0, already: 0, skipped: 0, held: 0, failed: 0 };
    const fails = [];
    const shownEmail = {};
    items.forEach((i) => { shownEmail[i.contact_id] = i.email; });
    setPushing({ ...tally });
    try {
      for (let i = 0; i < chosen.length; i += 25) {
        const ids = chosen.slice(i, i + 25);
        const emails = {};
        ids.forEach((id) => { emails[id] = shownEmail[id] || ''; });
        const r = await window.AtlasApi.dialerEmailPush({ contact_ids: ids, emails,
          clean_campaign_id: clean || null, filtered_campaign_id: filtered || null, include_live_deals: liveDeals });
        (r.results || []).forEach((x) => {
          tally[x.status] = (tally[x.status] || 0) + 1;
          if (x.status === 'failed') fails.push(x);
        });
        tally.done = Math.min(chosen.length, i + 25);
        setPushing({ ...tally });
      }
      pushToast({ type: tally.failed || tally.held ? 'error' : 'success',
        title: `Added ${tally.added} to SuperSend${tally.held ? ` · ${tally.held} held` : ''}${tally.failed ? ` · ${tally.failed} failed` : ''}`,
        body: [tally.already ? `${tally.already} were already in` : null, tally.skipped ? `${tally.skipped} skipped` : null,
          fails[0] ? `${fails[0].email}: ${fails[0].detail}` : null].filter(Boolean).join(' · ') || undefined,
        duration: tally.failed || tally.held ? 12000 : undefined });
    } catch (e) {
      pushToast({ type: 'error', title: 'Push stopped', body: `${tally.added} added before: ${e.message}` });
    } finally {
      setPushing(null);
      loadBoard();
      runPreview();
    }
  };
  const writeOpeners = async () => {
    const total = forOpeners.length + forComps.length;
    if (!total) return;
    setWriting({ done: 0, total });
    try {
      for (let i = 0; i < forOpeners.length; i += 25) {
        await window.AtlasApi.dialerEmailOpeners({ contact_ids: forOpeners.slice(i, i + 25) });
        setWriting({ done: Math.min(forOpeners.length, i + 25), total });
      }
      for (let i = 0; i < forComps.length; i += 25) {
        await window.AtlasApi.dialerEmailFields({ contact_ids: forComps.slice(i, i + 25), fields: ['local_comp'] });
        setWriting({ done: forOpeners.length + Math.min(forComps.length, i + 25), total });
      }
    } catch (e) {
      pushToast({ type: 'error', title: forComps.length ? 'Could not write the fields' : 'Could not write the openers', body: e.message });
    } finally {
      setWriting(null);
      runPreview();
    }
  };
  const showUpNext = async (id) => {
    if (upNext && upNext.campaign_id === id) { setUpNext(null); return; }
    try {
      const data = await window.AtlasApi.dialerEmailUpNext(id);
      setUpNext({ campaign_id: id, data });
    } catch (e) { pushToast({ type: 'error', title: 'Could not load Up next', body: e.message }); }
  };
  const showHeld = async (id) => {
    if (heldOpen && heldOpen.campaign_id === id) { setHeldOpen(null); return; }
    try {
      const r = await window.AtlasApi.dialerEmailHeld(id);
      setHeldOpen({ campaign_id: id, items: r.items || [] });
    } catch (e) { pushToast({ type: 'error', title: 'Could not load the held contacts', body: e.message }); }
  };
  const saveMiles = async () => {
    const n = parseInt(miles, 10);
    if (!n || n < 1) return;
    try {
      const r = await window.AtlasApi.dialerEmailSettings({ opener_nearby_miles: n });
      setBoard((b) => ({ ...b, opener_nearby_miles: r.opener_nearby_miles }));
      setMiles('');
      runPreview();
    } catch (e) { pushToast({ type: 'error', title: 'Could not save the distance', body: e.message }); }
  };
  const patchCampaign = async (id, body) => {
    try {
      const r = await window.AtlasApi.dialerEmailCampaign(id, body);
      setBoard((b) => ({ ...b, tracked: r.tracked }));
      // An open Up next for this campaign follows its new delay / settings.
      if (upNext && upNext.campaign_id === id) {
        window.AtlasApi.dialerEmailUpNext(id).then((data) => setUpNext({ campaign_id: id, data })).catch(() => {});
      }
    } catch (e) { pushToast({ type: 'error', title: 'Could not update the campaign', body: e.message }); }
  };
  const saveFolder = async (folderId) => {
    const f = ((folders && folders.available) || []).find((x) => String(x.folder_id) === String(folderId));
    try {
      const r = await window.AtlasApi.dialerEmailSettings({ replied_folder_id: f ? String(f.folder_id) : null,
        replied_folder_name: f ? f.folder_name : null });
      setBoard((b) => ({ ...b, ...r }));
      pushToast({ type: 'success', title: f ? `Replies go to ${f.folder_name}` : 'Folder cleared' });
    } catch (e) { pushToast({ type: 'error', title: 'Could not save the folder', body: e.message }); }
  };
  const toggle = (list, setList, k) => setList(list.includes(k) ? list.filter((x) => x !== k) : [...list, k]);
  const blocked = board && board.send_blocked;
  const tracked = (board && board.tracked) || [];
  const seats = ((status && status.members) || []).filter((m) => m.user_id);
  const repliedFolder = board && board.replied_folder;
  const folderHit = folders && (folders.available || []).find((f) =>
    (repliedFolder && (repliedFolder.ids || []).includes(String(f.folder_id)))
    || (repliedFolder && (f.folder_name || '').trim().toLowerCase() === (repliedFolder.name || '').trim().toLowerCase()));
  const sync = board && board.last_sync;
  // Held column + opener settings only once a campaign on the board uses an Atlas field.
  const boardOpener = tracked.some((c) => c.uses_opener || c.held || (c.atlas_fields || []).length);

  return (
    <>
      {dialog}
      {blocked && (
        <div style={{ marginTop: 12, padding: '8px 12px', borderRadius: 8, fontSize: 12.5,
          background: 'var(--amber-bg, var(--surface-2))', color: 'var(--amber-fg)' }}>{blocked}</div>
      )}
      {board && board.error && <div style={{ marginTop: 12, fontSize: 12.5, color: 'var(--rose-fg)' }}>{board.error}</div>}

      <div style={{ ...callBox, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5 }}>
          <span style={{ color: 'var(--text-2)', minWidth: 110 }} title="Google, Microsoft 365, other hosts and personal mailboxes">Clean inboxes →</span>
          <window.Picker value={clean} onChange={setClean} emptyLabel="Don’t send" options={campaignOptions}
            searchable width={360} menuMinWidth={420} />
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5 }}>
          <span style={{ color: 'var(--text-2)', minWidth: 110 }} title="Proofpoint, Mimecast, Barracuda and other security gateways">Spam-filtered →</span>
          <window.Picker value={filtered} onChange={setFiltered} emptyLabel="Don’t send" options={campaignOptions}
            searchable width={360} menuMinWidth={420} />
        </div>
      </div>

      <div style={{ ...callBox, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 2, padding: 2, background: 'var(--surface-2)', borderRadius: 8 }}>
            <button style={callTabBtn(!queuedOnly && !emailed && !uploads)} onClick={() => setMode('build')}>Build a list</button>
            <button style={callTabBtn(uploads)} onClick={() => setMode('uploads')}>Uploads</button>
            <button style={callTabBtn(queuedOnly)} onClick={() => setMode('queued')}>Marked to call</button>
            {tracked.length > 0 && (
              <button style={callTabBtn(!!emailed)} onClick={() => setMode('emailed')}>Emailed</button>
            )}
          </div>
          {uploads && (
            <window.Picker value={listId} onChange={setListId} minWidth={240} menuMinWidth={340}
              placeholder={lists.length ? 'Pick an upload' : 'No uploads yet'} disabled={!lists.length}
              options={lists.map((l) => ({ value: l.id, label: l.name, count: l.count,
                hint: l.created_at ? new Date(l.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : undefined }))} />
          )}
          <input ref={listFileRef} type="file" accept=".xlsx,.csv" style={{ display: 'none' }}
            onChange={(e) => importList(e.target.files && e.target.files[0])} />
          <Btn size="sm" variant="outline" disabled={importing} onClick={() => listFileRef.current && listFileRef.current.click()}
            title="An .xlsx or .csv with names, emails, phones and companies — people already in Atlas are matched">
            {importing ? 'Uploading…' : 'Upload list'}
          </Btn>
          {queuedOnly && queueLists.length > 0 && (
            <window.Picker value={queueList} onChange={setQueueList} emptyLabel="All marked" minWidth={200} menuMinWidth={300}
              options={queueLists.map((l) => ({ value: l.source, label: l.label, count: l.count }))} />
          )}
          {emailed && (
            <window.Picker value={emailed} onChange={setEmailed} minWidth={220} menuMinWidth={320}
              options={tracked.map((c) => ({ value: c.campaign_id, label: c.name, count: c.emailed }))} />
          )}
          {!uploads && <window.Picker value={industry} onChange={setIndustry} emptyLabel="All industries" minWidth={170} menuMinWidth={260}
            options={industries.map((i) => ({ value: i.id, label: i.name }))} />}
          {!uploads && <CallingPlacePickers states={states} setStates={setStates} cities={cities} setCities={setCities} />}
          {!uploads && <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, company or email…" style={{ ...callSel, minWidth: 180 }} />}
          <window.Picker value={limit} onChange={setLimit} options={CALL_LIMIT_OPTIONS} searchable={false} width={124} />
        </div>
        {!queuedOnly && !emailed && !uploads && <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', fontSize: 12 }}>
          <span style={{ color: 'var(--text-3)' }}>Marketing status:</span>
          {CALL_STATUS_OPTIONS.map(([k, l]) => (
            <button key={k} onClick={() => toggle(statuses, setStatuses, k)}
              style={{ ...callTabBtn(statuses.includes(k)), fontSize: 11.5, padding: '3px 8px' }}>{l}</button>
          ))}
        </div>}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', fontSize: 12 }}>
          <span style={{ color: 'var(--text-3)' }}>Mail filter:</span>
          {EMAIL_BUCKETS.map(([k, l]) => (
            <button key={k} onClick={() => toggle(buckets, setBuckets, k)}
              style={{ ...callTabBtn(buckets.includes(k)), fontSize: 11.5, padding: '3px 8px' }}>{l}</button>
          ))}
          {tracked.length > 0 && (
            <window.Picker value={inCampaign} onChange={setInCampaign} emptyLabel="Any campaign" size="sm" width={230} menuMinWidth={340}
              title="Who has been added to a campaign on the board"
              options={[{ value: 'none', label: 'Not in a campaign yet' },
                ...tracked.map((c) => ({ value: c.campaign_id, label: `In ${c.name}`, count: c.members }))]} />
          )}
          <span style={{ flex: 1 }} />
          <label style={{ display: 'flex', gap: 5, alignItems: 'center', color: 'var(--text-2)' }}>
            <input type="checkbox" checked={liveDeals} onChange={(e) => setLiveDeals(e.target.checked)} /> Include companies with a live deal
          </label>
          {loading && <span style={{ fontSize: 12, color: 'var(--text-4)' }}>Updating…</span>}
        </div>
      </div>

      {upload && (
        <div style={{ marginTop: 12, padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 8,
          background: 'var(--surface-2)', fontSize: 12.5, display: 'grid', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <b style={{ color: 'var(--text)' }}>{upload.file}</b>
            <span style={{ color: 'var(--text-3)' }}>
              {upload.matched} of {upload.rows} rows already in Atlas{upload.added ? ` · ${upload.added} added` : ''}
            </span>
            <span style={{ flex: 1 }} />
            {(upload.leftover_rows || []).length > 0 && (
              <Btn size="xs" variant="ghost" onClick={downloadLeftovers}>Download the {upload.leftovers} not added (.csv)</Btn>
            )}
            <Btn size="xs" variant="ghost" onClick={() => setUpload(null)}>Dismiss</Btn>
          </div>
          {upload.addable > 0 && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--text-2)' }}>
                <b>{upload.addable}</b> {upload.addable === 1 ? 'person isn’t' : 'people aren’t'} in Atlas
                {upload.new_companies ? ` (${upload.new_companies} new compan${upload.new_companies === 1 ? 'y' : 'ies'})` : ''}.
              </span>
              <Btn size="xs" variant="accent" disabled={!!addingList} onClick={addMissing}>
                {addingList ? `Adding… ${addingList.done} of ${addingList.total}` : `Add ${upload.addable} to Atlas`}
              </Btn>
            </div>
          )}
        </div>
      )}
      {!narrowed && (
        <div style={{ marginTop: 14, fontSize: 12.5, color: 'var(--text-3)' }}>
          {uploads ? 'Upload a list or pick one.' : 'Pick a state, city, industry or list to see contacts.'}
        </div>
      )}
      {preview && (
        <>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 14, flexWrap: 'wrap', fontSize: 12.5 }}>
            <span style={{ color: 'var(--text)' }}><b>{split.clean + split.filtered}</b> selected of {preview.eligible} eligible</span>
            {Object.entries(preview.skipped || {}).map(([k, n]) => <Pill key={k} hue="slate" sz="sm">{n} · {k}</Pill>)}
            {preview.would_hold > 0 && <Pill hue="rose" sz="sm">{preview.would_hold} would be held</Pill>}
            {preview.limit_reached && <span style={{ color: 'var(--text-4)' }}>(hit the limit)</span>}
            <span style={{ flex: 1 }} />
            {(split.clean + split.filtered) > 0 && (
              <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
                → <Pill hue={clean ? 'green' : 'slate'} sz="sm">Clean {split.clean}{clean ? '' : ' · no campaign'}</Pill>{' '}
                <Pill hue={filtered ? 'amber' : 'slate'} sz="sm">Spam-filtered {split.filtered}{filtered ? '' : ' · no campaign'}</Pill>
              </span>
            )}
            {(showOpener || fieldsInPlay.includes('local_comp')) && (
              <Btn size="sm" variant="ghost" disabled={!(forOpeners.length + forComps.length) || !!pushing || !!writing}
                onClick={writeOpeners}>
                {writing ? `Writing… ${writing.done} of ${writing.total}` : (fieldsInPlay.includes('local_comp') ? 'Write fields' : 'Write openers')}
              </Btn>
            )}
            <Btn size="sm" variant="accent" icon={<Icon.Mail size={11} />}
              disabled={!!blocked || !chosen.length || !!pushing || !!writing} onClick={push}>
              {pushing ? `Adding… ${pushing.done} of ${pushing.total}` : `Add ${chosen.length} to SuperSend`}
            </Btn>
          </div>
          <div style={{ ...callBox, maxHeight: 520, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-3)', position: 'sticky', top: 0, background: 'var(--surface)' }}>
                  <th style={{ padding: '7px 10px', width: 28 }}>
                    <input type="checkbox" checked={preview.eligible > 0 && split.clean + split.filtered === preview.eligible}
                      onChange={(e) => {
                        const sel = {};
                        if (e.target.checked) items.forEach((i) => { if (i.eligible) sel[i.contact_id] = true; });
                        setPicked(sel);
                      }} />
                  </th>
                  {['Contact', 'Company', 'Email', 'Mail filter', 'Calls', ...(showOpener ? ['Opener'] : []),
                    ...fieldsInPlay.map((f) => EMAIL_FIELD_LABEL[f] || f), ''].map((h) => (
                    <th key={h} style={{ padding: '7px 8px', fontWeight: 600, borderBottom: '1px solid var(--border)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.contact_id} style={{ borderBottom: '1px solid var(--border)', opacity: i.eligible ? 1 : 0.55 }}>
                    <td style={{ padding: '6px 10px' }}>
                      <input type="checkbox" disabled={!i.eligible} checked={!!picked[i.contact_id] && i.eligible}
                        onChange={(e) => setPicked((p) => ({ ...p, [i.contact_id]: e.target.checked }))} />
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      <RecLink kind="contact" id={i.contact_id} style={{ color: 'var(--text)', fontWeight: 550 }}>{i.name || '—'}</RecLink>
                      {i.title && <div style={{ fontSize: 11, color: 'var(--text-4)' }}>{i.title}</div>}
                    </td>
                    <td style={{ padding: '6px 8px', color: 'var(--text-2)' }}>{i.company || '—'}{i.state ? <span style={{ color: 'var(--text-4)' }}> · {i.state}</span> : null}</td>
                    <td style={{ padding: '6px 8px', color: 'var(--text-2)' }}>{i.email || '—'}</td>
                    <td style={{ padding: '6px 8px' }}><EmailProviderPill provider={i.email_provider} gateway={i.email_gateway} labels={labels} /></td>
                    <td style={{ padding: '6px 8px', color: 'var(--text-3)' }}>
                      {i.last_called_at ? `${callWhen(i.last_called_at)} · ${CALL_OUTCOME_LABEL[i.last_outcome] || i.last_outcome || ''}`
                        : i.in_phoneburner ? 'In PhoneBurner' : i.queued ? 'Marked to call' : '—'}
                    </td>
                    {showOpener && (
                      <td style={{ padding: '6px 8px', minWidth: 200, maxWidth: 320 }}>{i.eligible && i.opener && <EmailOpenerCell o={i.opener} />}</td>
                    )}
                    {fieldsInPlay.map((f) => (
                      <td key={f} style={{ padding: '6px 8px', minWidth: 160, maxWidth: 300 }}>
                        {i.eligible && i.fields && <EmailFieldCell name={f} f={i.fields[f]} />}
                      </td>
                    ))}
                    <td style={{ padding: '6px 8px', minWidth: 150, ...(showChecked ? { maxWidth: 280 } : {}) }}><EmailCheckCell i={i} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!items.length && <div style={{ padding: 18, fontSize: 12.5, color: 'var(--text-3)' }}>Nobody matches these filters.</div>}
          </div>
        </>
      )}

      {tracked.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-3)', marginBottom: 6 }}>Campaigns</div>
          <div style={{ ...callBox, marginTop: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-3)' }}>
                  {['Campaign', 'Added', 'Emailed', 'Replied', 'In PhoneBurner', 'Marked to call', 'Pulled on reply', ...(boardOpener ? ['Held'] : []), 'When emailed'].map((h) => (
                    <th key={h} style={{ padding: '7px 10px', fontWeight: 600, borderBottom: '1px solid var(--border)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tracked.map((c) => (
                  <React.Fragment key={c.campaign_id}>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '7px 10px', color: 'var(--text)' }}>
                      {c.name} <Pill hue={c.audience === 'filtered' ? 'amber' : 'green'} sz="sm">{c.audience === 'filtered' ? 'Spam-filtered' : 'Clean'}</Pill>
                      {c.last_sent_at && <div style={{ fontSize: 11, color: 'var(--text-4)' }}>last sent {callWhen(c.last_sent_at)}</div>}
                    </td>
                    <td style={{ padding: '7px 10px' }} className="num">{(c.members || 0).toLocaleString()}</td>
                    <td style={{ padding: '7px 10px' }} className="num">{c.emailed.toLocaleString()}</td>
                    <td style={{ padding: '7px 10px' }} className="num">{c.replied.toLocaleString()}</td>
                    <td style={{ padding: '7px 10px' }} className="num">{c.in_pb.toLocaleString()}</td>
                    <td style={{ padding: '7px 10px' }} className="num">{c.marked.toLocaleString()}</td>
                    <td style={{ padding: '7px 10px' }} className="num">{c.pulled.toLocaleString()}</td>
                    {boardOpener && (
                      <td style={{ padding: '7px 10px' }} className="num">
                        {c.held ? (
                          <button onClick={() => showHeld(c.campaign_id)}
                            style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', color: 'var(--rose-fg)', font: 'inherit' }}>
                            {c.held.toLocaleString()}
                          </button>
                        ) : 0}
                      </td>
                    )}
                    <td style={{ padding: '7px 10px', whiteSpace: 'nowrap' }}>
                      <span style={{ display: 'inline-block', marginRight: 8, verticalAlign: 'middle' }}>
                        <window.Picker value={c.when_emailed || (c.auto_queue ? 'mark' : 'none')} size="sm" width={165}
                          title={`Once a contact's first email goes: “Emailed · ${c.name}” in Send → Marked to call, or straight to PhoneBurner`}
                          onChange={(v) => patchCampaign(c.campaign_id, { when_emailed: v })} searchable={false}
                          options={[{ value: 'none', label: 'Nothing' }, { value: 'mark', label: 'Mark to call' },
                            { value: 'send', label: 'Send to PhoneBurner' }]} />
                      </span>
                      <Btn size="xs" variant="ghost" onClick={() => showUpNext(c.campaign_id)}>Up next</Btn>
                      <Btn size="xs" variant="ghost" onClick={() => { setQueuedOnly(false); setEmailed(c.campaign_id); }}>Show emailed</Btn>
                      <Btn size="xs" variant="ghost" onClick={async () => {
                        if (await ask({ title: `Take ${c.name} off this board?`, confirmLabel: 'Remove' })) patchCampaign(c.campaign_id, { forget: true });
                      }}>Remove</Btn>
                    </td>
                  </tr>
                  {upNext && upNext.campaign_id === c.campaign_id && (
                    <CallingUpNextPanel data={upNext.data} colSpan={boardOpener ? 9 : 8} onClose={() => setUpNext(null)} />
                  )}
                  {c.when_emailed === 'send' && (
                    <CallingAutoSendRow c={c} seats={seats} found={folders} tags={pbTags} delays={(board && board.delay_hours) || []}
                      onPatch={(send) => patchCampaign(c.campaign_id, { send })} colSpan={boardOpener ? 9 : 8} />
                  )}
                  {heldOpen && heldOpen.campaign_id === c.campaign_id && (
                    <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface-2)' }}>
                      <td colSpan={boardOpener ? 9 : 8} style={{ padding: '6px 10px' }}>
                        {!heldOpen.items.length && <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Nobody held.</span>}
                        {heldOpen.items.map((h) => (
                          <div key={h.contact_id} style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '4px 0', fontSize: 12, flexWrap: 'wrap' }}>
                            <RecLink kind="contact" id={h.contact_id} style={{ color: 'var(--text)', fontWeight: 550 }}>{h.name || h.email || '—'}</RecLink>
                            <span style={{ color: 'var(--text-3)' }}>{h.company || '—'} · {h.email}</span>
                            <span style={{ color: 'var(--rose-fg)', flex: 1, minWidth: 220 }}>{(h.reasons || []).join(' · ')}</span>
                            {h.held_at && <span style={{ color: 'var(--text-4)' }}>{callWhen(h.held_at)}</span>}
                          </div>
                        ))}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ marginTop: 18, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5 }}>
        <span style={{ color: 'var(--text-3)' }}>Replies move to</span>
        {isAdmin && folders && !folders.error ? (
          <window.Picker value={folderHit ? String(folderHit.folder_id) : ''} onChange={saveFolder} minWidth={240} menuMinWidth={300}
            placeholder={repliedFolder ? `${repliedFolder.name} (not found)` : 'Pick a folder'}
            options={(folders.available || []).map((f) => ({ value: String(f.folder_id), label: f.path || f.folder_name }))} />
        ) : <b style={{ color: 'var(--text)' }}>{repliedFolder ? repliedFolder.name : '—'}</b>}
        <span style={{ color: 'var(--text-4)' }}>in PhoneBurner</span>
        {sync && sync.at && (
          <span style={{ color: 'var(--text-4)' }} title={JSON.stringify(sync)}>
            · checked {callWhen(sync.at)}
            {sync.replies && sync.replies.pulled ? ` · ${sync.replies.pulled} pulled` : ''}
            {sync.replies && sync.replies.error ? ` · ${sync.replies.error}` : ''}
          </span>
        )}
      </div>
      {(boardOpener || showChecked) && <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5 }}>
        <span style={{ color: 'var(--text-3)' }}>Copy rules</span>
        <Btn size="xs" variant="ghost" onClick={() => setRulesOpen((o) => !o)}>{rulesOpen ? 'Close' : 'Edit'}</Btn>
      </div>}
      {rulesOpen && <EmailCopyRules onClose={() => setRulesOpen(false)} onSaved={() => runPreview()} />}
      {(boardOpener || showOpener) && <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5 }}>
        <span style={{ color: 'var(--text-3)' }}>Openers use same-trade sales within</span>
        {isAdmin ? (
          <>
            <input type="number" min={1} max={500} value={miles} placeholder={String((board && board.opener_nearby_miles) || 50)}
              onChange={(e) => setMiles(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') saveMiles(); }}
              style={{ ...callSel, width: 70 }} />
            <span style={{ color: 'var(--text-4)' }}>miles</span>
            {miles && <Btn size="xs" variant="ghost" onClick={saveMiles}>Save</Btn>}
          </>
        ) : <b style={{ color: 'var(--text)' }}>{(board && board.opener_nearby_miles) || 50} miles</b>}
      </div>}
    </>
  );
};

// Recording routing: new contacts land in REC or NO REC – Attempt 1 by
// whether the call may be recorded (backend app/call_recording.py).
// PhoneBurner moves them through Attempt 2/3, Callback, Exhausted itself.
const CallingRoutingSection = ({ status, isAdmin, onChanged }) => {
  const routing = status.routing || {};
  const [found, setFound] = React.useState({});
  const [foldersLoaded, setFoldersLoaded] = React.useState(false);
  const loadFolders = React.useCallback(() => {
    window.AtlasApi.dialerRoutingFolders()
      .then((f) => { setFound(f || {}); setFoldersLoaded(true); })
      .catch((e) => { setFound({ error: e.message }); setFoldersLoaded(true); });
  }, []);
  React.useEffect(() => { loadFolders(); }, [loadFolders, JSON.stringify(status.routing || {})]);   // eslint-disable-line react-hooks/exhaustive-deps
  const [states, setStates] = React.useState(routing.no_rec_states || []);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { setStates(routing.no_rec_states || []); }, [JSON.stringify(routing.no_rec_states || [])]);   // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = JSON.stringify([...states].sort()) !== JSON.stringify([...(routing.no_rec_states || [])].sort());
  const toggle = (code) => setStates((cur) => (cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code]));
  const save = async () => {
    setBusy(true);
    try {
      await window.AtlasApi.dialerRouting({ no_rec_states: states });
      pushToast({ type: 'success', title: 'Recording routing saved' });
      onChanged();
    } catch (e) { pushToast({ type: 'error', title: 'Could not save', body: e.message }); }
    finally { setBusy(false); }
  };
  // Every folder PhoneBurner shows (subfolders too) — pick Attempt 1 here.
  const available = found.available || [];
  const pickFolder = async (key, folderId) => {
    const f = available.find((x) => x.folder_id === folderId);
    if (!f) return;
    setBusy(true);
    try {
      await window.AtlasApi.dialerRouting({ no_rec_states: routing.no_rec_states || [],
        [key]: { name: f.folder_name, ids: [f.folder_id] } });
      pushToast({ type: 'success', title: 'Folder saved', body: f.path || f.folder_name });
      onChanged();
    } catch (e) { pushToast({ type: 'error', title: 'Could not save', body: e.message }); }
    finally { setBusy(false); }
  };
  const folderLine = (label, key, spec, hit) => (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '2px 0', flexWrap: 'wrap' }}>
      <span style={{ minWidth: 70, color: 'var(--text-2)' }}>{label}</span>
      <span style={{ color: 'var(--text)' }}>{(spec && spec.name) || '—'}</span>
      {!foldersLoaded ? <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>checking PhoneBurner…</span>
        : found.error ? <span style={{ color: 'var(--rose-fg)' }}>{found.error}</span>
        : hit && hit.found ? <Pill hue="green" sz="sm">found · id {hit.folder_id}</Pill>
          : <Pill hue="rose" sz="sm">not found in PhoneBurner — sends will stop</Pill>}
      {isAdmin && available.length > 0 && (
        <window.Picker value={(hit && hit.found && hit.folder_id) || ''} disabled={busy}
          onChange={(v) => pickFolder(key, v)} minWidth={240} menuMinWidth={320} placeholder="Choose a folder…"
          title="The PhoneBurner folder new contacts go to"
          options={available.map((f) => ({ value: f.folder_id, label: f.path || f.folder_name }))} />
      )}
    </div>
  );
  return (
    <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: 12.5 }}>
      <div style={{ fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>
        Recording routing
        <window.HelpTip text="Every send goes to Attempt 1 of one track. A contact goes to NO REC when its phone's area code, its state, or its company's state is an all-party-consent state — or when the number is outside the US or there is no location at all. Everyone else goes to REC." />
      </div>
      {folderLine('REC', 'rec_folder', routing.rec_folder, found.rec)}
      {folderLine('NO REC', 'no_rec_folder', routing.no_rec_folder, found.no_rec)}
      {!found.error && foldersLoaded && !available.length && (
        <div style={{ color: 'var(--rose-fg)', marginTop: 4 }}>PhoneBurner returned no folders for the connected account.</div>
      )}
      <div style={{ marginTop: 10, color: 'var(--text-2)' }}>All-party-consent states (NO REC):</div>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
        {(status.states || []).map((s) => {
          const on = states.includes(s.code);
          return (
            <button key={s.code} disabled={!isAdmin} onClick={() => toggle(s.code)} title={s.name}
              style={{ fontSize: 11, padding: '2px 7px', borderRadius: 5, cursor: isAdmin ? 'pointer' : 'default',
                border: '1px solid', borderColor: on ? 'var(--amber-fg)' : 'var(--border)',
                background: on ? 'var(--amber-bg, var(--surface-2))' : 'transparent',
                color: on ? 'var(--amber-fg)' : 'var(--text-4)' }}>{s.code}</button>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
        {isAdmin && <Btn size="xs" variant="accent" disabled={!dirty || busy} onClick={save}>{busy ? 'Saving…' : 'Save states'}</Btn>}
        <span style={{ fontSize: 11, color: 'var(--text-4)' }}>
          Default: the 11 clear all-party states plus CT, MI, OR and VT (mixed law, treated as all-party). Not legal advice — confirm with counsel.
        </span>
      </div>
    </div>
  );
};

// ============ Custom fields: PhoneBurner field ← Atlas value ============
// Loaded live from PhoneBurner with their types. A dropdown needs an option,
// so a mapped dropdown gets a value map — auto-matched by name, editable.
const cfNorm = (s) => String(s == null ? '' : s).toLowerCase().replace(/&/g, ' and ')
  .replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();

const CallingCustomFieldsSection = ({ isAdmin }) => {
  const { ask, dialog: confirmDialog } = useCallingConfirm();
  const [data, setData] = React.useState(null);
  const [rows, setRows] = React.useState({});
  const [base, setBase] = React.useState('');
  const [openMap, setOpenMap] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  const [adding, setAdding] = React.useState(null);   // {name, type_id, source} while the New field form is open
  const toRows = (d) => {
    const out = {};
    (d.pb_fields || []).forEach((f) => {
      const m = ((d.mapping && d.mapping.fields) || []).find((x) => (x.pb_id && x.pb_id === f.id) || cfNorm(x.pb_name) === cfNorm(f.name));
      out[f.id] = { source: m ? m.source : '', value_map: (m && m.value_map) || {},
        required: m && typeof m.required === 'boolean' ? m.required : undefined };
    });
    return out;
  };
  const toSave = (d, rs) => (d.pb_fields || []).filter((f) => rs[f.id] && rs[f.id].source)
    .map((f) => ({ pb_name: f.name, pb_id: f.id, source: rs[f.id].source, value_map: rs[f.id].value_map || {},
      ...(typeof rs[f.id].required === 'boolean' ? { required: rs[f.id].required } : {}) }));
  const take = (d) => { const rs = toRows(d); setData(d); setRows(rs); setBase(JSON.stringify(toSave(d, rs))); };
  const load = () => {
    setData(null);
    window.AtlasApi.dialerCustomFields().then(take).catch((e) => setData({ error: e.message, pb_fields: [] }));
  };
  React.useEffect(() => { load(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: 12.5, color: 'var(--text-4)' }}>Loading custom fields…</div>;
  const fields = data.pb_fields || [];
  const sources = data.sources || [];
  const srcOf = (k) => sources.find((s) => s.key === k);
  const unlisted = ((data.mapping && data.mapping.fields) || []).filter((m) =>
    !fields.some((f) => (m.pb_id && m.pb_id === f.id) || cfNorm(m.pb_name) === cfNorm(f.name)));
  const missing = unlisted.filter((m) => !m.create);
  const pending = unlisted.filter((m) => m.create);
  const dirty = JSON.stringify(toSave(data, rows)) !== base;
  const setRow = (id, patch) => setRows((cur) => ({ ...cur, [id]: { ...(cur[id] || {}), ...patch } }));
  const setOpt = (id, value, optId) => setRows((cur) => {
    const vm = { ...((cur[id] && cur[id].value_map) || {}) };
    if (optId) vm[value] = optId; else delete vm[value];
    return { ...cur, [id]: { ...(cur[id] || {}), value_map: vm } };
  });
  const save = async () => {
    setBusy(true);
    try {
      take(await window.AtlasApi.dialerSaveCustomFields({ fields: toSave(data, rows) }));
      pushToast({ type: 'success', title: 'Custom fields saved', body: 'Contacts sent from now on use this mapping.' });
    } catch (e) { pushToast({ type: 'error', title: 'Could not save', body: e.message }); }
    finally { setBusy(false); }
  };
  const typeName = (f) => (data.types && data.types[f.type_id]) || f.type_name || 'Text';
  const copyValues = (values) => {
    try {
      navigator.clipboard.writeText(values.join('\n'));
      pushToast({ type: 'success', title: `Copied ${values.length} values`, body: 'One per line — paste them as the dropdown’s options in PhoneBurner.' });
    } catch (_) { pushToast({ type: 'error', title: 'Could not copy' }); }
  };
  const deleteField = async (f) => {
    const dupes = (data.pb_fields || []).filter((x) => cfNorm(x.name) === cfNorm(f.name));
    if (!(await ask({
      title: `Delete “${f.name}” from PhoneBurner?`,
      body: `Field id ${f.id}. Its value on every contact is deleted with it, and this can’t be undone.`
        + (dupes.length > 1 ? `\n\nPhoneBurner has ${dupes.length} fields with this name (ids ${dupes.map((x) => x.id).join(', ')}). Keep the one your contacts show values in.` : ''),
      confirmLabel: 'Delete field', danger: true,
    }))) return;
    setBusy(true);
    try {
      take(await window.AtlasApi.dialerDeleteCustomField(f.id));
      pushToast({ type: 'success', title: `Deleted “${f.name}” from PhoneBurner` });
    } catch (e) { pushToast({ type: 'error', title: 'Could not delete the field', body: e.message }); }
    finally { setBusy(false); }
  };
  const nameCount = {};
  (data.pb_fields || []).forEach((f) => { nameCount[cfNorm(f.name)] = (nameCount[cfNorm(f.name)] || 0) + 1; });
  const createField = async () => {
    if (!adding || !adding.name.trim()) return;
    setBusy(true);
    try {
      const r = await window.AtlasApi.dialerNewCustomField({ name: adding.name.trim(), type_id: Number(adding.type_id),
        source: adding.source || null });
      take(r);
      setAdding(null);
      pushToast({ type: 'success', title: `Added “${r.created ? r.created.name : adding.name}”`,
        body: 'PhoneBurner creates it with the next send, filled from Atlas.' });
    } catch (e) { pushToast({ type: 'error', title: 'Could not create the field', body: e.message }); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: 12.5 }}>
      {confirmDialog}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontWeight: 600, color: 'var(--text-2)' }}>Custom fields</span>
        <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>
          {fields.length ? `${fields.length} in PhoneBurner · ${Object.values(rows).filter((r) => r.source).length} filled from Atlas` : ''}
        </span>
        <span style={{ flex: 1 }} />
        <Btn size="xs" variant="ghost" icon={<Icon.Refresh size={11} />} onClick={load} title="Read the fields from PhoneBurner again">Reload</Btn>
        {isAdmin && <Btn size="xs" variant="accent" disabled={!dirty || busy} onClick={save}>{busy ? 'Saving…' : 'Save mapping'}</Btn>}
      </div>
      {data.error && <div style={{ color: 'var(--rose-fg)', marginBottom: 6 }}>{data.error}</div>}
      {data.mapping && data.mapping.default && fields.length > 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 6 }}>
          Not saved yet: sends fill Company, Industry, Title and Atlas link when PhoneBurner has fields with those names.
        </div>
      )}
      {fields.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--text-3)' }}>
              <th style={{ padding: '4px 6px', fontWeight: 500 }}>PhoneBurner field</th>
              <th style={{ padding: '4px 6px', fontWeight: 500 }}>Type</th>
              <th style={{ padding: '4px 6px', fontWeight: 500 }}>Filled from Atlas</th>
              <th style={{ padding: '4px 6px' }} />
            </tr>
          </thead>
          <tbody>
            {fields.map((f) => {
              const r = rows[f.id] || { source: '', value_map: {} };
              const src = srcOf(r.source);
              const isDrop = f.type_id === 6;
              const values = isDrop && src && src.enum ? ((data.enums || {})[src.enum] || []) : [];
              const opts = f.options || [];
              const auto = (v) => opts.find((o) => cfNorm(o.label) === cfNorm(v));
              const matched = values.filter((v) => (r.value_map || {})[v] || auto(v)).length;
              return (
                <React.Fragment key={f.id}>
                  <tr style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '5px 6px', color: 'var(--text)' }}>
                      {f.name}
                      <span style={{ marginLeft: 6, fontSize: 10.5, color: nameCount[cfNorm(f.name)] > 1 ? 'var(--amber-fg)' : 'var(--text-4)' }}
                        title={nameCount[cfNorm(f.name)] > 1 ? 'PhoneBurner has more than one field with this name' : 'PhoneBurner field id'}>
                        id {f.id}{nameCount[cfNorm(f.name)] > 1 ? ' · duplicate name' : ''}
                      </span>
                    </td>
                    <td style={{ padding: '5px 6px' }}><Pill hue={isDrop ? 'blue' : 'slate'} sz="sm">{typeName(f)}</Pill></td>
                    <td style={{ padding: '5px 6px' }}>
                      <window.Picker value={r.source} disabled={!isAdmin} onChange={(v) => setRow(f.id, { source: v })}
                        emptyLabel="— not filled —" size="sm" minWidth={220} menuMinWidth={300}
                        options={sources.map((s) => ({ value: s.key, label: s.label }))} />
                    </td>
                    <td style={{ padding: '5px 6px', color: 'var(--text-3)', fontSize: 11.5 }}>
                      {isAdmin && (
                        <button onClick={() => deleteField(f)} disabled={busy} title={`Delete “${f.name}” from PhoneBurner`}
                          style={{ float: 'right', background: 'transparent', border: 'none', cursor: 'pointer',
                            color: 'var(--text-4)', padding: '2px 4px' }}>
                          <Icon.Trash size={12} />
                        </button>
                      )}
                      {isDrop && r.source && (
                        <label style={{ display: 'flex', gap: 5, alignItems: 'center', marginBottom: 3, color: 'var(--text-2)' }}
                          title="On: a contact whose value isn't one of this dropdown's options isn't sent — it's listed as errored so the option can be added in PhoneBurner. Off: the field is left empty.">
                          <input type="checkbox" disabled={!isAdmin} checked={r.required !== false}
                            onChange={(e) => setRow(f.id, { required: e.target.checked })} />
                          Don’t send if it doesn’t match
                        </label>
                      )}
                      {isDrop && r.source && !opts.length && f.options_blank && (
                        <span style={{ color: 'var(--amber-fg)' }}>
                          This dropdown has no options in PhoneBurner yet, so it’s left empty.
                          {src && src.enum && ((data.enums || {})[src.enum] || []).length > 0 && (
                            <Btn size="xs" variant="ghost" style={{ marginLeft: 6 }}
                              onClick={() => copyValues((data.enums || {})[src.enum])}
                              title="Copy Atlas’s values, one per line, to add as the dropdown’s options in PhoneBurner">
                              Copy the {((data.enums || {})[src.enum] || []).length} Atlas values
                            </Btn>
                          )}
                        </span>
                      )}
                      {isDrop && r.source && !opts.length && !f.options_blank && (
                        <span style={{ color: 'var(--rose-fg)' }}>
                          PhoneBurner didn’t list this dropdown’s options
                          {f.options_shape && (
                            <span style={{ display: 'block', color: 'var(--text-4)', fontSize: 10.5, wordBreak: 'break-word' }}>
                              Reply: {f.options_shape}
                            </span>
                          )}
                        </span>
                      )}
                      {isDrop && r.source && opts.length > 0 && values.length > 0 && (
                        <Btn size="xs" variant="ghost" onClick={() => setOpenMap((c) => ({ ...c, [f.id]: !c[f.id] }))}>
                          {matched} of {values.length} values matched {openMap[f.id] ? '▴' : '▾'}
                        </Btn>
                      )}
                      {isDrop && r.source && opts.length > 0 && !values.length && (
                        <span title="Sent only when the Atlas text equals one of the options">Sent when it matches an option</span>
                      )}
                    </td>
                  </tr>
                  {openMap[f.id] && values.length > 0 && (
                    <tr>
                      <td colSpan={4} style={{ padding: '4px 6px 10px 18px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 1fr) minmax(180px, 1fr)', gap: '4px 12px', maxHeight: 320, overflow: 'auto' }}>
                          {values.map((v) => {
                            const a = auto(v);
                            return (
                              <React.Fragment key={v}>
                                <span style={{ color: 'var(--text-2)', alignSelf: 'center' }}>{v}</span>
                                <window.Picker value={(r.value_map || {})[v] || ''} disabled={!isAdmin} size="sm" width="100%"
                                  onChange={(x) => setOpt(f.id, v, x)} emptyLabel={a ? `${a.label} (same name)` : '— leave empty —'}
                                  options={opts.map((o) => ({ value: o.id, label: o.label }))} />
                              </React.Fragment>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
      {!data.error && !fields.length && <div style={{ color: 'var(--text-4)' }}>PhoneBurner has no custom fields on this account.</div>}
      {isAdmin && !data.error && (adding ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8,
          padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8 }}>
          <input autoFocus value={adding.name} maxLength={80} placeholder="Field name, e.g. Nearest deal"
            onChange={(e) => setAdding((a) => ({ ...a, name: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') createField(); if (e.key === 'Escape') setAdding(null); }}
            style={{ ...callSel, width: 200 }} />
          <window.Picker value={adding.type_id} onChange={(v) => setAdding((a) => ({ ...a, type_id: v }))} width={120}
            title="PhoneBurner’s API can’t make dropdowns — build those in PhoneBurner, then map them here" searchable={false}
            options={[{ value: 1, label: 'Text' }, { value: 7, label: 'Number' }, { value: 3, label: 'Date' }, { value: 2, label: 'Checkbox' }]} />
          <window.Picker value={adding.source} onChange={(v) => setAdding((a) => ({ ...a, source: v }))}
            placeholder="Filled from…" minWidth={220} menuMinWidth={300}
            options={sources.map((s) => ({ value: s.key, label: s.label }))} />
          <Btn size="xs" variant="accent" disabled={busy || !adding.name.trim() || !adding.source} onClick={createField}
            title="PhoneBurner creates the field with the next send">
            {busy ? 'Adding…' : 'Add field'}
          </Btn>
          <Btn size="xs" variant="ghost" onClick={() => setAdding(null)}>Cancel</Btn>
        </div>
      ) : (
        <Btn size="xs" variant="ghost" icon={<Icon.Plus size={11} />} style={{ marginTop: 6 }}
          onClick={() => setAdding({ name: '', type_id: 1, source: '' })}
          title="Add a field to the caller’s screen in PhoneBurner, filled from Atlas">New field</Btn>
      ))}
      {pending.length > 0 && (
        <div style={{ marginTop: 6, color: 'var(--text-3)' }}>
          Added, created in PhoneBurner with the next send: {pending.map((m) => `${m.pb_name} ← ${(srcOf(m.source) || {}).label || m.source}`).join(' · ')}
        </div>
      )}
      {missing.length > 0 && (
        <div style={{ marginTop: 6, color: 'var(--amber-fg)' }}>
          Not in PhoneBurner any more (skipped on send): {missing.map((m) => m.pb_name).join(', ')}
        </div>
      )}
    </div>
  );
};

// A caller seat's own PhoneBurner token: the only one that can read that
// seat's calls (transcripts, AI summaries). Pasted by an admin; never shown.
const CallingSeatToken = ({ seat, onChanged }) => {
  const [open, setOpen] = React.useState(false);
  const [token, setToken] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await window.AtlasApi.dialerSeatToken({ pb_user_id: seat.user_id, token: token.trim() });
      setToken(''); setOpen(false);
      const top = r.check && r.check.reasons && r.check.reasons[0];
      pushToast({ type: r.check && r.check.found ? 'success' : 'info', title: `${seat.name || 'Seat'} connected`,
        body: (r.check ? `Checked ${r.check.checked}: ${r.check.found} transcript${r.check.found === 1 ? '' : 's'} found.` : '')
          + (top && !r.check.found ? ` ${top.reason}.` : ''),
        duration: 12000 });
      onChanged();
    } catch (e) { pushToast({ type: 'error', title: 'Could not connect the seat', body: e.message }); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await window.AtlasApi.dialerDeleteSeatToken(seat.user_id); onChanged(); }
    catch (e) { pushToast({ type: 'error', title: 'Could not remove the token', body: e.message }); }
    finally { setBusy(false); }
  };
  if (seat.own_token) {
    return <Btn size="xs" variant="ghost" disabled={busy} onClick={remove} title="Stop reading this seat's calls">Remove token</Btn>;
  }
  if (!open) {
    return <Btn size="xs" variant="outline" onClick={() => setOpen(true)} title="Needed for transcripts and AI summaries">Add seat token</Btn>;
  }
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <input type="password" autoComplete="off" value={token} onChange={(e) => setToken(e.target.value)}
        placeholder={`${seat.name || 'Seat'}'s PhoneBurner token`} style={{ ...callSel, width: 240 }} />
      <Btn size="xs" variant="accent" disabled={busy || token.trim().length < 10} onClick={save}>{busy ? 'Checking…' : 'Save'}</Btn>
      <Btn size="xs" variant="ghost" onClick={() => { setOpen(false); setToken(''); }}>Cancel</Btn>
    </span>
  );
};

// "Check transcripts now": re-asks PhoneBurner and says what came back.
const CallingTranscriptCheck = () => {
  const [busy, setBusy] = React.useState(false);
  const [res, setRes] = React.useState(null);
  const run = async () => {
    setBusy(true);
    try { setRes(await window.AtlasApi.dialerCheckTranscripts()); }
    catch (e) { setRes({ error: e.message }); }
    finally { setBusy(false); }
  };
  return (
    <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <Btn size="xs" variant="outline" disabled={busy} onClick={run}
        title="Ask PhoneBurner for transcripts now">
        {busy ? 'Checking…' : 'Check transcripts now'}
      </Btn>
      {res && (res.error
        ? <span style={{ fontSize: 11.5, color: 'var(--rose-fg)' }}>{res.error}</span>
        : <span style={{ fontSize: 11.5, color: res.found ? 'var(--green-fg)' : 'var(--text-3)' }}>
            Checked {res.checked}: {res.found} found{(res.reasons || []).filter((x) => x.reason !== 'Transcript found')
              .map((x) => ` · ${x.calls}: ${x.reason}`).join('')}
          </span>)}
    </span>
  );
};

// PhoneBurner folders are the truth for a sent contact (09-30): what a
// re-filed contact changes in Atlas — a preview until an admin applies it.
const CallingFolderSyncSection = () => {
  const { ask, dialog } = useCallingConfirm();
  const [data, setData] = React.useState(null);
  const [busy, setBusy] = React.useState(null);
  const load = () => window.AtlasApi.dialerFolderSync().then(setData).catch((e) => setData({ error: e.message }));
  React.useEffect(() => { load(); }, []);
  const run = async () => {
    setBusy('run');
    try { setData(await window.AtlasApi.dialerRunFolderSync()); }
    catch (e) { pushToast({ type: 'error', title: 'Could not read PhoneBurner', body: e.message }); }
    finally { setBusy(null); }
  };
  const setApply = async (on) => {
    const n = ((data && data.plan) || []).length;
    if (on && !(await ask({
      title: `Apply ${n} change${n === 1 ? '' : 's'}?`,
      body: 'Each call is re-tagged to match its PhoneBurner folder: statuses, the booked list and the dashboard follow, and #cold-calling hears about bookings that weren’t. From then on, folder moves apply on their own.',
      confirmLabel: 'Apply' }))) return;
    setBusy('apply');
    try {
      const r = await window.AtlasApi.dialerSetFolderSync(on);
      setData(r);
      pushToast({ type: 'success', title: on ? 'Folder corrections on' : 'Back to preview',
        body: on && r.result ? `${r.result.fixed || 0} applied now.` : undefined });
    } catch (e) { pushToast({ type: 'error', title: 'Could not save', body: e.message }); }
    finally { setBusy(null); }
  };
  const box = { padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: 12.5 };
  if (!data) return <div style={{ ...box, color: 'var(--text-4)' }}>Loading PhoneBurner folders…</div>;
  const plan = data.plan || [];
  const checked = !!data.at;
  const empty = checked && !data.listed;
  return (
    <div style={box}>
      {dialog}
      <div style={{ fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>PhoneBurner folders</div>
      <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 8, lineHeight: 1.5 }}>
        {data.apply ? 'Corrections apply on their own.' : 'Preview: nothing in Atlas changes until you apply it.'}
      </div>
      {data.error && <div style={{ color: 'var(--rose-fg)', marginBottom: 6 }}>{data.error}</div>}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: plan.length ? 8 : 0 }}>
        <span style={{ color: 'var(--text-3)' }}>
          {checked
            ? `Checked ${callWhen(data.at)} · ${data.listed || 0} contacts in PhoneBurner · ${data.matched || 0} sent from Atlas`
            : 'Not checked yet'}
        </span>
        {checked && data.booked_now != null && (
          <span style={{ color: 'var(--text-2)' }}>
            · Call scheduled: <b>{data.booked_now}</b>{data.booked_after !== data.booked_now ? <> → <b>{data.booked_after}</b></> : ''}
          </span>
        )}
        <span style={{ flex: 1 }} />
        <Btn size="xs" variant="outline" disabled={!!busy} onClick={run}>{busy === 'run' ? 'Checking…' : 'Check now'}</Btn>
        {!data.apply && plan.length > 0 && (
          <Btn size="xs" variant="accent" disabled={!!busy} onClick={() => setApply(true)}>
            {busy === 'apply' ? 'Applying…' : `Apply ${plan.length} change${plan.length === 1 ? '' : 's'}`}
          </Btn>
        )}
        {data.apply && <Btn size="xs" variant="ghost" disabled={!!busy} onClick={() => setApply(false)}>Back to preview</Btn>}
      </div>
      {empty && (
        <div style={{ color: 'var(--amber-fg)', marginTop: 4 }}>
          PhoneBurner listed no contacts{data.shape ? <> — its reply looked like <code style={{ fontSize: 11 }}>{data.shape}</code></> : ''}.
        </div>
      )}
      {checked && !empty && !plan.length && (
        <div style={{ color: 'var(--text-3)' }}>Every sent contact’s folder matches its latest call — nothing to change.</div>
      )}
      {plan.length > 0 && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
          {plan.map((p) => (
            <div key={p.contact_id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '6px 10px', borderTop: '1px solid var(--border)' }}>
              <RecLink kind="contact" id={p.contact_id} style={{ fontWeight: 600, color: 'var(--text)' }}>{p.name || 'Contact'}</RecLink>
              {p.company && <span style={{ color: 'var(--text-3)' }}>{p.company}</span>}
              <span style={{ flex: 1 }} />
              <span style={{ color: 'var(--text-4)' }}>in {p.folder}</span>
              <Pill hue={CALL_OUTCOME_HUE[p.from] || 'slate'} sz="sm">{p.from_label}</Pill>
              <span style={{ color: 'var(--text-4)' }}>→</span>
              <Pill hue={CALL_OUTCOME_HUE[p.to] || 'slate'} sz="sm">{p.to_label}</Pill>
            </div>
          ))}
        </div>
      )}
      {(data.recent || []).length > 0 && (
        <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--text-4)' }}>
          Last applied: {data.recent.slice(0, 5).map((r) => `${CALL_OUTCOME_LABEL[r.from] || r.from} → ${CALL_OUTCOME_LABEL[r.to] || r.to} (${r.folder})`).join(' · ')}
        </div>
      )}
    </div>
  );
};

// PhoneBurner callbacks → a short event on the caller's Outlook calendar.
const CallingCallbackEventsSection = () => {
  const [data, setData] = React.useState(null);
  const [busy, setBusy] = React.useState(null);
  React.useEffect(() => {
    window.AtlasApi.dialerCallbackEvents().then(setData).catch((e) => setData({ error: e.message }));
  }, []);
  const run = async () => {
    setBusy('run');
    try { setData(await window.AtlasApi.dialerRunCallbackEvents()); }
    catch (e) { pushToast({ type: 'error', title: 'Could not sync the calendar', body: e.message }); }
    finally { setBusy(null); }
  };
  const setOn = async (on) => {
    setBusy('on');
    try { setData(await window.AtlasApi.dialerSetCallbackEvents(on)); }
    catch (e) { pushToast({ type: 'error', title: 'Could not save', body: e.message }); }
    finally { setBusy(null); }
  };
  const box = { padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: 12.5 };
  if (!data) return <div style={{ ...box, color: 'var(--text-4)' }}>Loading callbacks…</div>;
  const last = data.last || {};
  const list = (data.callbacks || []).filter((c) => new Date(c.at) > new Date());
  return (
    <div style={box}>
      <div style={{ fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>Callbacks in Outlook</div>
      {data.error && <div style={{ color: 'var(--rose-fg)', marginBottom: 6 }}>{data.error}</div>}
      {last.needs_permission && (
        <div style={{ color: 'var(--amber-fg)', marginBottom: 6 }}>
          Waiting on Microsoft: grant <b>{last.needs_permission}</b> as an Application permission on the Atlas app in Entra (admin consent).
          {last.waiting ? ` ${last.waiting} callback${last.waiting === 1 ? '' : 's'} will be added once it’s granted.` : ''}
        </div>
      )}
      {last.error && <div style={{ color: 'var(--rose-fg)', marginBottom: 6 }}>{last.error}</div>}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: list.length ? 8 : 0 }}>
        <span style={{ color: 'var(--text-3)' }}>
          {!data.on ? 'Off' : last.at
            ? `Synced ${callWhen(last.at)}${last.created ? ` · ${last.created} added` : ''}${last.moved ? ` · ${last.moved} moved` : ''}${last.removed ? ` · ${last.removed} removed` : ''}${last.failed ? ` · ${last.failed} failed` : ''}`
            : 'Not synced yet'}
        </span>
        <span style={{ flex: 1 }} />
        {data.on && <Btn size="xs" variant="outline" disabled={!!busy} onClick={run}>{busy === 'run' ? 'Syncing…' : 'Sync now'}</Btn>}
        <Btn size="xs" variant={data.on ? 'ghost' : 'accent'} disabled={!!busy} onClick={() => setOn(!data.on)}>{data.on ? 'Turn off' : 'Turn on'}</Btn>
      </div>
      {list.length > 0 && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
          {list.map((c) => (
            <div key={c.contact_id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '6px 10px', borderTop: '1px solid var(--border)' }}>
              <RecLink kind="contact" id={c.contact_id} style={{ fontWeight: 600, color: 'var(--text)' }}>{c.name || 'Contact'}</RecLink>
              {c.company && <span style={{ color: 'var(--text-3)' }}>{c.company}</span>}
              <span style={{ flex: 1 }} />
              <span style={{ color: 'var(--text-2)' }}>{callWhen(c.at)}{c.time_set ? '' : ' (no time set)'}</span>
              {c.error
                ? <Pill hue="rose" sz="sm" title={c.error}>Failed</Pill>
                : c.in_calendar ? <Pill hue="green" sz="sm">In Outlook</Pill>
                  : <Pill hue="slate" sz="sm">{/call\s*back|follow\s*up/i.test(c.folder_name || '') ? 'Waiting' : 'Not in Callback'}</Pill>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Booked calls post to one team channel (the caller holds the second call).
const CallingSlackChannelsSection = ({ isAdmin }) => {
  const [data, setData] = React.useState(null);
  const [channel, setChannel] = React.useState('');
  const [base, setBase] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const take = (d) => {
    const id = (d.mapping && d.mapping.default && d.mapping.default.id) || '';
    setData(d); setChannel(id); setBase(id);
  };
  React.useEffect(() => {
    window.AtlasApi.dialerSlackChannels().then(take).catch((e) => setData({ error: e.message, channels: [] }));
  }, []);
  if (!data) return <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: 12.5, color: 'var(--text-4)' }}>Loading Slack channels…</div>;
  const channels = data.channels || [];
  const save = async () => {
    setBusy(true);
    try {
      const c = channels.find((x) => x.id === channel);
      const r = await window.AtlasApi.dialerSaveSlackChannels({ default: channel ? { id: channel, name: c ? c.name : null } : null });
      take(r);
      pushToast({ type: 'success', title: channel ? 'Slack channel saved' : 'Slack posts turned off',
        body: r.posted ? `Posted ${r.posted} booked call${r.posted === 1 ? '' : 's'} that hadn’t gone out yet.` : undefined });
    } catch (e) { pushToast({ type: 'error', title: 'Could not save', body: e.message }); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: 12.5 }}>
      <div style={{ fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>Slack channel for booked calls</div>
      {data.error && <div style={{ color: 'var(--rose-fg)', marginBottom: 6 }}>{data.error}</div>}
      {data.unposted && channel === base && (
        <div style={{ color: 'var(--amber-fg)', background: 'var(--amber-bg)', borderRadius: 6, padding: '6px 10px', marginBottom: 8, lineHeight: 1.5 }}>
          {data.unposted.count} booked call{data.unposted.count === 1 ? '' : 's'} didn’t post. {data.unposted.message}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <window.Picker value={channel} disabled={!isAdmin || !channels.length} onChange={setChannel}
          emptyLabel="No Slack post" minWidth={240} menuMinWidth={300}
          options={channels.map((c) => ({ value: c.id, label: `${c.is_private ? '🔒 ' : '#'}${c.name}`,
            hint: c.is_member ? undefined : 'bot not in channel' }))} />
        {isAdmin && <Btn size="xs" variant="accent" disabled={channel === base || busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</Btn>}
      </div>
    </div>
  );
};

const CallingSetupTab = ({ status, onChanged }) => {
  const { ask, dialog: confirmDialog } = useCallingConfirm();
  const [token, setToken] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [events, setEvents] = React.useState(null);
  const isAdmin = status && status.is_admin;
  React.useEffect(() => {
    if (isAdmin && status.connected) window.AtlasApi.dialerEvents(25).then((r) => setEvents(r.items || [])).catch(() => setEvents(null));
  }, [isAdmin, status && status.connected]);   // eslint-disable-line react-hooks/exhaustive-deps

  const connect = async () => {
    setBusy(true);
    try {
      await window.AtlasApi.dialerConnect(token.trim());
      setToken('');
      pushToast({ type: 'success', title: 'PhoneBurner connected' });
      onChanged();
    } catch (e) { pushToast({ type: 'error', title: 'Could not connect', body: e.message }); }
    finally { setBusy(false); }
  };
  const disconnect = async () => {
    if (!(await ask({ title: 'Disconnect PhoneBurner?', body: 'Calls stop syncing until someone reconnects.',
      confirmLabel: 'Disconnect', danger: true }))) return;
    setBusy(true);
    try { await window.AtlasApi.dialerDisconnect(); onChanged(); }
    catch (e) { pushToast({ type: 'error', title: 'Disconnect failed', body: e.message }); }
    finally { setBusy(false); }
  };
  const copy = (text) => {
    try { navigator.clipboard.writeText(text); pushToast({ type: 'success', title: 'Copied' }); } catch (_) {}
  };
  if (!status) return <div style={{ marginTop: 14, fontSize: 12.5, color: 'var(--text-4)' }}>Loading…</div>;
  const ev = status.events || {};
  const row = { padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: 12.5 };
  return (
    <div style={{ ...callBox }}>
      {confirmDialog}
      <div style={row}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <b style={{ color: 'var(--text)' }}>PhoneBurner</b>
          {status.connected ? <Pill hue="green" sz="sm">Connected</Pill> : <Pill hue="amber" sz="sm">Not connected</Pill>}
          {status.connected_by && <span style={{ color: 'var(--text-3)' }}>by {status.connected_by}</span>}
          {status.account && <span style={{ color: 'var(--text-4)' }}>· {status.account}</span>}
          <span style={{ flex: 1 }} />
          {isAdmin && status.connected && <Btn size="xs" variant="ghost" disabled={busy} onClick={disconnect}>Disconnect</Btn>}
        </div>
        {isAdmin && (
          <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input type="password" value={token} onChange={(e) => setToken(e.target.value)} autoComplete="off"
              placeholder={status.connected ? 'Paste a new token to replace it' : 'Paste the admin Personal Access Token'}
              style={{ ...callSel, width: 340 }} />
            <Btn size="sm" variant="accent" disabled={busy || token.trim().length < 10} onClick={connect}>
              {busy ? 'Checking…' : (status.connected ? 'Replace token' : 'Connect')}
            </Btn>
            <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>
              PhoneBurner → Settings → Integrations → Personal Access Tokens, on the admin account.
            </span>
          </div>
        )}
        {!isAdmin && !status.connected && (
          <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-3)' }}>An admin connects PhoneBurner here.</div>
        )}
      </div>

      {status.connected && (
        <div style={row}>
          <div style={{ fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>Seats</div>
          {status.members_error && <div style={{ color: 'var(--rose-fg)' }}>{status.members_error}</div>}
          {(status.members || []).map((m) => (
            <div key={m.user_id} style={{ display: 'flex', gap: 10, padding: '3px 0', flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--text)', minWidth: 160 }}>{m.name || m.user_id}</span>
              <span style={{ color: 'var(--text-3)', minWidth: 200 }}>{m.email}</span>
              {m.atlas_member_name
                ? <span style={{ color: 'var(--text-2)' }}>→ {m.atlas_member_name}</span>
                : <span style={{ color: 'var(--amber-fg)' }} title="Calls from this seat won't be credited to an Atlas member until the emails match">no Atlas member with this email</span>}
              {!m.has_token && <span style={{ color: 'var(--text-4)' }}>· no API access (webhooks only)</span>}
              {m.own_token && <Pill hue="green" sz="sm">token connected</Pill>}
              {isAdmin && <CallingSeatToken seat={m} onChanged={onChanged} />}
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
            <span style={{ fontSize: 11.5, color: 'var(--text-4)', lineHeight: 1.5 }}>
              Transcripts need the caller seat’s own token (log in as the caller → PhoneBurner Settings → Integrations →
              Personal Access Tokens) and transcription turned on for that seat.
            </span>
            {isAdmin && (status.members || []).some((m) => m.own_token) && <CallingTranscriptCheck />}
          </div>
        </div>
      )}

      {isAdmin && status.connected && status.webhook_urls && (
        <div style={row}>
          <div style={{ fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>Webhooks</div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', lineHeight: 1.6, marginBottom: 8 }}>
            Paste the Call End URL into <b>Call End</b> under Settings → Integrations → Webhooks on
            <b> each caller's seat</b> (admin → Team → Login As), or into the webhook field of every button in the
            shared disposition sets.
          </div>
          {Object.entries(status.webhook_urls).map(([k, url]) => (
            <div key={k} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
              <span style={{ minWidth: 130, color: 'var(--text-2)' }}>{k === 'call_done' ? 'Call End' : 'Email unsubscribe'}</span>
              {url ? (
                <>
                  <code className="mono" style={{ fontSize: 11, padding: '3px 6px', background: 'var(--surface-2)', borderRadius: 4,
                    maxWidth: 560, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{url}</code>
                  <Btn size="xs" variant="outline" onClick={() => copy(url)}>Copy</Btn>
                </>
              ) : <span style={{ color: 'var(--amber-fg)' }}>Set DIALER_WEBHOOK_BASE (or RENDER_EXTERNAL_URL) on the API</span>}
            </div>
          ))}
        </div>
      )}

      {status.connected && <CallingRoutingSection status={status} isAdmin={isAdmin} onChanged={onChanged} />}
      {status.connected && isAdmin && <CallingCustomFieldsSection isAdmin={isAdmin} />}
      {status.connected && isAdmin && <CallingSlackChannelsSection isAdmin={isAdmin} />}
      {status.connected && isAdmin && <CallingFolderSyncSection />}
      {status.connected && isAdmin && <CallingCallbackEventsSection />}
      {status.connected && isAdmin && <CallingEmailTemplatesSection />}

      <div style={{ ...row, borderBottom: 'none' }}>
        <div style={{ fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>Activity</div>
        <div style={{ color: 'var(--text-3)' }}>
          {ev.last_at ? `Last webhook ${callWhen(ev.last_at)}` : 'No webhooks received yet'} · {ev.last_24h || 0} in 24h
          {ev.pending ? ` · ${ev.pending} waiting` : ''}{ev.errors ? ` · ${ev.errors} with errors` : ''}
        </div>
        {events && events.length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {events.slice(0, 12).map((e) => (
              <div key={e.id} style={{ fontSize: 11.5, color: e.error ? 'var(--rose-fg)' : 'var(--text-3)', display: 'flex', gap: 8 }}>
                <span style={{ minWidth: 120 }}>{callWhen(e.received_at)}</span>
                <span style={{ minWidth: 90 }}>{e.kind}</span>
                <span>{e.error || (e.result && (e.result.outcome || e.result.ignored || e.result.stored)) || (e.processed_at ? 'ok' : 'waiting')}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

window.CallingView = CallingView;
window.DialerCallsPanel = DialerCallsPanel;
window.DialerCallRow = DialerCallRow;

// ── Meetings & calls drawer body (record-page redesign step 10; ?panel=calls[&call=<id>]).
// Deals: Fireflies analytics + soundbites (the classic sections, exported by deal_detail.jsx;
// their Section ids deal.call_analytics / deal.highlights are kept) — also for guests.
// Companies / people: the PhoneBurner calls (DialerCallRow) with the flags row and the
// Mark to call / Send to PhoneBurner actions; a row click opens CallDetailDrawer.
const RecordCallsBody = ({ api }) => {
  const kind = api.rec.kind; const id = api.rec.id; const ctx = api.ctx || {};
  const [analytics, setAnalytics] = React.useState(Array.isArray(ctx.callAnalytics) ? ctx.callAnalytics : null);
  const [highlights, setHighlights] = React.useState(null);
  const [data, setData] = React.useState(null);
  const [showAll, setShowAll] = React.useState(false);
  const load = React.useCallback(() => {
    if (kind === 'deal') return;
    const p = kind === 'contact' ? window.AtlasApi.contactCalls(id) : window.AtlasApi.companyCalls(id);
    p.then(setData).catch(() => setData(null));
  }, [kind, id]);
  React.useEffect(() => {
    if (!api.active) return undefined;
    let alive = true;
    if (kind === 'deal') {
      if (!Array.isArray(ctx.callAnalytics)) window.AtlasApi.engagementCallAnalytics(id).then((r) => { if (alive) setAnalytics(Array.isArray(r) ? r : []); }).catch(() => { if (alive) setAnalytics([]); });
      else setAnalytics(ctx.callAnalytics);
      window.AtlasApi.engagementHighlights(id).then((r) => { if (alive) setHighlights(Array.isArray(r) ? r : []); }).catch(() => { if (alive) setHighlights([]); });
    } else load();
    return () => { alive = false; };
  }, [kind, id, api.active, ctx.callAnalytics, load]);
  const contactId = kind === 'contact' ? id : null;
  const { busy, toggleQueue, send } = useDialerActions(contactId, load);
  const panel = React.useMemo(() => ({ open: (c) => api.set({ call: c.id }) }), [api.set]);
  if (kind === 'deal') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {analytics == null && highlights == null && <div style={{ padding: 14, color: 'var(--text-4)' }}>—</div>}
        {analytics && analytics.length === 0 && (highlights || []).length === 0 && <div style={{ padding: 14, color: 'var(--text-4)' }}>0</div>}
        {window.CallAnalyticsSection && <window.CallAnalyticsSection items={analytics} />}
        {window.HighlightsSection && <window.HighlightsSection items={highlights} />}
      </div>
    );
  }
  const calls = (data && data.calls) || []; const st = (data && data.state) || {};
  const shown = showAll ? calls : calls.slice(0, 5);
  const row = calls.find((c) => c.id === api.params.call) || null;
  return (
    <CallPanelContext.Provider value={panel}>
      <div data-calls-body="">
        {data && contactId && api.canWrite && (data.can_queue || data.can_send) && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            {data.can_queue && <Btn size="xs" variant={st.queued ? 'ghost' : 'outline'} disabled={busy || st.do_not_call} onClick={() => toggleQueue(st.queued)}>{st.queued ? 'Queued ✓ · Remove' : 'Mark to call'}</Btn>}
            {data.can_send && <Btn size="xs" variant="outline" icon={<Icon.Phone size={10} />} disabled={busy || st.do_not_call} onClick={send}>{busy ? 'Sending…' : (st.sent_at ? 'Send again' : 'Send to PhoneBurner')}</Btn>}
          </div>
        )}
        {(st.do_not_call || st.queued || st.bad_phone || st.sent_at) && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
            {st.do_not_call && <Pill hue="rose" sz="sm">Do not call</Pill>}
            {st.queued && <Pill hue="blue" sz="sm">Marked to call</Pill>}
            {st.bad_phone && <Pill hue="amber" sz="sm">Phone marked bad</Pill>}
            {st.sent_at && <span style={{ fontSize: 11, color: 'var(--text-4)' }}>{`In PhoneBurner since ${new Date(st.sent_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`}</span>}
          </div>
        )}
        <div style={{ margin: '0 -14px' }}>
          {data == null ? <div style={{ padding: 14, color: 'var(--text-4)' }}>—</div>
            : calls.length === 0 ? <div style={{ padding: 14, color: 'var(--text-4)' }}>0</div>
            : shown.map((c) => <DialerCallRow key={c.id} c={c} showContact={kind === 'company'} />)}
          {calls.length > 5 && <div style={{ padding: '6px 14px' }}><Btn size="xs" variant="ghost" onClick={() => setShowAll((x) => !x)}>{showAll ? 'Show fewer' : `Show all ${calls.length}`}</Btn></div>}
        </div>
        {row && <CallDetailDrawer call={row} onClose={() => api.set({ call: null })} />}
      </div>
    </CallPanelContext.Provider>
  );
};
Object.assign(window, { CallDetailDrawer, CallPanelContext, useDialerActions, RecordCallsBody });
